/**
 * Core game functionality for Netwrck platform
 * @module Game
 */

var api_base_url =
  window.fixtures && window.fixtures.api_base_url
    ? window.fixtures.api_base_url
    : '/api/chatpred_or';

// Ensure userData always has safe defaults to prevent undefined errors
if (typeof window.userData === 'undefined') {
  window.userData = {}; // Empty object prevents undefined errors
}

/**
 * Generate random string of specified length
 * @param {number} length - Length of random string
 * @returns {string} Random string
 */
function randomString(length) {
  return window.StringUtils.randomString(length);
}

function isPrivateRoomChat() {
  return !!window.fixtures?.room_name && (!!window.fixtures.room_is_private || new URLSearchParams(window.location.search).get('anon_chat') === '1');
}

function preparePrivateRoomChat() {
  if (!isPrivateRoomChat() || fixtures.private_room_prepared) return;
  fixtures.room_is_private = true;
  fixtures.private_room_prepared = true;
  fixtures.room_can_moderate = false;
  fixtures.live_updates_enabled = false;
  window.mediaUploadHandler?.resetChatImages?.();
  fixtures.room_ais = JSON.parse(JSON.stringify(fixtures.room_ais || {}));
  fixtures.currentAI = null;
  fixtures.lastResponderCharacter = null;
  fixtures.newRespondingCharacter = null;
  fixtures.messages = Object.values(fixtures.room_ais).map(function (ai) {
    return { username: ai.name, text: ai.greeting || 'Hello! I\'m ' + ai.name + '.', avatar_file_name: ai.avatar_file_name };
  });
  delete fixtures.save_game_json;
  if (window.liveSource) {
    window.liveSource.close();
    window.liveSource = null;
  }
  window.EventSourceManager?.close(fixtures.room_name);
}

window.startPrivateRoomChat = function () {
  if (!window.fixtures?.room_name) return;
  var url = new URL(window.location.href);
  url.searchParams.delete('saved_chat');
  url.searchParams.set('anon_chat', '1');
  window.history.replaceState(null, '', url.href);
  fixtures.private_room_prepared = false;
  fixtures.private_room_rendered = false;
  preparePrivateRoomChat();
  window.switchChatMode('chatroom', { room_name: fixtures.room_name, room_ais: fixtures.room_ais, room_is_private: true }, true);
  ReRenderCurrentGame();
  window.dispatchEvent(new CustomEvent('netwrck:private-room-start'));
};

function safeClipboardWrite(text) {
  if (!text && text !== '') {
    return Promise.resolve();
  }
  if (
    typeof navigator !== 'undefined' &&
    navigator.clipboard &&
    typeof navigator.clipboard.writeText === 'function'
  ) {
    return navigator.clipboard.writeText(text).catch(function (err) {
      console.warn('[CLIPBOARD] navigator.clipboard.writeText failed:', err);
      return Promise.resolve();
    });
  }
  return Promise.resolve();
}

/**
 * Populate elf styles in the UI
 * @returns {void}
 */
function populateElfStyles() {
  return window.TemplateUtils.populateElfStyles();
}

if (typeof navigator.share !== 'function') {
  navigator.share = function (_data) {
    try {
      return Promise.resolve();
    } catch (e) {
      return Promise.resolve();
    }
  };
}

getTagsTemplate = function (tags) {
  if (window.TemplateUtils && window.TemplateUtils.getTagsTemplate) {
    return window.TemplateUtils.getTagsTemplate(tags);
  }

  if (!tags) {
    return '';
  }

  // Handle case where tags might be a comma-separated string instead of array
  var tagArray;
  if (typeof tags === 'string') {
    // Split by comma and trim each tag
    tagArray = tags
      .split(',')
      .map(function (tag) {
        return tag.trim();
      })
      .filter(function (tag) {
        return tag.length > 0;
      });
  } else if (Array.isArray(tags)) {
    tagArray = tags;
  } else {
    return '';
  }

  var tagsTemplate = '<div class="mdl-card__supporting-text">';
  for (var i = 0; i < tagArray.length; i++) {
    var currentTag = tagArray[i];
    var urlEncodedTag = encodeURIComponent(currentTag);
    var currentTagDisplay =
      currentTag.length > 20 ? currentTag.substring(0, 20) + '...' : currentTag;
    currentTag = currentTag.trim();
    if (currentTag) {
      tagsTemplate +=
        '<div class="mdl-chip"><span class="mdl-chip__text"><a href="/ai-chatbots?category=' +
        urlEncodedTag +
        '" target="_blank" data-bypass>' +
        currentTagDisplay +
        '</a></span></div>';
    }
  }
  tagsTemplate += '</div>';
  return tagsTemplate;
};

/**
 * Normalize chat prediction responses to a plain string.
 * Handles strings, arrays, and objects used by various backends.
 * @param {*} response - Raw response from chat prediction endpoints.
 * @returns {string} Extracted response text or empty string if unavailable.
 */
function extractChatPredText(response) {
  if (!response) {
    return '';
  }

  if (typeof response === 'string') {
    return response;
  }

  if (Array.isArray(response)) {
    for (var i = 0; i < response.length; i++) {
      var entry = response[i];
      if (typeof entry === 'string') {
        return entry;
      }
      if (
        entry &&
        typeof entry === 'object' &&
        typeof entry.generated_text === 'string'
      ) {
        return entry.generated_text;
      }
      if (
        entry &&
        typeof entry === 'object' &&
        typeof entry.text === 'string'
      ) {
        return entry.text;
      }
    }
    return '';
  }

  if (typeof response === 'object') {
    if (response.success === false || response.error) return '';
    if (typeof response.generated_text === 'string') {
      return response.generated_text;
    }
    if (typeof response.text === 'string') {
      return response.text;
    }
    if (response.data) {
      var data = response.data;
      if (typeof data === 'string') {
        return data;
      }
      if (Array.isArray(data)) {
        for (var j = 0; j < data.length; j++) {
          var dataEntry = data[j];
          if (typeof dataEntry === 'string') {
            return dataEntry;
          }
          if (
            dataEntry &&
            typeof dataEntry === 'object' &&
            typeof dataEntry.generated_text === 'string'
          ) {
            return dataEntry.generated_text;
          }
          if (
            dataEntry &&
            typeof dataEntry === 'object' &&
            typeof dataEntry.text === 'string'
          ) {
            return dataEntry.text;
          }
        }
      } else if (
        data &&
        typeof data === 'object' &&
        typeof data.generated_text === 'string'
      ) {
        return data.generated_text;
      } else if (data && typeof data === 'object' && typeof data.text === 'string') {
        return data.text;
      }
    }
    if (typeof response.message === 'string') {
      return response.message;
    }
  }

  return '';
}

function isCannedChatPrediction(text) {
  const lower = String(text || '')
    .trim()
    .toLowerCase();
  if (!lower) return true;
  return lower === '...' || /^failed to generate(?: a)? (?:response|reply)[.!]?$/i.test(lower);
}

function collectChatImageFields() {
  try {
    if (
      window.mediaUploadHandler &&
      typeof window.mediaUploadHandler.getChatImageContext === 'function'
    ) {
      const pending = window.mediaUploadHandler.getChatImageContext();
      return {
        images: pending.map((p) => p.url).filter(Boolean),
        image_captions: pending.map((p) => p.caption || ''),
      };
    }
  } catch (err) {
    /* image attach is best-effort */
  }
  return { images: [], image_captions: [] };
}

function chatWebToolsEnabled() {
  try {
    return localStorage.getItem('netwrckDevWebTools') === '1';
  } catch {
    return false;
  }
}

async function fetchChatModelFallback(payload, modelName) {
  if (isPrivateRoomChat() && !fixtures.ephemeral_chat_supported) throw new Error('Private chat requires the updated Go server.');
  const response = await fetch('/api/chatpred_or', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
      query: payload.query,
      images: payload.images || [],
      image_captions: payload.image_captions || [],
      context: payload.context,
      examples: payload.examples || [],
      greeting: payload.greeting || '',
      model_name: modelName,
      character: payload.character || '',
      web_tools: chatWebToolsEnabled(),
    }),
  });
  if (!response.ok) {
    throw new Error(modelName + ' chatpred_or failed: ' + response.status);
  }
  const body = await response.json();
  const text = extractChatPredText(body);
  if (isCannedChatPrediction(text)) {
    throw new Error(modelName + ' returned non-usable fallback');
  }
  return String(text).trim();
}

async function fetchTextGeneratorChatFallback(payload) {
  const models = [
    'text-generator/auto',
    'gryphe/mythomax-l2-13b',
    'neversleep/llama-3.1-lumimaid-8b',
    'meta-llama/llama-3.1-8b-instruct',
  ];
  let lastErr;
  for (const modelName of models) {
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        return await fetchChatModelFallback(payload, modelName);
      } catch (err) {
        lastErr = err;
        if (attempt < 1) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }
  }
  throw lastErr || new Error('all chat fallbacks failed');
}

function getCookie(cookieName) {
  if (window.CookieUtils && window.CookieUtils.getCookie) {
    return window.CookieUtils.getCookie(cookieName);
  }

  var someCookie = document.cookie.split(';').find(function (item) {
    return item.trim().startsWith(cookieName + '=');
  });
  if (someCookie) {
    return someCookie.split('=')[1];
  }
  return null;
}

function isUserAuthenticated() {
  if (window.AuthUtils && window.AuthUtils.isUserAuthenticated) {
    return window.AuthUtils.isUserAuthenticated();
  }

  if (window.userData?.secret && window.userData?.email) {
    return {
      isAuthenticated: true,
      email: window.userData.email,
      source: 'userData',
    };
  } else if (
    window.authSession &&
    window.authSession.isLoggedIn &&
    window.authSession.isLoggedIn()
  ) {
    var currentUser = window.authSession.getCurrentUser();
    return {
      isAuthenticated: true,
      email: currentUser ? currentUser.email : '',
      source: 'authSession',
    };
  }

  return { isAuthenticated: false, email: '', source: 'none' };
}

// Ensure window.userData is always at least an empty object to prevent null reference errors
window.userData = window.userData || {};

// Global signout function for onclick handlers
if (typeof window.signout !== 'function') {
  window.signout = async function () {
    window.__netwrckLogoutStartedAt = Date.now();
    window.__netwrckLogoutBlockSessionSave = true;
    try {
      sessionStorage.setItem('netwrck_logout_pending', '1');
    } catch {
      /* Error ignored */
    }

    try {
      await fetch('/api/logout', { method: 'POST', credentials: 'include' });
    } catch (e) {
      console.error('[GAME] Logout error:', e);
    }

    // Clear user data and tokens
    try {
      window.userData = {};
      window.currentUser = null;
      window.userSecret = null;

      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('userData');
        localStorage.removeItem('userSecret');
        localStorage.removeItem('userIsSubscribed');
        localStorage.removeItem('userDataTimestamp');
        localStorage.removeItem('authToken');
        localStorage.removeItem('netwrck_user');
      }
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
        sessionStorage.setItem('netwrck_logout_pending', '1');
      }

      function deleteCookie(name) {
        document.cookie =
          name + '=; expires=Thu, 01 Jan 1970 00:00:01 GMT; path=/';
      }
      [
        'userData',
        'userSecret',
        'user_secret',
        'userIsSubscribed',
        'userDataTimestamp',
        'auth_token',
        'session_id',
      ].forEach(deleteCookie);

      // Update header/UI inline without navigation
      var loginEls = document.querySelectorAll(
        '.header-login-signup, .login-link, .signup-link'
      );
      var logoutEls = document.querySelectorAll('.header-login-signout');
      var accountEls = document.querySelectorAll(
        '.header-account, .account-link'
      );
      var subscribeEls = document.querySelectorAll('.header-subscribe');

      loginEls.forEach(function (el) {
        el.style.display = '';
      });
      logoutEls.forEach(function (el) {
        el.style.display = 'none';
      });
      accountEls.forEach(function (el) {
        el.style.display = 'none';
      });
      subscribeEls.forEach(function (el) {
        el.style.display = 'none';
      });

      if (window.dispatchEvent) {
        try {
          window.dispatchEvent(new Event('authStateChanged'));
        } catch {
          /* Error ignored */
        }
      }
    } catch (e) {
      console.warn('[GAME] UI update after logout failed:', e);
    }
    return false;
  };
}

// Ensure setCookie is defined early and safely under strict mode
if (typeof window.setCookie !== 'function') {
  window.setCookie = function (name, value, days) {
    if (window.CookieUtils && window.CookieUtils.setCookie) {
      return window.CookieUtils.setCookie(name, value, days);
    }

    var expires;
    if (days) {
      var date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = '; expires=' + date.toGMTString();
    } else {
      expires = '';
    }
    document.cookie =
      name + '=' + value + expires + '; path=/; SameSite=Strict';
  };
}
var setCookie = window.setCookie;
window.subscribeImageGenCounter = window.subscribeImageGenCounter || 0;
window.subscribeMessageCounter = window.subscribeMessageCounter || 0;

function initializeCounters() {
  var subscribeImageGenCounterCookie = +getCookie('subscribeImageGenCounter');
  if (subscribeImageGenCounterCookie) {
    window.subscribeImageGenCounter = subscribeImageGenCounterCookie;
  }

  var subscribeMessageCounterCookie = +getCookie('subscribeMessageCounter');
  if (subscribeMessageCounterCookie) {
    window.subscribeMessageCounter = subscribeMessageCounterCookie;
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeCounters);
} else {
  initializeCounters();
}
// Ensure minimal fixtures shape early
if (typeof window.fixtures === 'undefined') {
  window.fixtures = { room_ais: {}, currentAI: null, room_name: null };
}
// Only connect to live room stream when actually on a room page or chat UI is present
var __isRoomPath = /^\/r(\/|$)/.test(window.location.pathname);
var __hasChatUI =
  typeof document !== 'undefined' && !!document.querySelector('.chat-log');
if (
  window.fixtures &&
  window.fixtures.room_name &&
  !isPrivateRoomChat() &&
  (__isRoomPath || __hasChatUI) &&
  window.EventSourceManager
) {
  var searchBaseUrl = fixtures['search_base_url'] || '';

  if (searchBaseUrl && searchBaseUrl.indexOf('localhost') > -1) {
    searchBaseUrl = '';
  }
  EventSourceManager.create(fixtures.room_name, {
    baseUrl: searchBaseUrl,
    onMessage: function (e) {
      try {
        var data = JSON.parse(e.data);
        if (window.ChatWorkspace?.applyLiveModerationEvent(data)) {
          return;
        }
        if (data.text && data.username) {
          var srcImg = getSrcImgForCharacter({
            avatar_file_name: data.avatar_file_name,
          });
          appendToChatLog(
            `<img class="chat-log-avatar-icon" src="${srcImg}" alt="${data.username}" width="100" height="100"><p class="chat-group-name" tabindex="0">${data.username}</p><div class="chat-log__item"><div class="chat-log__message" contenteditable="true" spellcheck="false">${data.text}</div></div>`,
            undefined,
            data
          );
          if (typeof playNotification === 'function') {
            playNotification();
          }
        }
      } catch (err) {
        console.error('invalid live message', err);
      }
    },
  });
}

function loadMarkedLibrary() {
  if (window.marked) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src =
      String(window.STATIC_URL || '/static').replace(/\/$/, '') +
      '/libs/marked-18.0.3.umd.js';
    script.onload = resolve;
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function cleanNameForPrompt(name) {
  return window.StringUtils.cleanNameForPrompt(name);
}

loadMarkedLibrary().then(() => {
  marked.setOptions({
    breaks: true,
    gfm: true,
  });
});

(function ensureMediaUploadLoaded() {
  function injectOnce(src, id) {
    if (id && document.getElementById(id)) return;
    if ([...document.scripts].some((s) => s.src.includes(src))) return;
    const script = document.createElement('script');
    if (id) script.id = id;
    script.src = src + (src.includes('?') ? '&' : '?') + 'v=' + Date.now();
    document.head.appendChild(script);
  }

  if (!window.mediaUploadHandler) {
    injectOnce(
      (window.STATIC_URL || '/static') + '/js/media-upload.js',
      'media-upload-script'
    );
  }
  if (!window.chatDragDropHandler) {
    injectOnce(
      (window.STATIC_URL || '/static') + '/js/chat-drag-drop.js',
      'chat-drag-drop-script'
    );
  }
})();

function processMarkdown(text) {
  return window.StringUtils.processMarkdown(text);
}

function createMessageActionButton(className, label, iconName, datasetKey, datasetValue) {
  var button = document.createElement('button');
  button.type = 'button';
  button.className = className;
  button.setAttribute('aria-label', label);
  if (datasetKey && datasetValue) {
    button.dataset[datasetKey] = datasetValue;
  }
  var icon = document.createElement('span');
  icon.className = 'material-icons';
  icon.textContent = iconName;
  button.appendChild(icon);
  return button;
}

function ensureMessageActionControls(chatItem) {
  if (!chatItem || !chatItem.querySelector('.chat-log__message')) {
    return;
  }
  var reactionsDiv = chatItem.querySelector('.reactions');
  if (!reactionsDiv) {
    reactionsDiv = document.createElement('div');
    reactionsDiv.className = 'reactions';
    chatItem.appendChild(reactionsDiv);
  }
  if (!reactionsDiv.querySelector('.add-reaction')) {
    reactionsDiv.appendChild(
      createMessageActionButton(
        'add-reaction message-action-button',
        'Add reaction',
        'sentiment_satisfied_alt'
      )
    );
  }
  if (!reactionsDiv.querySelector('[data-message-action="copy"]')) {
    reactionsDiv.appendChild(
      createMessageActionButton(
        'message-action-button message-copy-button',
        'Copy message',
        'content_copy',
        'messageAction',
        'copy'
      )
    );
  }
  if (chatItem.classList.contains('chat-log__item--own')) {
    return;
  }
  if (!reactionsDiv.querySelector('[data-audio-action="speak"]')) {
    reactionsDiv.appendChild(
      createMessageActionButton(
        'message-action-button message-speak-button',
        'Speak message',
        'volume_up',
        'audioAction',
        'speak'
      )
    );
  }
  if (!reactionsDiv.querySelector('[data-audio-action="replay"]')) {
    var replayButton = createMessageActionButton(
      'message-action-button message-audio-action',
      'Play message',
      'play_arrow',
      'audioAction',
      'replay'
    );
    replayButton.setAttribute('aria-disabled', 'false');
    reactionsDiv.appendChild(replayButton);
  }
  if (!reactionsDiv.querySelector('[data-audio-action="download"]')) {
    var downloadButton = createMessageActionButton(
      'message-action-button message-audio-action',
      'Download audio',
      'download',
      'audioAction',
      'download'
    );
    downloadButton.setAttribute('aria-disabled', 'false');
    reactionsDiv.appendChild(downloadButton);
  }
}

function hydrateChatMessageActions(root) {
  var scope = root || document;
  if (!scope || !scope.querySelectorAll) {
    return;
  }
  scope.querySelectorAll('.chat-log__item').forEach(function (chatItem) {
    ensureMessageActionControls(chatItem);
  });
  if (typeof window.syncReplayAudioButtons === 'function') {
    window.syncReplayAudioButtons();
  }
}

function linkChatAuthorToDm(root, message) {
  if (!message) return;
  var authorEl = root.querySelector('.chat-group-name');
  if (!authorEl) return;
  var authorName = authorEl.textContent.trim();
  if (!authorName) return;
  var authorId = message.author_id || message.user_id;
  var href = null;
  var linkClass = 'room-author-dm-link';
  if (authorId && String(authorId) !== authorName) {
    href = '/messages/' + encodeURIComponent(authorId);
  } else if (message.character_slug) {
    href = '/ai-chat/' + encodeURIComponent(message.character_slug);
    linkClass = 'room-author-chat-link';
  }
  if (!href) return;
  $(authorEl).replaceWith(
    $('<a>', {
      class: (authorEl.className + ' ' + linkClass).trim(),
      href: href,
      'data-bypass': 'true',
      title: authorName,
      text: authorName,
    })
  );
}

function sanitizeChatMarkup(root) {
  root.querySelectorAll('script,iframe,object,embed,base,meta,link,style,svg,math').forEach(element => element.remove());
  root.querySelectorAll('*').forEach(element => {
    Array.from(element.attributes).forEach(attribute => {
      if (/^on/i.test(attribute.name) || attribute.name === 'srcdoc') element.removeAttribute(attribute.name);
      if (!['href', 'src', 'xlink:href', 'action', 'formaction'].includes(attribute.name)) return;
      try {
        const url = new URL(attribute.value, window.location.href);
        const imageData = attribute.name === 'src' && /^data:image\/(?:png|jpeg|gif|webp);base64,/i.test(attribute.value);
        if (!['http:', 'https:', 'mailto:', 'tel:', 'blob:'].includes(url.protocol) && !imageData) element.removeAttribute(attribute.name);
      } catch {
        element.removeAttribute(attribute.name);
      }
    });
  });
}

function appendToChatLog(html, $chatLog = $('.chat-log'), message = null) {
  $chatLog = $($chatLog);

  const codeBlockMatches = (html.match(/```/g) || []).length;
  if (codeBlockMatches % 2 !== 0) {
    html += '\n```';
  }
  var currentHeight = $chatLog.height();

  if (html.includes('chat-log__message')) {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const messageDiv = tempDiv.querySelector('.chat-log__message');
    if (messageDiv) {
      const messageContent = messageDiv.innerHTML.replace(/[\s\n]+$/, '');

      // Check for duplicate consecutive messages from same AI character
      const lastMessages = $chatLog.find('.chat-log__message');
      if (lastMessages.length > 0) {
        const lastMessage = lastMessages.last();
        const lastMessageText = lastMessage.text().trim();
        const currentMessageText = messageDiv.textContent.trim();

        // Check if this is potentially an AI greeting by looking for nearby character name
        const prevSibling = lastMessage.parent().prev();
        const isLastFromAI =
          prevSibling.hasClass('chat-group-name') ||
          prevSibling.hasClass('chat-one-to-one-name');

        // If the last message is identical to current and likely from AI, skip
        if (isLastFromAI && lastMessageText === currentMessageText) {
          console.log(
            'Skipping duplicate AI message:',
            currentMessageText.substring(0, 50) + '...'
          );
          return;
        }
      }

      messageDiv.innerHTML = processMarkdown(messageContent);
      var chatItem = messageDiv.closest('.chat-log__item');
      if (chatItem) {
        ensureMessageActionControls(chatItem);
      }
    }
    linkChatAuthorToDm(tempDiv, message);
    sanitizeChatMarkup(tempDiv);
    html = tempDiv.innerHTML.replace(/[\s\n]+$/, '');
  }
  $chatLog.append(html);
  hydrateChatMessageActions($chatLog[0]);
  if (window.chatInitialized && !html.includes('chat-log__item--own')) {
    if (typeof playNotification === 'function') {
      playNotification();
    }
  }

  requestAnimationFrame(() => {
    var newHeight = $chatLog.height();
    var extraHeight = newHeight - currentHeight;
    var currentScrollTop = $('.mdl-layout__content').scrollTop();
    $('.mdl-layout__content').scrollTop(currentScrollTop + extraHeight);
  });
}

function sanitizePrompt(prompt) {
  return window.StringUtils.sanitizePrompt(prompt);
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripSpeakerLabel(text, speakerName) {
  if (!text) return '';
  if (!speakerName) return text.trim();

  var names = [speakerName];
  var lowerName = speakerName.toLowerCase();
  if (lowerName.indexOf('narrator') !== -1) {
    names.push('Narrator');
  }

  var namePattern = names.map(escapeRegExp).join('|');
  if (!namePattern) return text.trim();

  var boldInside = new RegExp('(^|\\n)\\s*\\*\\*(' + namePattern + '):\\*\\*\\s*', 'i');
  var boldOutside = new RegExp('(^|\\n)\\s*\\*\\*(' + namePattern + ')\\*\\*:\\s*', 'i');
  var plain = new RegExp('(^|\\n)\\s*(' + namePattern + '):\\s*', 'i');

  if (boldInside.test(text)) {
    return text.replace(boldInside, '$1').trim();
  }
  if (boldOutside.test(text)) {
    return text.replace(boldOutside, '$1').trim();
  }
  if (plain.test(text)) {
    return text.replace(plain, '$1').trim();
  }

  return text.trim();
}

function buildPrompt(game) {
  var characterName = fixtures.currentAI?.name || 'Character';
  var rawHumanName = window.userData?.name || 'Noo';
  var humanName = cleanNameForPrompt(rawHumanName);

  var prompt = `concise role play chat
desirable main character ${humanName}
${characterName}`;
  if (game.backstory) {
    prompt += `
${game.backstory}`;
  }

  prompt +=
    `
${characterName} ` + (fixtures.currentAI?.greeting || '');

  // Long chats keep a wider recent window; older relevant turns come back from
  // the server's embedding memory.
  var recentWindow = game.inputOutputPairs.length > 24 ? 24 : 12;
  var istart = Math.max(game.inputOutputPairs.length - recentWindow, 0);

  for (var i = istart + 1; i < game.inputOutputPairs.length; i += 2) {
    if (game.inputOutputPairs[i] && game.inputOutputPairs[i - 1]) {
      prompt += `
${humanName} ${game.inputOutputPairs[i - 1]}
${characterName} ${game.inputOutputPairs[i]}`;
    }
  }

  var lastLine = window.questionsgame.game.allInputs.slice(-1)[0];
  prompt += `
${lastLine}
${characterName}:`;
  prompt = sanitizePrompt(prompt) + ' ';

  return prompt;
}

function selectMentionedRoomCharacter(text) {
  const lower = String(text || '').toLowerCase();
  const mentioned = Object.values(fixtures.room_ais || {})
    .filter(character => character.name?.length > 1 && lower.includes(character.name.toLowerCase()))
    .sort((a, b) => b.name.length - a.name.length)[0];
  if (mentioned) fixtures.newRespondingCharacter = mentioned;
}

function buildRoomChatContext(game) {
  const character = fixtures.currentAI || {};
  const roster = Object.values(fixtures.room_ais || {}).map(ai => ai.name).join(', ');
  let context = `You are ${character.name || 'AI'} in the group chat "${fixtures.room_name}".\n${character.description || ''}\nParticipants: ${roster}.\nReply only as ${character.name || 'AI'}, directly to the latest user message. Keep your established personality and the conversation continuous. Do not restart introductions or speak for other participants.`;
  const history = game.allMessages || [];
  const speakers = game.allMessagesCharacters || [];
  const visible = typeof document !== 'undefined' ? document.querySelectorAll('.chat-log__message') : [];
  const lines = [];
  for (let i = Math.max(0, history.length - 25); i < history.length - 1; i++) {
    const text = visible.length === history.length ? visible[i].textContent : history[i];
    lines.push(`${speakers[i] || 'Participant'}: ${text}`);
  }
  if (lines.length) context += '\n\nConversation so far:\n' + lines.join('\n');
  return context;
}

function buildPromptChatRoom(game) {
  var characterName = fixtures.currentAI?.name || 'Character';
  var rawHumanName = window.userData?.name || 'Noo';
  var humanName = cleanNameForPrompt(rawHumanName);

  var prompt = `concise role play chat
desirable main character ${humanName}
${characterName}`;

  prompt +=
    `
${characterName}: ` +
    (fixtures.currentAI?.greeting || 'Hello! Welcome to the chatroom.');

  var istart = Math.max(game.allMessages.length - 6, 0);
  for (var i = istart; i < game.allMessages.length; i++) {
    var characterTalkingNow = game.allMessagesCharacters[i];
    if (characterTalkingNow) {
      prompt += `
${characterTalkingNow}: ${game.allMessages[i]}`;
    }
  }

  prompt += `
${characterName}:`;
  prompt = sanitizePrompt(prompt) + ' ';

  return prompt;
}

function hasSubscribeText(prediction) {
  return window.StringUtils.hasSubscribeText(prediction);
}

function setupSaveGameState(game, inputOutputs) {
  for (var i = 1; i < inputOutputs.length; i += 2) {
    var input = inputOutputs[i - 1];

    input = sanitizePrompt(input);
    var output = inputOutputs[i];
    let userName = 'Noo';
    if (window.userData) {
      userName = window.userData?.username || window.userData?.name || 'Noo';
    }

    output = output.replace('{{user}}', userName);
    output = output.replace('[user]', userName);
    output = output.replace('{{ user}}', userName);
    output = output.replace('{{user }}', userName);
    output = output.replace('{{ user }}', userName);
    game.input += `
${userName}: ${input}
${fixtures.currentAI?.name || 'AI'}: ${output}`;
    game.lastGeneratedText = output;

    game.addInputOutputPair(input, output);
  }
}

function rebuildGameState(gameState) {
  let $allMessages = $('.chat-log__message');
  var currentGreeting = $allMessages.first().text();

  var inputMessages = [];
  var outputMessages = [];
  $allMessages.each(function (index, element) {
    var $element = $(element);
    var text = $element.text();
    if ($element.parent().hasClass('chat-log__item--own')) {
      inputMessages.push(text);
    } else {
      outputMessages.push(text);
    }
  });
  outputMessages = outputMessages.slice(1, outputMessages.length);

  var inputOutputs = [];
  for (var i = 0; i < inputMessages.length && i < outputMessages.length; i++) {
    inputOutputs.push(inputMessages[i]);
    inputOutputs.push(outputMessages[i]);
  }

  var saveGame = {
    backstory: currentGreeting,
    input_outputs: inputOutputs,
  };
  gameState.constructStateFromSave(saveGame);
  return saveGame;
}

function reInitGameState(game) {
  game.inputOutputPairs = [];
  game.input = game.originalInput;
}

window.hashCode = function (s) {
  return window.CollectionUtils.hashCode(s);
};

function shuffle(array, seed) {
  return window.CollectionUtils.shuffle(array, seed);
}

function random(seed) {
  return window.CollectionUtils.random(seed);
}

function generateNewBackground(prediction) {
  if (window.BackgroundUtils && window.BackgroundUtils.generateNewBackground) {
    return window.BackgroundUtils.generateNewBackground(prediction);
  }

  console.warn(
    '[GAME.JS] BackgroundUtils not available, skipping background generation'
  );
  return;
}

var generateNewBackgroundDebounced =
  window.fixtures && window.fixtures.DEV
    ? generateNewBackground
    : _.debounce(generateNewBackground, 10000);

function generateAICharacterPortraitImgen(prompt) {
  var predictionPrompt = sanitizePrompt(prompt);
  predictionPrompt = predictionPrompt.replace(/[^a-zA-Z0-9 ]/g, '');
  var bgPrompt = `${predictionPrompt}`;
  bgPrompt = sanitizePrompt(bgPrompt);
  var bgPromptKey = encodeURIComponent(bgPrompt.replaceAll(' ', '-'));

  var { aspect, width, height } = dataAccessor.getWidthHeight();

  var falargs = {
    model_name: 'comfy/lee101/imgen',
    emptylatentimage_width: width,
    emptylatentimage_height: height,
    cliptextencode_text: bgPrompt,
  };

  dataAccessor.falFetch(falargs).then(async function (result) {
    var decoded = await result.json();
    var filefullURL = decoded.result.outputs['9'].images[0].url;

    preloadImage(filefullURL, function () {
      var $chatLog = $('.chat-log');
      $chatLog.append(`<img src="${filefullURL}" class="ai-portrait" />`);
    });
  });
}

function generateAICharacterPortrait(prompt) {
  var predictionPrompt = sanitizePrompt(prompt);
  predictionPrompt = predictionPrompt.replace(/[^a-zA-Z0-9 ]/g, '');
  var bgPrompt = `${predictionPrompt}`;
  bgPrompt = sanitizePrompt(bgPrompt);
  var bgPromptKey = encodeURIComponent(bgPrompt.replaceAll(' ', '-'));

  var sizeKey = dataAccessor.getKeyForSDSize();

  var falargs = {
    prompt: bgPrompt,
    model_name: 'fal-ai/flux/schnell',
    image_size: sizeKey,
    num_inference_steps: 4,
    guidance_scale: 3.5,
    num_images: 1,
    enable_safety_checker: false,
  };

  dataAccessor.falFetch(falargs).then(async function (result) {
    var decoded = await result.json();
    var filefullURL = decoded.result.images[0].url;

    preloadImage(filefullURL, function () {
      var $chatLog = $('.chat-log');
      $chatLog.append(`<img src="${filefullURL}" class="ai-portrait" />`);
    });
  });
}

function getSrcImgForCharacter(currentAI) {
  if (window.ImageUtils && window.ImageUtils.getSrcImgForCharacter) {
    return window.ImageUtils.getSrcImgForCharacter(currentAI);
  }

  if (!currentAI) {
    return '/static/img/netwrck-logo-colord256.webp';
  }
  if (currentAI.avatar_file_name) {
    return '/static/img/ai_characters/' + currentAI.avatar_file_name;
  }
  return '/static/img/netwrck-logo-colord256.webp';
}

let setupGameInit = function () {
  preparePrivateRoomChat();
  if (isPrivateRoomChat() && !fixtures.private_room_rendered) {
    $('.chat-log').empty();
    renderRoomHistory(fixtures.messages);
    fixtures.private_room_rendered = true;
  }
  try {
    if (
      typeof process !== 'undefined' &&
      process.env &&
      process.env.DEBUG === 'true'
    ) {
      console.debug('[GAME.JS] Loading updated version - syntax fixes applied');
    }
  } catch (e) {
    console.error('Error checking localStorage for version:', e);
  }
  window.questionsgame = {};
  var self = window.questionsgame;

  if (typeof window.fixtures === 'undefined') {
    window.fixtures = {
      room_ais: {},
      currentAI: null,
      room_name: null,
    };
  }

  if (!window.fixtures.room_ais) {
    window.fixtures.room_ais = {};
  }

  self.Game = function (input) {
    let greeting = fixtures.currentAI?.greeting || 'Welcome to the chatroom!';
    if (fixtures.save_game_json && fixtures.save_game_json.backstory) {
      greeting = fixtures.save_game_json.backstory;
    }
    var gameState = {
      numQuestions: 0,
      input: input,
      backstory: fixtures.currentAI?.description || '',
      greeting: greeting,
      allInputs: [],
      allMessagesCharacters: [],
      allMessages: [],
      inputOutputPairs: [],
      originalInput: input,
      extra_lines: '',
      moves: 0,
      repetition_penalty: 1.4,
      currentCharacter: fixtures.currentAI || {
        name: 'Assistant',
        greeting: 'Hello!',
        description: '',
      },
    };

    const chatModeActive =
      fixtures.room_name &&
      window.chatModeState &&
      window.chatModeState.currentMode === 'chatroom';
    const chatLogHasExisting =
      fixtures.room_name && $('.chat-log').children().length > 0;
    const shouldRenderChatroomDom = !(
      fixtures.room_name && (chatModeActive || isPrivateRoomChat()) && chatLogHasExisting
    );
    const escapeHtml =
      window.StringUtils && typeof window.StringUtils.escapeHtml === 'function'
        ? window.StringUtils.escapeHtml
        : function (value) {
            if (value === null || value === undefined) return '';
            return String(value).replace(/[&<>"']/g, function (match) {
              var escapeMap = {
                '&': '&amp;',
                '<': '&lt;',
                '>': '&gt;',
                '"': '&quot;',
                "'": '&#39;',
              };
              return escapeMap[match] || match;
            });
          };

    var CharPrefixLength = ((fixtures.currentAI?.name || 'AI') + ': ').length;
    var inputDisplayMessage = input.slice(CharPrefixLength);
    gameState.allInputs.push(input);

    var currentChatCharacterDisplayName = fixtures.currentAI?.name || '';
    if (fixtures.room_name && !isPrivateRoomChat()) {
      var authCheck =
        typeof isUserAuthenticated === 'function' && isUserAuthenticated();
      if (!authCheck || !authCheck.isAuthenticated) {
        console.log(
          '[CHAT] Not authenticated. Showing login dialog before sending message.'
        );
        if (typeof window.showSignupDialog === 'function') {
          window.showSignupDialog();
        } else {
          try {
            window.location.href =
              '/login?next=' +
              encodeURIComponent(
                window.location.pathname + window.location.search
              );
          } catch {
            /* Error ignored */
          }
        }
        return;
      }
    }
    if (fixtures.room_name) {
      var srcImg = getSrcImgForCharacter(fixtures.currentAI);
      if (shouldRenderChatroomDom) {
        appendToChatLog(`<img class="chat-log-avatar-icon"
                                                     src="${srcImg}"
                                                     alt="${fixtures.currentAI?.name || 'AI'}"
                                                     width="100"
                                                     height="100">
                                                  <p id="" class="chat-group-name" tabindex="0">${currentChatCharacterDisplayName}</p>

                                                     <div class="chat-log__item">

                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${escapeHtml(inputDisplayMessage)}</div>
                        </div>`);
      }
      if (fixtures.messages) {
        var messages = fixtures.messages;
        for (var i = 0; i < messages.length; i++) {
          var message = messages[i];
          var messageSrcImg = getSrcImgForCharacter({
            avatar_file_name: message.avatar_file_name,
          });

          var resolvedText = '';
          if (typeof message === 'string') {
            resolvedText = message;
          } else if (message) {
            resolvedText =
              (typeof message.text === 'string' && message.text) ||
              (typeof message.content === 'string' && message.content) ||
              '';
          }

          if (!resolvedText && Array.isArray(message?.attachments)) {
            resolvedText = message.attachments
              .map(function (att) {
                return att?.alt || att?.caption || '[attachment]';
              })
              .join(' ');
          }

          gameState.allMessages.push(resolvedText);
          gameState.allMessagesCharacters.push(message.username);
          gameState.inputOutputPairs.push(resolvedText);

          gameState.allInputs.push(`${message.username}: ${resolvedText}`);

          if (shouldRenderChatroomDom) {
            var attachmentsMarkup = '';
            if (Array.isArray(message.attachments) && message.attachments.length > 0) {
              attachmentsMarkup =
                '<div class="chat-message-attachments">' +
                message.attachments
                  .map(function (att) {
                    if (!att || !att.url || !/^https?:\/\//i.test(att.url)) {
                      return '';
                    }
                    var type = (att.type || '').toLowerCase();
                    var safeUrl = escapeHtml(att.url);
                    var caption = escapeHtml(att.caption || att.alt || '');
                    var alt = escapeHtml(att.alt || att.caption || 'Attachment');
                    if (type.indexOf('video/') === 0) {
                      return (
                        '<figure class="chat-attachment chat-attachment--video">' +
                        '<video controls preload="metadata" src="' +
                        safeUrl +
                        '" aria-label="' +
                        alt +
                        '"></video>' +
                        (caption
                          ? '<figcaption>' + caption + '</figcaption>'
                          : '') +
                        '</figure>'
                      );
                    }
                    return (
                      '<figure class="chat-attachment chat-attachment--image chat-i2v" data-image-url="' +
                      safeUrl +
                      '">' +
                      '<div class="chat-i2v__media">' +
                      '<img class="chat-i2v__image" src="' +
                      safeUrl +
                      '" alt="' +
                      alt +
                      '" loading="lazy">' +
                      '<button type="button" class="chat-i2v__play" aria-label="Animate to video" title="Animate to video">' +
                      '<i class="material-icons" aria-hidden="true">play_arrow</i></button>' +
                      '</div>' +
                      (caption
                        ? '<figcaption>' + caption + '</figcaption>'
                        : '') +
                      '<div class="chat-i2v__status" hidden></div>' +
                      '<div class="chat-i2v__video-slot"></div>' +
                      '</figure>'
                    );
                  })
                  .join('') +
                '</div>';
            }

            appendToChatLog(
              `<img class="chat-log-avatar-icon"
                                                             src="${messageSrcImg}"
                                                             alt="${message.username}"
                                                            width="100"
                                                            height="100">
                                                            <p id="" class="chat-group-name" tabindex="0">${message.username}</p>
<div class="chat-log__item">
                                    ${attachmentsMarkup}
                                    <div class="chat-log__message" contenteditable="true" spellcheck="false">${escapeHtml(resolvedText)}</div>
                                </div>`,
              undefined,
              message
            );
          }
        }
      }
    } else {
      // Check if chat log already has content from server-side rendering
      var chatLog = $('.chat-log');
      var existingMessages = chatLog.find('.chat-log__message');

      // Only append if chat log is empty or doesn't already contain this message
      if (existingMessages.length === 0) {
        appendToChatLog(`<div class="chat-log__item">
                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${escapeHtml(inputDisplayMessage)}</div>
                        </div>`);
      }
    }

    if (gameState.inputOutputPairs.length % 2 === 1) {
      gameState.inputOutputPairs.pop();
    }

    gameState.constructUIFromSave = function (saveGame) {
      if (saveGame && saveGame.input_outputs) {
        var isUser = true;
        for (var i = 0; i < saveGame.input_outputs.length; i++) {
          var currentMessage = saveGame.input_outputs[i];
          if (isUser) {
            appendToChatLog(`<div class="chat-log__item chat-log__item--own">
                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${currentMessage}</div>
                        </div>`);
          } else {
            appendToChatLog(`<div class="chat-log__item">
                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${currentMessage}</div>
                        </div>`);
          }
          isUser = !isUser;
        }
      }
    };
    gameState.constructStateFromSave = function (saveGame) {
      if (saveGame && saveGame.input_outputs) {
        setupSaveGameState(gameState, saveGame.input_outputs);
      }
    };

    gameState.getNewPrompt = function () {
      gameState.numQuestions++;
    };

    gameState.addInputOutputPair = function (inputText, output) {
      gameState.inputOutputPairs.push(inputText);
      gameState.inputOutputPairs.push(output);
    };

    gameState.constructStateFromSave(fixtures.save_game_json);
    gameState.constructUIFromSave(fixtures.save_game_json);
    fixtures.save_game_json = null;

    $('.chat-one-to-one-name-tags').html(
      window.getTagsTemplate(fixtures.currentAI?.tags || null)
    );

    if ((window.fixtures && window.fixtures.DEV) || Math.random() < 0.05) {
      generateNewBackground(input);
    }

    return gameState;
  };

  ReRenderCurrentGame();

  var currentCookieChecked = getCookie('autoart');
  if (currentCookieChecked && currentCookieChecked === 'true') {
    $('#autoart').attr('checked', true);
    $('#autoart').parent().addClass('is-checked');
  }
  $('#autoart').change(function () {
    setCookie('autoart', $(this).is(':checked'), 365);
  });

  $('.form-horizontal').on('submit', async function (event) {
    event.preventDefault();
    event.stopPropagation();
    if (window.mediaUploadHandler?.processingCount > 0) {
      if (window.showToast) window.showToast('Wait for your attachments to finish processing.');
      return false;
    }
    if (isPrivateRoomChat() && !fixtures.ephemeral_chat_supported) {
      if (window.showToast) window.showToast('Private chat requires the updated Go server. Reload it before sending.');
      return false;
    }
    if (typeof window.canSendFreeMessage === 'function' && !(await window.canSendFreeMessage())) {
      return;
    }
    const chatCreditBalance = Number(window.userData?.credits || 0) ||
      Number(window.userData?.free_credits || 0) + Number(window.userData?.paid_credits || 0);
    if (typeof window.canSendFreeMessage !== 'function' && window.userFullyLoaded && !(window.userData && window.userData.is_subscribed) && chatCreditBalance <= 0) {
      if (subscribeMessageCounter >= 5) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'chat');
        return;
      }
    }

    if (!window.questionsgame || !window.window.questionsgame.game) {
      if (window.gameInitializing) {
        console.warn(
          '[GAME] Game initialization already in progress, skipping...'
        );
        return;
      }

      console.warn('[GAME] Game state not initialized, initializing now...');
      window.gameInitializing = true;

      try {
        if (typeof setupGameInit === 'function') {
          setupGameInit();
        }
        if (!window.questionsgame || !window.questionsgame.game) {
          console.error('[GAME] Failed to initialize game state');
          return;
        }
      } finally {
        window.gameInitializing = false;
      }
    }

    reInitGameState(window.questionsgame.game);
    rebuildGameState(window.questionsgame.game);

    var sentText = $('.question-text').val();
    sentText = sanitizePrompt(sentText);

    setTimeout(function () {
      $('.question-text').val('');
      $('.question-text').focus();
    }, 300);
    if (!sentText.trim() && window.mediaUploadHandler?.pendingChatImages?.length) sentText = 'Describe the attached image.';
    if (sentText.length < 1) return;
    sentText = sentText.trim().replace(/\n+/g, '\n');

    var characterName = fixtures.currentAI?.name || 'Character';
    var userName = 'Noo';
    if (window.userData) {
      userName =
        window.userData && (window.userData?.username || window.userData?.name)
          ? window.userData?.username || window.userData?.name
          : 'Noo';
    }

    if (fixtures.room_name && !isPrivateRoomChat()) {
      var authCheck =
        typeof isUserAuthenticated === 'function' && isUserAuthenticated();
      if (!authCheck || !authCheck.isAuthenticated) {
        console.log(
          '[CHAT] Not authenticated. Showing login dialog before sending message.'
        );
        if (typeof window.showSignupDialog === 'function') {
          window.showSignupDialog();
        } else {
          try {
            window.location.href =
              '/login?next=' +
              encodeURIComponent(
                window.location.pathname + window.location.search
              );
          } catch {
            /* Error ignored */
          }
        }
        return;
      }
    }
    if (fixtures.room_name && !isPrivateRoomChat()) {
      dataAccessor.saveMessage(
        sentText,
        userName,
        window.userData?.avatar_photo_url,
        fixtures.room_key || fixtures.room_name
      );
    }

    window.questionsgame.game.input += `
${userName}: ${sentText}
${characterName}:`;
    window.questionsgame.game.allInputs.push(`${userName}: ${sentText}`);
    window.questionsgame.game.allMessages.push(sentText);
    window.questionsgame.game.allMessagesCharacters.push(userName);

    if (fixtures.room_name) {
      appendToChatLog(`<div class="chat-log__item chat-log__item--own">
                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${window.StringUtils.escapeHtml(sentText)}</div>
                        </div>
                        <div class="chat-log__item loading-chat-bar">

                        </div>`);
    } else {
      appendToChatLog(`<div class="chat-log__item chat-log__item--own">
                            <div class="chat-log__message" contenteditable="true" spellcheck="false">${window.StringUtils.escapeHtml(sentText)}</div>
                        </div>
                        <div class="chat-log__item loading-chat-bar">
                        </div>`);
    }

    async function generateArtFromResponse(artPrompt, fullResponse) {
      try {
        console.log('Generating art for response:', artPrompt);

        let cleanPrompt = artPrompt
          .replace(/[^\w\s,.!?-]/g, '')
          .replace(/\s+/g, ' ')
          .trim();

        let promptAddition = localStorage.getItem('makeArtPromptAddition');
        if (promptAddition === null) {
          promptAddition = 'anime';
        }
        promptAddition = (promptAddition || '')
          .replace(/[^\w\s,.!?-]/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();

        if (promptAddition) {
          cleanPrompt = `${promptAddition}${cleanPrompt ? ' ' + cleanPrompt : ''}`;
        }

        if (cleanPrompt.length < 20) {
          cleanPrompt = `artistic interpretation of: ${cleanPrompt}`;
        }

        let response;
        const makeArtModel = localStorage.getItem('makeArtModel') || 'default';
        if (makeArtModel === 'gpt-image-2') {
          const secret = window.userData && (window.userData.secret || window.userData.api_key);
          if (!secret || secret === 'anonymous') {
            if (window.showSubscribeDialog) window.showSubscribeDialog();
            return;
          }
          const size = localStorage.getItem('makeArtSize') || '1024x1024';
          const result = await fetch('/api/gpt-image-2', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-API-Key': secret,
              Authorization: 'Bearer ' + secret,
            },
            body: JSON.stringify({
              prompt: cleanPrompt,
              size: size,
              quality: 'auto',
              num_images: 1,
              source: 'chat',
            }),
          });
          const decoded = await result.json();
          if (!result.ok || !decoded.success) {
            throw new Error(decoded.error || 'GPT Image 2 generation failed');
          }
          response = {
            path: decoded.image_url,
            credits_charged: decoded.credits_charged,
          };
          if (
            decoded.credits_charged &&
            window.userData &&
            window.userData.credits !== undefined
          ) {
            window.userData.credits -= decoded.credits_charged;
            $('.api-credits-balance-usd').text(
              parseFloat(window.userData.credits).toFixed(2)
            );
            $('.api-credits-balance').text(window.userData.credits);
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem(
                'netwrck_user',
                JSON.stringify(window.userData)
              );
            }
          }
        } else {
          response = await dataAccessor.makeAIImage(
            cleanPrompt,
            `chat_art_${Date.now()}`,
            512,
            512
          );
        }

        // Handle multiple response formats defensively
        let imageUrl;
        if (response && response.path) {
          // Current makeAIImage format: {path: url}
          imageUrl = response.path;
        } else if (response && response.images && response.images.length > 0) {
          // Expected format: {images: [{url: url}]}
          imageUrl = response.images[0].url;
        } else if (
          response &&
          response.result &&
          response.result.images &&
          response.result.images.length > 0
        ) {
          // Raw FAL API format: {result: {images: [{url: url}]}}
          imageUrl = response.result.images[0].url;
        }

        if (imageUrl) {
          const $chatLog = $('.chat-log');
          let wrap = null;
          if (
            window.ChatImageToVideo &&
            typeof window.ChatImageToVideo.appendChatArtImage === 'function'
          ) {
            wrap = window.ChatImageToVideo.appendChatArtImage(
              imageUrl,
              $chatLog[0]
            );
          } else {
            const imgElement = document.createElement('img');
            imgElement.src = imageUrl;
            imgElement.alt = 'Generated art';
            imgElement.style.cssText =
              'max-width: 100%; min-width: 150px; border-radius: 8px; margin-top: 10px;';
            imgElement.loading = 'lazy';
            $chatLog.append(imgElement);
          }

          const extraHeight = (wrap && wrap.offsetHeight) || 200;
          const currentScrollTop = $('.mdl-layout__content').scrollTop();
          $('.mdl-layout__content').scrollTop(currentScrollTop + extraHeight);
        }
      } catch (error) {
        console.error('Error generating art from response:', error);
      }
    }

    async function fetch_result(num_retries) {
      if (fixtures.room_name && num_retries === 0) selectMentionedRoomCharacter(sentText);
      if (fixtures.room_name && fixtures.newRespondingCharacter) {
        window.questionsgame.game.currentCharacter =
          fixtures.newRespondingCharacter;
        fixtures.currentAI = fixtures.newRespondingCharacter;
      }

      var prompt;
      if (fixtures.room_name) {
        prompt = buildPromptChatRoom(window.questionsgame.game);
      } else {
        prompt = buildPrompt(window.questionsgame.game);
      }

      var inputLength = prompt.length;

      var data = {
        text: prompt,
        number_of_results: 1,
        max_length: 100,
        min_length: 1,
        max_sentences: 1,
        min_probability: 0,
        stop_sequences: [
          (window.questionsgame.game.currentCharacter?.name || 'AI') + ':',

          (window.userData?.name || 'Noo') + ':',

          'Noo:',
          'Adam:',
          'john:',
          'adam:',
          'Bob:',
          'George:',
        ],
        top_p: 0.93,
        top_k: 50,
        temperature: 1.0,
        repetition_penalty:
          window.questionsgame.game.repetition_penalty + Math.random() * 0.3,
        seed: 0,
        model: 'chat',
      };
      let userName = 'Noo';
      if (window.userData) {
        userName =
          window.userData &&
          (window.userData?.username || window.userData?.name)
            ? window.userData?.username || window.userData?.name
            : 'Noo';
      }
      let onfulfilled = async (data) => {
        console.log('Success:', data);
        let fullGeneratedText = data[0]['generated_text'];
        let generatedText = fullGeneratedText.substring(inputLength);

        let prediction = stripSpeakerLabel(
          generatedText,
          fixtures.currentAI?.name || 'AI'
        );
        if (isCannedChatPrediction(prediction)) {
          if (num_retries < 3) {
            return fetch_result(num_retries + 1);
          }

          if (!window.questionsgame.game.textGeneratorFallbackUsed) {
            window.questionsgame.game.textGeneratorFallbackUsed = true;
            try {
              const tgText = await fetchTextGeneratorChatFallback({
                query: sentText,
 ...collectChatImageFields(),
                context: data.text,
                examples: window.questionsgame.game.inputOutputPairs,
                greeting: sanitizePrompt(
                  fixtures.currentAI?.greeting || ''
                ).replace('{{user}}', userName),
                character: fixtures.currentAI?.name || '',
              });
              if (tgText) {
                prediction = stripSpeakerLabel(
                  tgText,
                  fixtures.currentAI?.name || 'AI'
                );
              }
            } catch (tgErr) {
              console.error('chat model fallback failed:', tgErr);
            }
          }

          if (isCannedChatPrediction(prediction)) {
            if (!window.questionsgame.game.cannedResponseUsed) {
              const cannedResponses = [
                'yes',
                'no',
                'maybe',
                'can i ask you a question?',
              ];
              prediction =
                cannedResponses[
                  Math.floor(Math.random() * cannedResponses.length)
                ];
              window.questionsgame.game.cannedResponseUsed = true;
              console.warn(
                '[GAME] using canned yes/no — all chat backends failed'
              );
            } else {
              prediction = '...';
            }
          }
        }

        prediction = prediction.replace('{{user}}', userName);
        prediction = prediction.replace('{{ user}}', userName);
        prediction = prediction.replace('{{user }}', userName);
        prediction = prediction.replace('{{ user }}', userName);

        if (prediction === window.questionsgame.game.lastGeneratedText) {
          window.questionsgame.game.input =
            window.questionsgame.game.originalInput +
            `
${userName}: ${sentText}
${fixtures.currentAI?.name || 'AI'}: `;
          if (num_retries < 3) {
            return fetch_result(num_retries + 1);
          }
        }
        window.questionsgame.game.lastGeneratedText = prediction;
        window.mediaUploadHandler?.consumePendingChatImages?.();

        window.questionsgame.game.addInputOutputPair(sentText, prediction);

        window.questionsgame.game.allMessages.push(prediction);
        window.questionsgame.game.allMessagesCharacters.push(
          fixtures.currentAI?.name || 'AI'
        );
        $('.loading-chat-bar').remove();
        console.log('Chat response rendering:', prediction);

        var srcImg = getSrcImgForCharacter(fixtures.currentAI);

        let extraCharacterInfo = `<img class="chat-log-avatar-icon"
                                                             src="${srcImg}"
                                                             alt="${fixtures.currentAI?.name || 'AI'}"
                                                             width="100"
                                                             height="100">
                                                             <p id="" class="chat-one-to-one-name" tabindex="0">${fixtures.currentAI?.name || 'AI'}</p>`;
        if (!fixtures.room_name) {
          extraCharacterInfo = '';
        }
        appendToChatLog(`
                ${extraCharacterInfo}
                                                             <div class="chat-log__item">
                        <div class="chat-log__message" contenteditable="true" spellcheck="false">${window.StringUtils.escapeHtml(prediction)}</div>
                    </div>`);
        window.dispatchEvent(
          new CustomEvent('netwrck:character-response', {
            detail: {
              text: prediction,
              character: fixtures.currentAI?.name || 'AI',
            },
          })
        );
        console.log('Chat response rendered successfully');

        const rawMakeArt = localStorage.getItem('makeArtEnabled');
        const makeArtEnabled =
          (rawMakeArt || '').toLowerCase().trim() === 'true';
        console.log(
          'Art generation enabled:',
          makeArtEnabled,
          'Prediction length:',
          prediction?.length
        );
        if (!isPrivateRoomChat() && makeArtEnabled && prediction && prediction.length > 10) {
          const userContext = sentText ? `User: ${sentText}. ` : '';
          const fullContext = userContext + prediction;
          const artPrompt = fullContext.substring(0, 300);
          console.log('Full art prompt:', artPrompt);
          generateArtFromResponse(artPrompt, prediction);
        }

        if (!isPrivateRoomChat() && typeof window.playSound === 'function') {
          window.playSound(prediction);
        }
        // Background generation probability: default 100% on localhost, 20% otherwise
        // Allow override via global settings.background_generation_pct (0-100)
        var backgroundProbability;
        (function () {
          try {
            var pct =
              typeof window !== 'undefined' &&
              window.settings &&
              typeof window.settings.background_generation_pct === 'number'
                ? window.settings.background_generation_pct
                : null;
            if (pct != null && !isNaN(pct)) {
              // Clamp to [0,100]
              if (pct < 0) pct = 0;
              if (pct > 100) pct = 100;
              backgroundProbability = pct / 100.0;
            } else {
              backgroundProbability =
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1'
                  ? 1.0
                  : 0.2;
            }
          } catch (e) {
            backgroundProbability =
              window.location.hostname === 'localhost' ||
              window.location.hostname === '127.0.0.1'
                ? 1.0
                : 0.2;
          }
        })();

        if (!isPrivateRoomChat() && Math.random() < backgroundProbability) {
          generateNewBackgroundDebounced(prediction);
        }
        var hasAutoArt = !isPrivateRoomChat() && $('#autoart').is(':checked');
        if (!isPrivateRoomChat() && (sentText.trim().indexOf('appears') > -1 || hasAutoArt)) {
          var sent = sanitizePrompt(sentText);
          let promptArtGen = `character portrait ${sent} ${prediction}`;
          if (hasAutoArt) {
            var niceModifiers = [
              'artstation',
              'beautiful',
              'charming',
              'colorful',
              'fantasy',
              'illustration',
              'realistic',

              'aesthetic',
              'desire',
              'lust wow',
            ];
            var modifier =
              window.CollectionUtils && window.CollectionUtils.choose
                ? window.CollectionUtils.choose(niceModifiers)
                : niceModifiers[
                    Math.floor(Math.random() * niceModifiers.length)
                  ];
            var backstory =
              fixtures.currentAI?.description ||
              fixtures.currentAI?.greeting ||
              '';
            promptArtGen = `best quality hd aesthetic ${sent} ${prediction} ${modifier} ${backstory}`;
          }
          generateAICharacterPortrait(promptArtGen);
        }

        if (fixtures.room_name) {
          saveRoomCharacterResponse(prediction, fixtures.currentAI);

          fixtures.lastResponderCharacter =
            fixtures.lastResponderCharacter || fixtures.currentAI;
          var lastResponderCharacterIsSame =
            fixtures.lastResponderCharacter === fixtures.currentAI;

          var randomNum = Math.random();

          if (!fixtures.room_ais) {
            fixtures.room_ais = {};
          }
          var charactersInRoom = fixtures.room_ais;

          charactersInRoom = Object.values(charactersInRoom);

          if (charactersInRoom.length === 0) {
            charactersInRoom = [
              {
                name: 'Assistant',
                greeting: 'Hello!',
                description: 'A helpful assistant',
              },
            ];
          }

          var mentionedCharacter = null;
          if (sentText && charactersInRoom.length > 1) {
            var lowerMsg = sentText.toLowerCase();
            var bestMatch = null;
            var bestLen = 0;
            for (var ci = 0; ci < charactersInRoom.length; ci++) {
              var cname = (charactersInRoom[ci].name || '').toLowerCase();
              if (cname.length > 1 && lowerMsg.indexOf(cname) !== -1 && cname.length > bestLen) {
                bestMatch = charactersInRoom[ci];
                bestLen = cname.length;
              }
            }
            mentionedCharacter = bestMatch;
          }

          if (mentionedCharacter) {
            fixtures.newRespondingCharacter = mentionedCharacter;
          } else if (randomNum < 0.1) {
            fixtures.newRespondingCharacter = fixtures.currentAI;
          } else if (randomNum < 0.2) {
            var randomCharacter =
              window.CollectionUtils && window.CollectionUtils.choose
                ? window.CollectionUtils.choose(charactersInRoom)
                : charactersInRoom[
                    Math.floor(Math.random() * charactersInRoom.length)
                  ];
            fixtures.newRespondingCharacter = randomCharacter;
          } else {
            if (lastResponderCharacterIsSame) {
              const randomCharacterChoice =
                window.CollectionUtils && window.CollectionUtils.choose
                  ? window.CollectionUtils.choose(charactersInRoom)
                  : charactersInRoom[
                      Math.floor(Math.random() * charactersInRoom.length)
                    ];
              fixtures.newRespondingCharacter = randomCharacterChoice;
            } else {
              fixtures.newRespondingCharacter = fixtures.lastResponderCharacter;
            }
          }
          fixtures.lastResponderCharacter = fixtures.currentAI;
        }
      };

      var examples = fixtures.room_name ? [] : window.questionsgame.game.inputOutputPairs;

      var selectedModel = $('#ai-model-selector').val();
      if (window.nsfwChatRouter && typeof window.nsfwChatRouter.preferDrummerIfNSFW === 'function') {
        try {
          selectedModel = await window.nsfwChatRouter.preferDrummerIfNSFW(
            {
              query: sentText,
 ...collectChatImageFields(),
              context:
                (window.questionsgame.game.backstory || '') +
                '\n' +
                (data && data.text ? data.text : ''),
              greeting: sanitizePrompt(fixtures.currentAI?.greeting || ''),
              examples: window.questionsgame.game.inputOutputPairs || [],
            },
            selectedModel
          );
        } catch (nsfwErr) {
          console.warn('nsfw chat router failed', nsfwErr);
        }
      }

      let currentUserName = userName;
      if (window.userData?.username) {
        currentUserName = window.userData?.username;
      } else if (window.userData?.name) {
        currentUserName = window.userData
          ? window.userData?.username || window.userData?.name
          : undefined;
      }

      const predictionsEnabled =
        window.fixtures && window.fixtures.predictions_enabled;

      var route = '/api/chatpred';
      if (num_retries === 0 && !fixtures.room_name) {
        let context =
          `concise role play chat\n desirable main character ${currentUserName}\n` +
          (fixtures.currentAI?.name || 'AI') +
          '\n' +
          (window.questionsgame.game.backstory || '');
        if (fixtures.room_name) context = buildRoomChatContext(window.questionsgame.game);

        if (window.personasManager && window.personasManager.currentPersona) {
          const personaContext =
            window.personasManager.getCurrentPersonaContext();
          if (personaContext && personaContext.backstory) {
            context += `\n\nUser persona: ${personaContext.backstory}`;
            if (personaContext.greeting) {
              context += `\nUser introduction: ${personaContext.greeting}`;
            }
          }
        }

        const netwrck_base_url = '/api/chatpred_or';
        var chatPromise = fetch(netwrck_base_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
            query: sentText,
 ...collectChatImageFields(),
            context: context,
            examples: examples,
            greeting: sanitizePrompt(
              fixtures.currentAI?.greeting || ''
            ).replace('{{user}}', currentUserName),
            model_name: selectedModel,
            username: currentUserName,
            character: fixtures.currentAI?.name || '',
            web_tools: chatWebToolsEnabled(),
            use_usage_credits:
              !(window.userData && window.userData.is_subscribed) &&
              window.userData?.use_api_credits_for_chat === true &&
              chatCreditBalance > 0,
          }),
        })
          .then(async (response) => {
            const payload = await response.json();
            if (response.status === 402) {
              const subscribe = window.confirm(
                'Subscribe for unlimited chat. Select Cancel to buy usage credits instead.'
              );
              if (subscribe && window.showSubscribeDialog)
                window.showSubscribeDialog();
              if (!subscribe && window.showTopupDialog)
                window.showTopupDialog();
              throw new Error(payload.error || 'Chat payment required');
            }
            if (!response.ok)
              throw new Error(payload.error || 'Chat request failed');
            return payload;
          })
          .then((response_json) => {
            const responseText = extractChatPredText(response_json);
            if (!responseText) throw new Error('Empty chat response');
            if (!responseText) {
              console.log('Unexpected response format:', response_json);
              return '';
            }

            return [
              {
                generated_text: data.text + responseText,
              },
            ];
          })

          .catch((error) => {
            return '';
          });
      } else if (predictionsEnabled && !collectChatImageFields().images.length && (num_retries === 1 || num_retries === 0)) {
        const netwrck_base_url_pred = '/api/predictions';
        let inputsCopy = JSON.parse(JSON.stringify(data));
        inputsCopy['stop_sequences'].push('User 0:');
        inputsCopy['stop_sequences'].push('User 1:');

        if (fixtures.room_name) {
          var characters = new Set();
          let lastCharactersTalking =
            window.questionsgame.game.allMessagesCharacters.slice(-6);
          for (var i = 0; i < lastCharactersTalking.length; i++) {
            var character = lastCharactersTalking[i];
            characters.add(character + ':');
          }
          inputsCopy['stop_sequences'].push(...characters);

          inputsCopy['stop_sequences'] = [
            ...new Set(inputsCopy['stop_sequences']),
          ];
        }

        chatPromise = fetch(netwrck_base_url_pred, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Rapid-API-Key': 'YHWEFVNWHXIWNGVSH',
          },
          body: JSON.stringify(inputsCopy),
        })
          .then((response) => {
            if (response.status !== 200) {
              throw new Error('Bad response from server');
            }
            return response;
          })
          .then((response) => {
            if (!response.ok) throw new Error('Chat request failed: ' + response.status);
            return response.json();
          })

          .catch((error) => {
            return fetch('/api/chatpred', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ query: data.text, ephemeral: isPrivateRoomChat() }),
            })
              .then((response) => {
            if (!response.ok) throw new Error('Chat request failed: ' + response.status);
            return response.json();
          })
              .then((response_json) => {
                const responseText = extractChatPredText(response_json);
                if (!responseText) throw new Error('Empty chat response');
                return [
                  {
                    generated_text: data.text + (responseText || ''),
                  },
                ];
              })
              .catch((error) => {
                console.error('Error from TG:', error);
                return '';
              });
          });
      } else if (num_retries > 3) {
        const fallbackGreeting = sanitizePrompt(
          fixtures.currentAI?.greeting || ''
        ).replace('{{user}}', userName);

        chatPromise = fetch('/api/chatpred_or', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
            query: sentText,
 ...collectChatImageFields(),
            context: data.text,
            examples: examples,
            greeting: fallbackGreeting,
            model_name: 'openai/gpt-6-luna',
          }),
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error('chatpred_or request failed');
            }
            return response.json();
          })
          .then((response_json) => {
            const responseText = extractChatPredText(response_json);
            if (!responseText) throw new Error('Empty chat response');
            if (!responseText) {
              throw new Error('chatpred_or empty response');
            }
            return [
              {
                generated_text: data.text + (responseText || ''),
              },
            ];
          })

          .catch((error) => {
            console.error('chatpred_or retry fallback failed:', error);
            return fetch('/api/chatpred', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
                query: sentText,
 ...collectChatImageFields(),
                context: data.text,
                examples: examples,
                greeting: fallbackGreeting,
              }),
            })
              .then((response) => {
            if (!response.ok) throw new Error('Chat request failed: ' + response.status);
            return response.json();
          })
              .then((response_json) => {
                const responseText = extractChatPredText(response_json);
                if (!responseText) throw new Error('Empty chat response');
                return [
                  {
                    generated_text: data.text + (responseText || ''),
                  },
                ];
              })
              .catch(() => {
                return '';
              });
          });
      } else {
        let context =
          'You are a fun role play character named:' +
          (fixtures.currentAI?.name || 'AI') +
          '\n' +
          (window.questionsgame.game.backstory || '');
        if (fixtures.room_name) context = buildRoomChatContext(window.questionsgame.game);

        if (window.personasManager && window.personasManager.currentPersona) {
          const personaContext =
            window.personasManager.getCurrentPersonaContext();
          if (personaContext && personaContext.backstory) {
            context += `\n\nUser persona: ${personaContext.backstory}`;
            if (personaContext.greeting) {
              context += `\nUser introduction: ${personaContext.greeting}`;
            }
          }
        }

        selectedModel = $('#ai-model-selector').val();
        if (window.nsfwChatRouter && typeof window.nsfwChatRouter.preferDrummerIfNSFW === 'function') {
          try {
            selectedModel = await window.nsfwChatRouter.preferDrummerIfNSFW(
              {
                query: sentText,
 ...collectChatImageFields(),
                context: (window.questionsgame.game.backstory || '') + '\n' + (typeof context === 'string' ? context : ''),
                greeting: sanitizePrompt(fixtures.currentAI?.greeting || ''),
                examples: examples || window.questionsgame.game.inputOutputPairs || [],
              },
              selectedModel
            );
          } catch (nsfwErr) {
            console.warn('nsfw chat router failed', nsfwErr);
          }
        }

        let currentUserName = userName;
        if (window.userData && window.userData?.username) {
          currentUserName = window.userData?.username;
        } else if (
          window.userData &&
          (window.userData?.username || window.userData?.name)
        ) {
          currentUserName = window.userData?.username || window.userData?.name;
        }

        chatPromise = fetch(route, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
            query: sentText,
 ...collectChatImageFields(),
            context: context,
            examples: examples,
            greeting: sanitizePrompt(
              fixtures.currentAI?.greeting || ''
            ).replace('{{user}}', currentUserName),
            model_name: selectedModel,
            username: currentUserName,
            character: fixtures.currentAI?.name || '',
          }),
        })
          .then((response) => {
            if (!response.ok) throw new Error('Chat request failed: ' + response.status);
            return response.json();
          })
          .then((response_json) => {
            const responseText = extractChatPredText(response_json);
            if (!responseText) throw new Error('Empty chat response');
            return [
              {
                generated_text: data.text + (responseText || ''),
              },
            ];
          })

          .catch((error) => {
            return '';
          })
          .then((response_json) => {
            if (response_json.length > 0) {
              return response_json;
            }

            const fallbackGreeting = sanitizePrompt(
              fixtures.currentAI?.greeting || ''
            ).replace('{{user}}', currentUserName);

            const chatpredOrPayload = {
              ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
              query: sentText,
 ...collectChatImageFields(),
              context: context,
              examples: examples,
              greeting: fallbackGreeting,
              model_name: 'openai/gpt-6-luna',
              character: fixtures.currentAI?.name || '',
            };

            return fetch('/api/chatpred_or', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(chatpredOrPayload),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error('chatpred_or request failed');
                }
                return response.json();
              })
              .then((fallbackJson) => {
                const fallbackText = extractChatPredText(fallbackJson);
                if (!fallbackText) {
                  throw new Error('chatpred_or returned empty response');
                }
                return [
                  {
                    generated_text: data.text + (fallbackText || ''),
                  },
                ];
              })
              .catch((error) => {
                console.error('chatpred_or fallback failed:', error);
                return fetch('/api/chatpred', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    ephemeral: isPrivateRoomChat(),
            room_key: fixtures.room_key || fixtures.room_name || '',
                    query: sentText,
 ...collectChatImageFields(),
                    context: context,
                    examples: examples,
                    greeting: fallbackGreeting,
                  }),
                })
                  .then((response) => {
            if (!response.ok) throw new Error('Chat request failed: ' + response.status);
            return response.json();
          })
                  .then((response_json) => {
                    const responseText = extractChatPredText(response_json);
                    if (!responseText) throw new Error('Empty chat response');
                    return [
                      {
                        generated_text: data.text + (responseText || ''),
                      },
                    ];
                  })
                  .catch(() => {
                    return '';
                  });
              });
          });
      }

      var resultOnce = false;
      var resultFn = async function (result) {
        if (resultOnce) {
          return;
        }
        resultOnce = true;

        subscribeMessageCounter++;
        setCookie('subscribeMessageCounter', subscribeMessageCounter, 365);

        await onfulfilled(result);
      };
      var bothFailed = false;
      chatPromise.then(async (result) => {
        if (result) {
          return resultFn(result);
        }

        if (num_retries <= 3) {
          return fetch_result(num_retries + 1);
        }
        $('.loading-chat-bar').remove();
        if (window.showToast) window.showToast('Unable to generate a reply. Try again.');
      }).catch(() => {
        $('.loading-chat-bar').remove();
        if (window.showToast) window.showToast('Unable to generate a reply. Try again.');
      });
    }

    fetch_result(0);
    return false;
  });

  $('#add-new-ai-form').submit(function (e) {
    e.preventDefault();
    e.stopPropagation();
    var form = e.currentTarget;
    if (form.dataset.submitting === 'true') return false;
    form.dataset.submitting = 'true';
    var submitButton = form.querySelector('#create-ai');
    if (submitButton) submitButton.disabled = true;
    let ainame = $('#add-new-ai-form input[name="ainame"]').val();
    let aidescription = $('#aidescription').val();
    let aigreeting = $('#aigreeting').val();
    let newaiavatarurl = $(
      '#add-new-ai-form input[name="newaiavatarurl"]'
    ).val();
    let newaibucketname = $(
      '#add-new-ai-form input[name="newaibucketname"]'
    ).val();
    if (isPrivateRoomChat()) {
      ainame = String(ainame || '').trim();
      try {
        if (!ainame || ainame.length > 200) throw new Error('Character name must be 1 to 200 characters');
        if (!fixtures.room_ais[ainame] && Object.keys(fixtures.room_ais).length >= 8) throw new Error('Rooms support up to 8 characters');
        var localAI = { name: ainame, description: aidescription || '', greeting: aigreeting || '', avatar_file_name: newaiavatarurl || '' };
        dataAccessor.aisByName[ainame] = localAI;
        applyRoomCharacters({ ...fixtures.room_ais, [ainame]: localAI });
        document.getElementById('create-new-ai-dialog')?.close();
        triggerCharacterRoomResponse(localAI);
      } catch (error) {
        reportRoomCharacterFailure('[PRIVATE-CHARACTER]', error);
      } finally {
        form.dataset.submitting = 'false';
        if (submitButton) submitButton.disabled = false;
      }
      return false;
    }
    let aipublic = $('#add-new-ai-form input[name="publicai"]').is(':checked');
    dataAccessor
      .saveAICharacter(
        ainame,
        aidescription,
        aigreeting,
        newaiavatarurl,
        aipublic,
        newaibucketname
      )
      .then(async (createdAI) => {
        var createDialog = document.getElementById('create-new-ai-dialog');
        var targetRoomKey = createDialog
          ? String(createDialog.dataset.roomKey || '')
          : '';
        if (targetRoomKey) {
          var roomCharacter = {
            ...createdAI,
            name: createdAI.name || ainame,
            description: createdAI.description || aidescription,
            greeting: createdAI.greeting || aigreeting,
            avatar_file_name:
              createdAI.avatar_file_name || newaiavatarurl || '',
          };
          var nextRoomAIs = { ...(fixtures.room_ais || {}) };
          nextRoomAIs[roomCharacter.name] = roomCharacter;
          var savedRoomAIs = await persistRoomCharacters(
            nextRoomAIs,
            targetRoomKey
          );
          applyRoomCharacters(savedRoomAIs);
          if (createDialog.open) createDialog.close();
          delete createDialog.dataset.roomKey;
          triggerCharacterRoomResponse(roomCharacter);
          return;
        }
        ReRenderCurrentGame();
        if (fixtures.currentAI?.avatar_file_name) {
          var avatarFileName = fixtures.currentAI?.avatar_file_name;

          if (avatarFileName) {
            avatarFileName = avatarFileName.replace(' ', '-');
          }
          avatarFileName = avatarFileName.trim();
          if (avatarFileName.indexOf('static/uploads') === -1) {
            avatarFileName = 'static/uploads/' + avatarFileName;
          }
          $('.chat-one-to-one-icon').attr(
            'src',
            'https://netwrckstatic.netwrck.com/' + avatarFileName
          );
        } else {
          $('.chat-one-to-one-icon').attr(
            'src',
            'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
          );
        }
        $('.chat-one-to-one-name').text(fixtures.currentAI?.name || 'AI');
        $('.chat-one-to-one-name-tags').html(
          window.getTagsTemplate(fixtures.currentAI?.tags || null)
        );
        $('.chat-one-to-one-name-tooltip').text(
          fixtures.currentAI?.description || ''
        );

        var addChatDialog = document.getElementById('add-chat-dialog');
        if (addChatDialog && addChatDialog.open) addChatDialog.close();
        createDialog = document.getElementById('create-new-ai-dialog');
        if (createDialog && createDialog.open) createDialog.close();
        var urlkey = createdAI?.url_name || (fixtures.currentAI?.name
          ? encodeURIComponent(fixtures.currentAI.name.replace(/ /g, '-'))
          : '');
        if (window.APP && typeof window.APP.goto === 'function') {
          window.APP.goto('/ai-chat/' + urlkey);
        } else {
          window.location.assign('/ai-chat/' + urlkey);
        }
      })
      .catch((error) => {
        console.error('Error creating AI character:', error);
        if (window.showToast) {
          window.showToast(error.message || 'Character creation failed');
        }
      })
      .finally(() => {
        form.dataset.submitting = 'false';
        if (submitButton) submitButton.disabled = false;
      });
    return false;
  });
  if (
    window.componentHandler &&
    typeof window.componentHandler.upgradeDom === 'function'
  ) {
    window.componentHandler.upgradeDom();
  }

  // Auto-upgrade MDL components after dynamic content changes
  if (
    typeof MutationObserver !== 'undefined' &&
    !window._mdlAutoUpgradeInitialized
  ) {
    window._mdlAutoUpgradeInitialized = true;
    const observer = new MutationObserver(function (mutations) {
      let shouldUpgrade = false;
      for (let mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (let node of mutation.addedNodes) {
            if (
              node.nodeType === 1 &&
              node.className &&
              node.className.indexOf &&
              node.className.indexOf('mdl-') !== -1
            ) {
              shouldUpgrade = true;
              break;
            }
          }
        }
      }
      if (shouldUpgrade && typeof componentHandler !== 'undefined') {
        setTimeout(function () {
          componentHandler.upgradeDom();
        }, 10);
      }
    });

    // Start observing
    if (document.body) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  return self;
};
window.onceGameInit = false;
window.chatInitialized = false;
window.setupGameOnce = function () {
  if (window.onceGameInit) {
    return;
  }
  window.onceGameInit = true;

  $(setupGameInit);
  window.chatInitialized = true;
};
$(document).on('user-ready', function (evt, user) {
  'use strict';
  setupGameOnce();
  if (
    window.userData &&
    !window.userData?.is_subscribed &&
    window.userData?.name
  ) {
    // Placeholder for unsubscribed user logic
  }
});
if (window.requestIdleCallback) {
  window.requestIdleCallback(function () { window.setupGameOnce(); }, { timeout: 2000 });
} else {
  window.setTimeout(function () { window.setupGameOnce(); }, 1500);
}

// Ensure global setCookie exists to avoid ReferenceError during early usage
if (typeof window.setCookie !== 'function') {
  window.setCookie = function (name, value, days) {
    try {
      if (
        window.CookieUtils &&
        typeof window.CookieUtils.setCookie === 'function'
      ) {
        return window.CookieUtils.setCookie(name, value, days);
      }
      var expires = '';
      if (days) {
        var date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        expires = '; expires=' + date.toGMTString();
      }
      document.cookie =
        name + '=' + value + expires + '; path=/; SameSite=Strict';
    } catch (e) {
      console.error('Error setting cookie:', e);
    }
  };
}

function ReRenderCurrentGame() {
  if (
    window.location.href.endsWith('/ai-chatbots') ||
    window.location.href.indexOf('/tools/') !== -1
  ) {
    return;
  }

  if (!fixtures.currentAI && !fixtures.room_name) {
    return;
  }

  if (!fixtures.currentAI && fixtures.room_name && fixtures.room_ais) {
    const roomAIKeys = Object.keys(fixtures.room_ais);
    if (roomAIKeys.length > 0) {
      fixtures.currentAI = fixtures.room_ais[roomAIKeys[0]];
    } else {
      fixtures.currentAI = {
        name: 'Assistant',
        greeting: 'Welcome to ' + fixtures.room_name + '!',
        description: "I'm here to help facilitate the conversation.",
      };
    }
  }

  let userName = 'Noo';
  if (window.userData) {
    userName =
      window.userData && (window.userData?.username || window.userData?.name)
        ? window.userData?.username || window.userData?.name
        : 'Noo';
  }

  const chatModeActive =
    fixtures.room_name &&
    (!window.chatModeState ||
      window.chatModeState.currentMode === 'chatroom');
  const chatLogHasExisting = $('.chat-log').children().length > 0;

  // Only clear chat log if we're not loading a saved game
  // The saved game will populate the chat log itself
  if (!fixtures.save_game_json) {
    if (chatModeActive && chatLogHasExisting) {
      console.log(
        '[GAME] Preserving existing chatroom log during re-render (chat-mode active)'
      );
    } else {
      $('.chat-log').html('');
    }
  }

  let input = 'Welcome to the chat!';
  if (fixtures.currentAI) {
    input =
      fixtures.currentAI?.greeting || fixtures.currentAI?.description || '';
  }
  if (!input || input === 'Welcome to the chat!') {
    input = fixtures.room_name
      ? 'Welcome to the ' + fixtures.room_name + ' chatroom!'
      : 'Welcome to the chat!';
  }
  if (fixtures.save_game_json && fixtures.save_game_json.backstory) {
    input = fixtures.save_game_json.backstory;
  }

  input = input.replace('{{name}}', userName);
  input = input.replace('{{user}}', userName);
  input = input.replace('[user]', userName);
  if (userName !== 'Noo') {
    input = input.replace(
      'Login and set your name or everyone calls you... Noo',
      'Hi ' + userName
    );
  }

  if (fixtures.currentAI) {
    input = (fixtures.currentAI?.name || 'AI') + ': ' + input;
  } else {
    input = 'System: ' + input;
  }
  window.questionsgame.game = new window.questionsgame.Game(input);

  window.questionsgame.game.lastGeneratedText = sanitizePrompt(
    fixtures.currentAI?.greeting || input
  ).replace(
    '{{user}}',
    window.userData?.username || window.userData?.name || 'Noo'
  );

  window.questionsgame.game.cannedResponseUsed = false;
  window.questionsgame.game.textGeneratorFallbackUsed = false;

  if (window.userData?.is_subscribed) {
    $('.subscribe-btn-main').hide();
  }

  if (fixtures.currentAI?.voice) {
    // Voice setup handled elsewhere
  }

  $('#main-chatbox').focus();
}

$(document).on('click', '#mic-talk, #voice-waveform-container', function () {
  var $mic = $('#mic-talk');
  var $waveContainer = $('#voice-waveform-container');
  if (window.VoiceTranscribe && window.VoiceTranscribe.recording) {
    window.VoiceTranscribe.stop(true);
    return;
  }
  if (!window.VoiceTranscribe) {
    console.error('VoiceTranscribe not loaded');
    return;
  }

  var canvas = document.getElementById('voice-waveform');
  var timerEl = document.getElementById('voice-rec-timer');
  VoiceTranscribe.setCanvas(canvas, timerEl);

  VoiceTranscribe.onStateChange = function (state) {
    if (state === 'recording' || state === 'speaking') {
      $mic.addClass('pulse redish-icon');
      $mic.find('span').html('mic_off');
      $waveContainer.css('display', 'inline-block');
    } else if (state === 'transcribing') {
      $mic.addClass('pulse');
      $mic.removeClass('redish-icon');
      $mic.find('span').html('hourglass_top');
      $waveContainer.css('display', 'none');
    } else {
      $mic.removeClass('pulse redish-icon');
      $mic.find('span').html('mic');
      $waveContainer.css('display', 'none');
    }
  };

  VoiceTranscribe.onPartialText = function (text) {
    if (text) {
      $('.question-text').attr('placeholder', text + '...');
    }
  };

  VoiceTranscribe.onFinalText = function (text) {
    if (!text) return;
    var el = document.getElementsByClassName('question-text')[0];
    if (!el) return;
    var current = el.value || el.innerHTML || '';
    if (current.length > 0 && current[current.length - 1] !== ' ' && text[0] !== ' ') {
      text = ' ' + text;
    }
    if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
      el.value += text;
    } else {
      el.innerHTML += text;
    }
    $('.question-text').attr('placeholder', 'Chat');
    $('.form-horizontal').submit();
  };

  VoiceTranscribe.start();
});

$(document).on('click', '[data-message-action="copy"]', async function (event) {
  event.preventDefault();
  var chatItem = event.currentTarget.closest('.chat-log__item');
  var message = chatItem && chatItem.querySelector('.chat-log__message');
  var text = message ? (message.innerText || message.textContent || '').trim() : '';
  if (!text) {
    return;
  }
  var copied = false;
  if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      copied = true;
    } catch (error) {}
  }
  if (!copied) {
    var textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', 'readonly');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      copied = document.execCommand('copy');
    } catch (error) {}
    textarea.remove();
  }
  if (copied && typeof window.showNotification === 'function') {
    window.showNotification('Copied');
  }
});

$(document).ready(function () {
  hydrateChatMessageActions(document);
});

function roomCharacterChatHref(character) {
  var name =
    (character && (character.url_name || character.URLName || character.name)) ||
    '';
  return '/ai-chat/' + encodeURIComponent(name);
}

function roomCharacterChatAnchor(character) {
  var escapeHtml =
    window.StringUtils && typeof window.StringUtils.escapeHtml === 'function'
      ? window.StringUtils.escapeHtml
      : function (value) {
          if (value === null || value === undefined) return '';
          return String(value).replace(/[&<>"']/g, function (match) {
            var escapeMap = {
              '&': '&amp;',
              '<': '&lt;',
              '>': '&gt;',
              '"': '&quot;',
              "'": '&#39;',
            };
            return escapeMap[match] || match;
          });
        };
  var label = escapeHtml((character && character.name) || '');
  return (
    '<a class="room-character-chat-link" href="' +
    roomCharacterChatHref(character) +
    '" target="_blank" rel="noopener" data-bypass="true" title="Chat 1:1 with ' +
    label +
    ' in a new tab">' +
    label +
    '</a>'
  );
}

let renderAIsInRoom = function (ais) {
  console.log('[RENDER] renderAIsInRoom called with:', ais);

  if (!ais || typeof ais !== 'object') {
    console.warn('[RENDER] Invalid ais parameter:', ais);
    return;
  }

  var aiCharacters = Object.values(ais);
  console.log('[RENDER] AI characters to render:', aiCharacters.length);

  var templates = [];
  for (var i = 0; i < aiCharacters.length; i++) {
    var character = aiCharacters[i];
    if (character && character.name) {
      templates.push(
        `<div class="mdl-list__item mdl-list__item--three-line shadow-boxed">
                            <span class="mdl-list__item-primary-content">
                                <!--                  <i class="contact-icon material-icons mdl-list__item-avatar">person</i>-->
                                <span>${roomCharacterChatAnchor(character)}</span>
                                <a class="kick-character-from-room floating-button mdl-list__item-secondary-action" href="#" data-name="${character.name}">
                                        <button class="new-chat-button mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-js-ripple-effect mdl-button--colored" data-upgraded=",MaterialButton,MaterialRipple">
                                            <i class="material-icons">delete</i>
                                        <span class="mdl-button__ripple-container"><span class="mdl-ripple is-animating" style="width: 160.392px; height: 160.392px; transform: translate(-50%, -50%) translate(42px, 28px);"></span></span></button>
                                    </a>
                            </span>
                        </div>`
      );
    }
  }

  var $target = $('#autoCompleteUsersInRoomList');
  console.log('[RENDER] Target element found:', $target.length > 0);

  if ($target.length > 0) {
    $target.html(templates.join(''));
    console.log('[RENDER] Updated room list with', templates.length, 'AIs');
  } else {
    console.warn(
      '[RENDER] Target element #autoCompleteUsersInRoomList not found'
    );
  }
};
var static_url = 'https://netwrckstatic.netwrck.com/';
let renderAIsInRoomList = function (ais) {
  console.log('[RENDER] renderAIsInRoomList called with:', ais);

  if (!ais || typeof ais !== 'object') {
    console.warn('[RENDER] Invalid ais parameter for room list:', ais);
    return;
  }

  var aiCharacters = Object.values(ais);
  console.log('[RENDER] AI characters for room list:', aiCharacters.length);

  var templates = [];
  for (var i = 0; i < aiCharacters.length; i++) {
    var character = aiCharacters[i];
    if (character && character.name) {
      var fileName = getImageFileNameForAI(character);

      templates.push(
        `<div>
                    <a class="room-character-chat-link" href="${roomCharacterChatHref(character)}" target="_blank" rel="noopener" data-bypass="true" title="Chat 1:1 with ${character.name} in a new tab"><img class="chat-log-avatar-icon"
                          src="${static_url}${fileName}"
                          alt="${character.name}"
                          width="100"
                          height="100"></a>
                    <p id="chat-one-to-one-name-group${i}" class="chat-group-member chat-group-name chat-one-to-one-name-group${i}">
                        ${roomCharacterChatAnchor(character)}</p>
                    <div class="chat-one-to-one-name-tooltip playground-tooltip mdl-tooltip mdl-tooltip--large mdl-tooltip--right"
                         for="chat-one-to-one-name-group${i}">${character.description || 'AI Character'}</div>
                    <div class="clear">
                    </div>
                    <br>
                </div>`
      );
    }
  }

  var $target = $('#chatroom-ais-in-room');
  console.log('[RENDER] Room list target element found:', $target.length > 0);

  if ($target.length > 0) {
    $target.html(templates.join(''));
    console.log(
      '[RENDER] Updated chatroom display with',
      templates.length,
      'AIs'
    );
  } else {
    console.warn('[RENDER] Target element #chatroom-ais-in-room not found');
  }

  if (typeof window.syncEmptyRoomSetupDialog === 'function') {
    window.syncEmptyRoomSetupDialog();
  }
};

function getRoomCharacterCount(roomAIs) {
  if (!roomAIs || typeof roomAIs !== 'object') {
    return 0;
  }

  return Object.values(roomAIs).filter(function (character) {
    return !!(character && character.name);
  }).length;
}

function getCurrentRoomDialogKey() {
  var path = window.location.pathname || '';
  if (/^\/r\/.+/.test(path)) {
    return path;
  }

  var roomName =
    window.fixtures && window.fixtures.room_name
      ? String(window.fixtures.room_name).trim()
      : '';
  if (!roomName) {
    return '';
  }

  return (
    '/r/' +
    encodeURIComponent(roomName.replace(/\s+/g, '-').toLowerCase())
  );
}

function getCurrentRoomKey() {
  if (isPrivateRoomChat()) return '';
  var fixtureKey =
    window.fixtures && window.fixtures.room_key
      ? String(window.fixtures.room_key).trim()
      : '';
  if (fixtureKey) return fixtureKey;
  var match = (window.location.pathname || '').match(/^\/r\/([^/]+)/);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch (_) {
    return match[1];
  }
}

function applyRoomCharacters(roomAIs) {
  fixtures.room_ais = roomAIs && typeof roomAIs === 'object' ? roomAIs : {};
  renderAIsInRoom(fixtures.room_ais);
  renderAIsInRoomList(fixtures.room_ais);
}

function triggerCharacterRoomResponse(ai) {
  if (!ai?.name) return;
  var escape = function (value) {
    var node = document.createElement('span');
    node.textContent = String(value || '');
    return node.innerHTML.replace(/"/g, '&quot;');
  };
  var name = escape(ai.name);
  var greeting = escape(ai.greeting || "Hello everyone! I'm " + ai.name + '.');
  var srcImg = escape(getSrcImgForCharacter(ai));
  appendToChatLog(`<img class="chat-log-avatar-icon" src="${srcImg}" alt="${name}" width="100" height="100"><p class="chat-group-name" tabindex="0">${name}</p><div class="chat-log__item"><div class="chat-log__message" contenteditable="true" spellcheck="false">${greeting}</div></div>`, undefined, {
    username: ai.name, character_slug: ai.url_name || ai.URLName || ai.name,
  });
  if (window.questionsgame?.game) {
    window.questionsgame.game.allMessages.push(ai.greeting || "Hello everyone! I'm " + ai.name + '.');
    window.questionsgame.game.allMessagesCharacters.push(ai.name);
  }
  if (typeof playNotification === 'function') playNotification();
}

var visitorRoomCharacters = {};
var visitorHiddenRoomCharacters = {};
var sharedRoomCharacters = null;
var visitorRoomKey = '';

function resetRoomCharacterScope() {
  var key = getCurrentRoomKey();
  if (key === visitorRoomKey) return;
  visitorRoomKey = key;
  visitorRoomCharacters = {};
  visitorHiddenRoomCharacters = {};
  sharedRoomCharacters = null;
}

function isVisitorRoomCharacter(name) {
  resetRoomCharacterScope();
  return Object.keys(visitorRoomCharacters).some(function (localName) {
    return localName.toLowerCase() === String(name || '').toLowerCase();
  });
}

function saveRoomCharacterResponse(text, character) {
  if (isPrivateRoomChat()) return;
  if (isVisitorRoomCharacter(character?.name)) return;
  return dataAccessor.saveMessage(text, character?.name || 'AI', character?.avatar_file_name || '', fixtures.room_key || fixtures.room_name);
}

function applySharedRoomCharacters(roomAIs) {
  if (isPrivateRoomChat()) return;
  resetRoomCharacterScope();
  sharedRoomCharacters = roomAIs || {};
  var merged = { ...sharedRoomCharacters, ...visitorRoomCharacters };
  Object.keys(visitorHiddenRoomCharacters).forEach(function (name) {
    delete merged[name];
  });
  applyRoomCharacters(merged);
}

function applyVisitorRoomCharacters(nextRoomAIs) {
  resetRoomCharacterScope();
  if (!sharedRoomCharacters) sharedRoomCharacters = { ...(fixtures.room_ais || {}) };
  visitorRoomCharacters = {};
  visitorHiddenRoomCharacters = {};
  Object.keys(nextRoomAIs || {}).forEach(function (name) {
    if (!sharedRoomCharacters[name]) visitorRoomCharacters[name] = nextRoomAIs[name];
  });
  Object.keys(sharedRoomCharacters).forEach(function (name) {
    if (!nextRoomAIs[name]) visitorHiddenRoomCharacters[name] = true;
  });
  if (window.showToast) window.showToast('Changed for you only while you are here.');
  return nextRoomAIs || {};
}

var ROOM_CHARACTER_AUTH_CODE = 'AUTHENTICATION_REQUIRED';

function isRoomCharacterEditor() {
  if (window.userData && window.userData.id) return true;
  var authResult = isUserAuthenticated();
  return !!(authResult && authResult.isAuthenticated);
}

function promptLoginDialog(event) {
  var next = window.location.pathname + (window.location.search || '');
  if (typeof window.showLoginDialog === 'function') {
    window.showLoginDialog(event || null, next);
  } else {
    window.location.href = '/login?next=' + encodeURIComponent(next);
  }
}

function promptRoomCharacterLogin(event) {
  document.querySelectorAll('dialog[open]').forEach(function (openDialog) {
    if (openDialog.id === 'login-dialog' || openDialog.id === 'signup-dialog') {
      return;
    }
    try {
      openDialog.close();
    } catch (closeError) {
      console.warn(
        '[ROOM-AUTH] Failed to close dialog before login:',
        closeError
      );
    }
  });
  promptLoginDialog(event);
}

function requireRoomCharacterLogin(event) {
  if (isPrivateRoomChat()) return true;
  if (isRoomCharacterEditor()) return true;
  promptRoomCharacterLogin(event);
  return false;
}

function roomCharacterAuthError() {
  var error = new Error('Please log in to add characters to this room.');
  error.code = ROOM_CHARACTER_AUTH_CODE;
  return error;
}

function reportRoomCharacterFailure(context, error) {
  if (error && error.code === ROOM_CHARACTER_AUTH_CODE) {
    return;
  }
  console.error(context, error && error.message);
  if (window.showToast) {
    window.showToast(
      (error && error.message) || 'Room character update failed'
    );
  }
}

function persistRoomCharacters(nextRoomAIs, explicitRoomKey) {
  if (isPrivateRoomChat()) return Promise.resolve(nextRoomAIs || {});
  resetRoomCharacterScope();
  var roomKey = explicitRoomKey || getCurrentRoomKey();
  if (!roomKey) return Promise.reject(new Error('Room key is unavailable'));
  var names = Object.keys(nextRoomAIs || {}).filter(Boolean);
  if (!isRoomCharacterEditor()) {
    promptRoomCharacterLogin(null);
    return Promise.reject(roomCharacterAuthError());
  }
  return fetch('/api/rooms/set-characters', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ room_key: roomKey, characters: names }),
  }).then(async function (response) {
    var body = await response.json().catch(function () {
      return null;
    });
    if (response.status === 401) {
      promptRoomCharacterLogin(null);
      throw roomCharacterAuthError();
    }
    if (!response.ok || !body || body.success === false) {
      throw new Error(
        (body && (body.error || body.message)) ||
          'Room character update failed'
      );
    }
    var data = body.data || body;
    fixtures.room_characters_locked = !!data.characters_locked;
    if (data.scope === 'visitor') return applyVisitorRoomCharacters(nextRoomAIs);
    visitorRoomCharacters = {};
    visitorHiddenRoomCharacters = {};
    var saved = data && data.room_ais;
    if (saved && typeof saved === 'object') {
      sharedRoomCharacters = saved;
      return saved;
    }
    return nextRoomAIs || {};
  });
}

window.persistRoomCharacters = persistRoomCharacters;

function closeEmptyRoomSetupDialog() {
  var dialog = document.getElementById('empty-room-setup-dialog');
  if (!dialog || !dialog.open) {
    return;
  }

  try {
    dialog.close();
  } catch (err) {
    console.error('[EMPTY-ROOM] Failed to close setup dialog:', err);
  }
}

window.syncEmptyRoomSetupDialog = function (force) {
  var dialog = document.getElementById('empty-room-setup-dialog');
  var roomKey = getCurrentRoomDialogKey();
  var hasRoom =
    !!(window.fixtures && window.fixtures.room_name) && /^\/r(\/|$)/.test(window.location.pathname || '');
  var roomCharacterCount = getRoomCharacterCount(
    window.fixtures && window.fixtures.room_ais
  );

  if (!dialog || !hasRoom || roomCharacterCount > 0 || !roomKey) {
    closeEmptyRoomSetupDialog();
    if (!hasRoom || roomCharacterCount > 0) {
      window.__emptyRoomSetupShownFor = null;
    }
    return;
  }

  if (!force && window.__emptyRoomSetupShownFor === roomKey) {
    return;
  }

  window.__emptyRoomSetupShownFor = roomKey;

  try {
    if (dialog.open) {
      dialog.close();
    }
  } catch (err) {
    console.error('[EMPTY-ROOM] Failed to reset setup dialog:', err);
  }

  try {
    dialog.showModal();
  } catch (err) {
    console.error('[EMPTY-ROOM] Failed to open setup dialog:', err);
  }
};

$(document).on('click', '#empty-room-add-character-btn', function (event) {
  event.preventDefault();
  if (!requireRoomCharacterLogin(event)) {
    return false;
  }
  closeEmptyRoomSetupDialog();
  $('#edit-characters-btn').trigger('click');
  return false;
});

$(document).on('click', '#empty-room-create-character-btn', function (event) {
  event.preventDefault();
  if (!requireRoomCharacterLogin(event)) {
    return false;
  }
  closeEmptyRoomSetupDialog();
  $('#create-new-ai-btn').trigger('click');
  return false;
});

$(document).on('click', '.create-character-for-room-btn', function (event) {
  event.preventDefault();
  if (!requireRoomCharacterLogin(event)) {
    return false;
  }
  var dialog = document.getElementById('add-chat-chatroom-dialog');
  if (dialog && dialog.open) dialog.close();
  $('#create-new-ai-btn').trigger('click');
  return false;
});

$(document).on('click', '#empty-room-invite-friends-btn', function (event) {
  event.preventDefault();
  closeEmptyRoomSetupDialog();
  $('#share-button').trigger('click');
  return false;
});

function closeAnyDialog() {
  var dialogs = document.querySelectorAll('dialog');
  dialogs.forEach(function (dialog) {
    dialog.close();
  });
}

function canEditRoomCharacters() {
  if (isPrivateRoomChat()) return false;
  var fx = window.fixtures || {};
  return !fx.room_characters_locked || !!fx.room_can_moderate;
}

function syncEditRoomCharactersCopy() {
  var copy = document.querySelector(
    '#add-chat-chatroom-dialog .edit-room-characters-copy'
  );
  if (!copy) return;
  if (!copy.dataset.defaultCopy) copy.dataset.defaultCopy = copy.textContent;
  copy.textContent = canEditRoomCharacters()
    ? copy.dataset.defaultCopy
    : isPrivateRoomChat()
      ? 'Characters and messages in this private group stay in this tab only.'
      : 'Characters you add here only chat with you. The room owner or a mod can add them for everyone.';
  var lock = document.getElementById('room-characters-lock');
  if (!lock && window.fixtures?.room_can_moderate) {
    var label = document.createElement('label');
    lock = document.createElement('input');
    lock.type = 'checkbox';
    lock.id = 'room-characters-lock';
    label.append(lock, document.createTextNode(' Only moderators can change characters for everyone'));
    copy.after(label);
    lock.addEventListener('change', function () {
      lock.disabled = true;
      fetch('/api/rooms/set-characters', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ room_key: getCurrentRoomKey(), characters_locked: lock.checked }),
      }).then(async function (response) {
        var body = await response.json();
        if (!response.ok || body.success === false) throw new Error(body.error || 'Room lock update failed');
        fixtures.room_characters_locked = !!(body.data || body).characters_locked;
      }).catch(function (error) {
        reportRoomCharacterFailure('[ROOM-LOCK]', error);
      }).finally(function () {
        lock.disabled = false;
        syncEditRoomCharactersCopy();
      });
    });
  }
  if (lock) {
    lock.parentElement.hidden = !window.fixtures?.room_can_moderate;
    lock.checked = !!window.fixtures?.room_characters_locked;
  }
}

function openEditRoomCharactersDialog(event) {
  if (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
  syncEditRoomCharactersCopy();

  var dialog = document.getElementById('add-chat-chatroom-dialog');
  if (!dialog) {
    console.error('[EDIT-CHARACTERS] Dialog not found');
    return false;
  }

  try {
    document.querySelectorAll('dialog[open]').forEach(function (openDialog) {
      if (openDialog !== dialog) openDialog.close();
    });
    if (!dialog.open && typeof dialog.showModal === 'function') {
      dialog.showModal();
    } else if (!dialog.open) {
      dialog.setAttribute('open', '');
    }
  } catch (e) {
    console.error('[EDIT-CHARACTERS] Open failed:', e.message);
    return false;
  }

  try {
    renderAIsInRoom(fixtures.room_ais || {});
    APPController.renderAIs('#autoCompleteUsersRoomList', '');
  } catch (e) {
    console.error('[EDIT-CHARACTERS] Render failed:', e.message);
  }
  window.requestAnimationFrame(function () {
    var search = dialog.querySelector('#searchusersforroom');
    if (search) search.focus();
    setTimeout(function () {
      if (search && dialog.open) search.focus();
    }, 50);
  });

  return false;
}

$(document)
  .off('click.editRoomCharacters', '#edit-characters-btn')
  .on(
    'click.editRoomCharacters',
    '#edit-characters-btn',
    openEditRoomCharactersDialog
  );

function setupDialog() {
  var dialog = document.querySelector('dialog');
  if (!dialog) {
    console.warn('setupDialog: No dialog element found in DOM');
    return;
  }

  if (dialog && dialog.showModal) {
    var closeBtn = dialog.querySelector('.close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        dialog.close();
      });
    }
  } else {
    console.warn('setupDialog: Dialog element does not support showModal API');
  }
  $(document).on('click', '#add-room-btn', function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // Check authentication first
    if (!window.userData || !window.userData.id) {
      console.log('User not authenticated, showing login dialog');
      if (typeof window.showLoginDialog === 'function') {
        window.showLoginDialog(event);
      } else {
        window.location.href =
          '/signup?next=' +
          encodeURIComponent(window.location.pathname + window.location.search);
      }
      return false;
    }

    var dialog = document.getElementById('add-room-dialog');
    if (!dialog) {
      console.warn('add-room-dialog element not found');
      return false;
    }

    APPController.renderRooms();

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#roomname').focus();
    } catch (e) {
      console.error('[ADD-ROOM] Failed to open dialog:', e.message);
    }

    return false;
  });

  $(document).on(
    'click',
    '#find-room-btn, [data-open-room-finder]',
    function (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      var dialog = document.getElementById('find-room-dialog');
      if (!dialog) {
        console.warn('find-room-dialog element not found');
        return false;
      }

      APPController.renderRooms();

      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        try {
          dialog.close();
        } catch (e) {
          // Dialog might already be closed
        }

        try {
          dialog.showModal();
        } catch (e) {
          console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
        }
        $('#searchrooms').focus();
      } catch (e) {
        console.error('[FIND-ROOM] Failed to open dialog:', e.message);
      }

      return false;
    }
  );

  $(document).on(
    'click',
    '#autoCompleteUsersRoomList .ai-character-item',
    function (event) {
      if ($(event.target).closest('.room-character-chat-link').length) {
        event.stopPropagation();
        return;
      }
      event.preventDefault();
      event.stopPropagation();

      var aiName = $(this).find('.ai-name').text().trim();

      aiName = aiName.replace(/\s*USER\s*$/, '');

      if (!aiName) {
        return false;
      }

      var ai = dataAccessor.aisByName[aiName];
      if (!ai) {
        console.error('Could not find AI data for:', aiName);
        return false;
      }

      if (!fixtures.room_ais) {
        fixtures.room_ais = {};
      }

      if (!fixtures.room_ais[aiName]) {
        if (!requireRoomCharacterLogin(event)) {
          return false;
        }
        if (Object.keys(fixtures.room_ais).length >= 8) {
          if (window.showToast) window.showToast('Rooms support up to 8 characters');
          return false;
        }
        var selectedItem = this;
        if (selectedItem.dataset.saving === 'true') return false;
        selectedItem.dataset.saving = 'true';
        selectedItem.setAttribute('aria-busy', 'true');
        var nextRoomAIs = { ...fixtures.room_ais, [aiName]: ai };
        persistRoomCharacters(nextRoomAIs)
          .then(function (savedRoomAIs) {
            applyRoomCharacters(savedRoomAIs);
            var dialog = document.getElementById('add-chat-chatroom-dialog');
            if (dialog && dialog.open) dialog.close();
            triggerCharacterRoomResponse(ai);
          })
          .catch(function (error) {
            reportRoomCharacterFailure('[ADD-AI] Failed:', error);
          })
          .finally(function () {
            selectedItem.dataset.saving = 'false';
            selectedItem.removeAttribute('aria-busy');
          });
      } else {
        var dialog = document.getElementById('add-chat-chatroom-dialog');
        if (dialog) {
          dialog.close();
        }
      }

      return false;
    }
  );

  $(document).on(
    'keydown',
    '#autoCompleteUsersRoomList .ai-character-item',
    function (event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.click();
      }
    }
  );

  $(document).on('click', '.kick-character-from-room', function (event) {
    event.preventDefault();
    event.stopPropagation();
    var currentCharacterToKick = $(this).attr('data-name');

    if (!fixtures.room_ais[currentCharacterToKick]) {
      return false;
    }
    var kickButton = this;
    if (kickButton.dataset.saving === 'true') return false;
    kickButton.dataset.saving = 'true';
    kickButton.setAttribute('aria-busy', 'true');
    var nextRoomAIs = { ...fixtures.room_ais };
    delete nextRoomAIs[currentCharacterToKick];
    persistRoomCharacters(nextRoomAIs)
      .then(function (savedRoomAIs) {
        applyRoomCharacters(savedRoomAIs);
        var remaining = Object.values(savedRoomAIs);
        var game = window.questionsgame && window.questionsgame.game;
        if (
          game &&
          game.currentCharacter &&
          game.currentCharacter.name === currentCharacterToKick
        ) {
          var replacement =
            window.CollectionUtils && window.CollectionUtils.choose
              ? window.CollectionUtils.choose(remaining)
              : remaining[0] || null;
          game.currentCharacter = replacement;
          fixtures.currentAI = replacement;
        }
      })
      .catch(function (error) {
        reportRoomCharacterFailure('[REMOVE-AI] Failed:', error);
      })
      .finally(function () {
        kickButton.dataset.saving = 'false';
        kickButton.removeAttribute('aria-busy');
      });
    return false;
  });

  $('.close').click(function (event) {
    var dialog = $(this).parents('dialog').get(0);
    dialog.close();
    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  $('#add-contact-btn').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('add-contact-dialog');
    if (!dialog) return false;

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
    } catch (e) {
      console.error('[ADD-CONTACT] Failed to open dialog:', e.message);
    }
    return false;
  });

  $('#add-chat-btn').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('add-chat-dialog');
    if (!dialog) return false;

    APPController.renderAIs('#autoCompleteUsersList', '');

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#searchusers').focus();
    } catch (e) {
      console.error('[ADD-CHAT] Failed to open dialog:', e.message);
    }

    return false;
  });

  $(document).on('click', '[data-open-ai-finder]', function (event) {
    event.preventDefault();
    event.stopPropagation();
    $('#add-chat-btn').trigger('click');
    return false;
  });

  $('#create-ai-profile-pic').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('create-pic-dialog');
    if (!dialog) return false;

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#createaiimageprompt').focus();
    } catch (e) {
      console.error('[CREATE-AI-PIC] Failed to open dialog:', e.message);
    }

    populateElfStyles();

    return false;
  });

  $('.ai-art-generator-button').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('create-pic-dialog');
    if (!dialog) return false;

    APPController.writeNewAItoLocationFunc =
      APPController.writeNewAItoLocationChatFunc;

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#createaiimageprompt').focus();
    } catch (e) {
      console.error('[AI-ART-GEN] Failed to open dialog:', e.message);
    }

    populateElfStyles();

    return false;
  });

  $('#create-user-profile-pic').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('create-pic-dialog');
    if (!dialog) return false;

    APPController.writeNewAItoLocationFunc =
      APPController.writeNewAItoLocationCharacterDesignFunc;

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#createaiimageprompt').focus();
    } catch (e) {
      console.error('[CREATE-USER-PIC] Failed to open dialog:', e.message);
    }

    return false;
  });
  $('#create-new-ai-btn').click(function (event) {
    // Prevent default and stop propagation immediately to avoid popstate issues
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var authResult = isUserAuthenticated();
    if (!authResult.isAuthenticated && !isPrivateRoomChat()) {
      promptLoginDialog(event);
      return false;
    }

    var dialog = document.getElementById('create-new-ai-dialog');

    if (!dialog) {
      console.error('[CREATE-NEW-AI] Dialog not found: create-new-ai-dialog');
      return false;
    }

    var roomKey = getCurrentRoomKey();
    if (roomKey && fixtures.room_name) {
      dialog.dataset.roomKey = roomKey;
    } else {
      delete dialog.dataset.roomKey;
    }

    APPController.writeNewAItoLocationFunc =
      APPController.writeNewAItoLocationCharacterDesignFunc;

    try {
      dialog.close();
    } catch (e) {
      // Dialog might not be open, that's ok
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#ainame').focus();
    } catch (e) {
      console.error('[CREATE-NEW-AI] Failed to open dialog:', e.message);
    }

    return false;
  });
  $('#share-button').click(function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('share-ai-dialog');
    if (!dialog) return false;

    window.setTimeout(function () {
      $('#share-code-url-text').val(window.location.href);
      $('#copy-url').attr('href', window.location.href);
      var documentTitle = document.title;
      $('#twitter-share-link').attr(
        'href',
        'https://x.com/intent/tweet?text=' +
          documentTitle +
          ' - ' +
          encodeURIComponent(window.location.href)
      );
      $('#facebook-share-link').attr(
        'href',
        'https://www.facebook.com/sharer/sharer.php?u=' +
          encodeURIComponent(window.location.href) +
          '&t=' +
          encodeURIComponent(documentTitle)
      );
      $('#reddit-share-link').attr(
        'href',
        'https://www.reddit.com/submit?url=' +
          encodeURIComponent(window.location.href) +
          '&title=' +
          encodeURIComponent(documentTitle)
      );
      $('#linkedin-share-link').attr(
        'href',
        'https://www.linkedin.com/shareArticle?mini=true&url=' +
          encodeURIComponent(window.location.href) +
          '&title=' +
          encodeURIComponent(documentTitle)
      );
      $('#share-code-url-text').focus();
      $('#share-code-url-text').select();
      navigator.share({
        url: window.location.href,
      });
    }, 300);

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
    } catch (e) {
      console.error('[SHARE] Failed to open dialog:', e.message);
    }

    return false;
  });

  $(document).on('click', '.mdl-share-btn', function (event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    var dialog = document.getElementById('share-img-dialog');
    if (!dialog) return false;

    var currentTarget = $(event.currentTarget);

    var imgTagTargetID = currentTarget.data('share');
    let $imgTag = $('.' + imgTagTargetID);
    var currentPrompt = $imgTag.attr('alt');
    var imageUrl = $imgTag.attr('src');
    imageUrl = imageUrl.replace('storage.googleapis.com/', '');
    window.setTimeout(function () {
      $('#share-code-url-text-img').val(imageUrl);
      $('#copy-url-img').attr('href', imageUrl);
      var documentTitle = currentPrompt;
      let shareText =
        'Netwrck.com Art Generator! ' + documentTitle + '\n' + imageUrl;
      let shareTextNoLink = 'Netwrck.com Art Generator! ' + documentTitle;
      $('#twitter-share-link-img').attr(
        'href',
        'https://x.com/intent/tweet?text=' + shareTextNoLink
      );
      $('#facebook-share-link-img').attr(
        'href',
        'https://www.facebook.com/sharer/sharer.php?u=' +
          encodeURIComponent(imageUrl) +
          '&t=' +
          encodeURIComponent(shareText)
      );
      $('#reddit-share-link-img').attr(
        'href',
        'https://www.reddit.com/submit?url=' +
          encodeURIComponent(imageUrl) +
          '&title=' +
          encodeURIComponent(shareTextNoLink)
      );
      $('#linkedin-share-link-img').attr(
        'href',
        'https://www.linkedin.com/shareArticle?mini=true&url=' +
          encodeURIComponent(imageUrl) +
          '&title=' +
          encodeURIComponent(shareTextNoLink)
      );
      $('#share-code-url-text-img').focus();
      $('#share-code-url-text-img').select();
      navigator.share({
        url: imageUrl,
      });
    }, 300);

    try {
      dialog.close();
    } catch (e) {
      // Dialog might already be closed
    }

    try {
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
    } catch (e) {
      console.error('[SHARE-IMG] Failed to open dialog:', e.message);
    }

    return false;
  });

  $('#save-button').click(function (event) {
    if (isPrivateRoomChat()) return false;
    var authResult = isUserAuthenticated();
    if (!authResult.isAuthenticated) {
      if (window.showLoginDialog) {
        window.showLoginDialog(event);
      } else {
        alert('Please login to save your game');
      }
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    if (!window.questionsgame || !window.window.questionsgame.game) {
      console.warn('[GAME] Game state not initialized for save');
      alert('Unable to save - game state not initialized');
      return;
    }
    reInitGameState(window.questionsgame.game);
    var saveGame = rebuildGameState(window.questionsgame.game);

    var titleOfGame =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    fetch('/api/save-game', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        title: titleOfGame,
        input_outputs: saveGame.input_outputs,
        backstory: saveGame.backstory,
        display_title:
          document.getElementById('chat-one-to-one-name')?.textContent ||
          document.title,
        character_path:
          window.location.pathname +
          (function () {
            var params = new URLSearchParams(window.location.search);
            params.delete('saved_chat');
            params.delete('anon_chat');
            return params.toString() ? '?' + params.toString() : '';
          })(),
        kind: 'checkpoint',
      }),
    })
      .then(function (response) {
        if (response.status === 401) {
          throw new Error('AUTHENTICATION_REQUIRED');
        }
        if (response.status !== 200) {
          console.log(
            'Looks like there was a problem. Status Code: ' + response.status
          );
          throw new Error('Something went wrong saving!');
        }
        return response.json();
      })
      .then(function (data) {
        if (
          data &&
          data.success === false &&
          data.error === 'Authentication required'
        ) {
          throw new Error('AUTHENTICATION_REQUIRED');
        }

        var dialog = document.getElementById('save-ai-dialog');
        window.setTimeout(function () {
          let locationUrl = new URL(window.location.href);
          let inputParams = new URLSearchParams(locationUrl.search);
          inputParams.set('saved_chat', titleOfGame);
          inputParams.delete('anon_chat');
          locationUrl.search = inputParams.toString();
          locationUrl = locationUrl.href;
          $('#save-code-url-text').val(locationUrl);
          $('#save-copy-url').attr('href', locationUrl);
          if (window.ChatWorkspace) {
            window.ChatWorkspace.saveChat({
              id: titleOfGame,
              url: locationUrl,
              title:
                document.getElementById('chat-one-to-one-name')?.textContent ||
                document.title,
              saved: true,
              owned: true,
              kind: 'checkpoint',
            });
          }
          var documentTitle = document.title;
          $('#twitter-save-link').attr(
            'href',
            'https://x.com/intent/tweet?text=' +
              documentTitle +
              ' - ' +
              encodeURIComponent(locationUrl)
          );
          $('#facebook-save-link').attr(
            'href',
            'https://www.facebook.com/sharer/sharer.php?u=' +
              encodeURIComponent(locationUrl) +
              '&t=' +
              encodeURIComponent(documentTitle)
          );
          $('#reddit-save-link').attr(
            'href',
            'https://www.reddit.com/submit?url=' +
              encodeURIComponent(locationUrl) +
              '&title=' +
              encodeURIComponent(documentTitle)
          );
          $('#linkedin-save-link').attr(
            'href',
            'https://www.linkedin.com/shareArticle?mini=true&url=' +
              encodeURIComponent(locationUrl) +
              '&title=' +
              encodeURIComponent(documentTitle)
          );
          $('#save-code-url-text').focus();
          $('#save-code-url-text').select();
          navigator.share({
            url: locationUrl,
          });
        }, 300);

        dialog.close();
        try {
          dialog.close();
        } catch (e) {
          // Dialog might already be closed
        }

        try {
          dialog.showModal();
        } catch (e) {
          console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
        }
      })
      .catch(function (err) {
        if (err.message === 'AUTHENTICATION_REQUIRED') {
          if (window.showLoginDialog) {
            window.showLoginDialog(event);
          } else {
            alert('Please login to save your game');
          }
        } else {
          alert('Error saving game (please try again): ' + err);
        }
      });

    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  $('#copy-url').click(function () {
    var $temp = $('<input>');
    $('body').append($temp);
    var href = $('#copy-url').attr('href');
    $temp.val(href).select();
    document.execCommand('copy');
    safeClipboardWrite(href);
    $('#share-code-url-text').focus();
    $('#share-code-url-text').select();
    $temp.remove();
  });

  $('#save-copy-url').click(function () {
    var $temp = $('<input>');
    $('body').append($temp);
    var href = $('#save-copy-url').attr('href');
    $temp.val(href).select();
    document.execCommand('copy');
    safeClipboardWrite(href);
    $('#save-code-url-text').focus();
    $('#save-code-url-text').select();
    $temp.remove();
  });

  $('#copy-url-img').click(function () {
    var $temp = $('<input>');
    $('body').append($temp);
    var href = $('#copy-url-img').attr('href');
    $temp.val(href).select();
    document.execCommand('copy');
    safeClipboardWrite(href);
    $('#share-code-url-text-img').focus();
    $('#share-code-url-text-img').select();
    $temp.remove();
  });

  $('#save-copy-url-img').click(function () {
    var $temp = $('<input>');
    $('body').append($temp);
    var href = $('#save-copy-url-img').attr('href');
    $temp.val(href).select();
    document.execCommand('copy');
    safeClipboardWrite(href);
    $('#save-code-url-text-img').focus();
    $('#save-code-url-text-img').select();
    $temp.remove();
  });
  window.searchCat = function (cat) {
    if ($('#searchusers').is(':hidden')) {
      $('#searchusersforroom').val(cat).trigger('input');
      $('#searchusersforroom').focus();

      onTypingAIFunction(null, '#autoCompleteUsersRoomList', cat);
    } else {
      $('#searchusers').val(cat).trigger('input');
      $('#searchusers').focus();

      onTypingAIFunction(null, '#autoCompleteUsersList', cat);
    }
    return false;
  };
  window.addGenerationCat = function (cat) {
    let currentVal = $('#createaiimageprompt').val();
    var isFinishingSpace = currentVal[currentVal.length - 1] === ' ';
    if (!isFinishingSpace && currentVal[currentVal.length - 1]) {
      cat = ' ' + cat;
    }
    $('#createaiimageprompt').focus();

    var input = $('#createaiimageprompt')[0];
    input.setRangeText(cat, input.value.length, input.value.length, 'end');
    input.dispatchEvent(new Event('input', { bubbles: true }));
    $('#createaiimageprompt').focus();

    return false;
  };
  // Fallback setupStripe function in case shared.js doesn't load
  if (!window.setupStripe) {
    window.setupStripe = async function setupStripe() {
      // Wait for Stripe library
      let attempts = 0;
      while (typeof Stripe === 'undefined' && attempts < 20) {
        await new Promise((resolve) => setTimeout(resolve, 250));
        attempts++;
      }

      if (typeof Stripe === 'undefined') {
        console.warn('[GAME-STRIPE] Stripe is not defined after waiting');
        return;
      }

      if (!fixtures || !fixtures.stripe_publishable_key) {
        console.warn(
          '[GAME-STRIPE] Stripe publishable key not found in fixtures'
        );
        return;
      }

      const stripe = Stripe(fixtures.stripe_publishable_key);
      window.stripeCheckout = false;

      var baseUrl =
        window.fixtures && window.fixtures.api_base_url
          ? window.fixtures.api_base_url
          : '';

      if (
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1' ||
        window.location.hostname === 'netwrck.com'
      ) {
        baseUrl = '';
      }

      try {
        const currentUser = window.userData || userData || {};
        if (!currentUser || !currentUser.id) {
          console.warn('[GAME-STRIPE] User not found');
          return;
        }

        const response = await fetch(
          baseUrl + '/create-checkout-session-embedded',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              uid: currentUser.id,
              // Secret optional; include api_key fallback when present
              secret: currentUser.secret || currentUser.api_key || '',
              email: currentUser.email,
              referral: CookieUtils.getCookie('evangeler_ref'),
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const session = await response.json();

        const clientSecret = session.client_secret || session.clientSecret;
        if (clientSecret) {
          if (
            window.EmbeddedCheckout &&
            typeof window.EmbeddedCheckout.destroyAll === 'function'
          ) {
            try {
              window.EmbeddedCheckout.destroyAll();
            } catch {
              /* Error ignored */
            }
          }
          window.stripeCheckout = await stripe.initEmbeddedCheckout({
            clientSecret: clientSecret,
            onComplete: function handleComplete() {
              try {
                if (typeof window.trackSubscriptionEvent === 'function') {
                  window.trackSubscriptionEvent(
                    'subscription_checkout_completed',
                    {
                      plan: 'monthly',
                      trigger: 'chat',
                      authenticated: true,
                    }
                  );
                }
              } catch {
                /* Error ignored */
              }
              try {
                if (window.thFunnel) window.thFunnel.purchase('monthly', (window.evangelPurchaseAmounts || {}).monthly || 9, { flow: 'subscription' });
              } catch { /* ignored */ }
              try {
                if (typeof window.evangelTrackConversion === 'function') {
                  var u = currentUser || window.userData || {};
                  var firstOnly = window.evangelFirstPurchaseOnly === true;
                  var purchaseAmounts = window.evangelPurchaseAmounts || {};
                  var purchaseAmount = purchaseAmounts.monthly || 9;
                  var purchaseID = firstOnly
                    ? 'first_purchase_' + window.location.hostname + '_' + (u.id || u.email || '')
                    : 'sub_' + (u.id || '') + '_' + Date.now();
                  window.evangelTrackConversion(purchaseAmount, purchaseID, u.email);
                }
              } catch { /* ignored */ }
              if (window.stripeCheckout) { window.stripeCheckout.destroy(); window.stripeCheckout = null; }
              setTimeout(function() { window.location.reload(); }, 50);
            },
          });

          window.stripeCheckout.mount('#stripe-checkout-dialog-content');
          try {
            const dlg = document.getElementById('subscribe-dialog');
            if (dlg) dlg.checkoutInstance = window.stripeCheckout;
          } catch {
            /* Error ignored */
          }
        } else {
          console.error(
            '[GAME-STRIPE] No client_secret or clientSecret in session response'
          );
        }
      } catch (error) {
        console.error('[GAME-STRIPE] Error setting up Stripe:', error);
      }
    };
  }

  window.showSubscribeDialog = async function showSubscribeDialog(event) {
    // Immediately prevent event propagation to avoid popstate issues
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }

    if (window.stripeCheckout) {
      try {
        window.stripeCheckout.destroy();
        window.stripeCheckout = null;
      } catch (e) {
        console.warn('[SUBSCRIBE] Error destroying existing checkout:', e);
      }
    }

    var authResult = isUserAuthenticated();
    var isAuthenticated = authResult.isAuthenticated;

    var dialog = document.getElementById('subscribe-dialog');

    if (isAuthenticated) {
      if (!dialog) {
        console.warn('[SUBSCRIBE] subscribe-dialog element not found on page');
        return false;
      }

      var signupContent = dialog.querySelector('.header-login-signup');
      if (signupContent) {
        signupContent.style.display = 'none';
      }

      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
        if (typeof window.trackSubscriptionEvent === 'function') {
          window.trackSubscriptionEvent('subscription_dialog_opened', {
            plan: 'monthly',
            trigger: 'chat',
            authenticated: true,
          });
        }
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }

      // Wait for setupStripe to become available (up to 2 seconds)
      let attempts = 0;
      while (
        (!window.setupStripe || typeof window.setupStripe !== 'function') &&
        attempts < 20
      ) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (window.setupStripe && typeof window.setupStripe === 'function') {
        if (typeof window.trackSubscriptionEvent === 'function') {
          window.trackSubscriptionEvent('begin_checkout', {
            plan: 'monthly',
            trigger: 'chat',
            authenticated: true,
          });
        }
        try {
          await window.setupStripe();
          if (typeof window.trackSubscriptionEvent === 'function') {
            window.trackSubscriptionEvent('subscription_checkout_ready', {
              plan: 'monthly',
              trigger: 'chat',
              authenticated: true,
            });
          }
        } catch (error) {
          console.error('[SUBSCRIBE] Error setting up Stripe:', error);

          // Retry once after a short delay
          try {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            await window.setupStripe();
            if (typeof window.trackSubscriptionEvent === 'function') {
              window.trackSubscriptionEvent('subscription_checkout_ready', {
                plan: 'monthly',
                trigger: 'chat',
                authenticated: true,
              });
            }
          } catch (retryError) {
            console.error('[SUBSCRIBE] Retry failed:', retryError);
            if (typeof window.trackSubscriptionEvent === 'function') {
              window.trackSubscriptionEvent('subscription_checkout_failed', {
                plan: 'monthly',
                trigger: 'chat',
                stage: 'checkout_loading',
                authenticated: true,
              });
            }
            var stripeContainer = document.getElementById(
              'stripe-checkout-dialog-content'
            );
            if (stripeContainer) {
              stripeContainer.innerHTML =
                '<div style="padding: 20px; text-align: center;"><p>Error loading payment form. Please try again.</p></div>';
            }
          }
        }
      } else {
        console.warn(
          '[SUBSCRIBE] setupStripe function not available after waiting'
        );

        stripeContainer = document.getElementById(
          'stripe-checkout-dialog-content'
        );
        if (stripeContainer) {
          stripeContainer.innerHTML =
            '<div style="padding: 20px; text-align: center;"><p>Payment system not available. Please try again later.</p></div>';
        }
      }
    } else {
      if (!dialog) {
        console.warn('[SUBSCRIBE] subscribe-dialog element not found on page');
        return false;
      }

      if (dialog.open) {
        dialog.close();
      }

      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
        if (typeof window.trackSubscriptionEvent === 'function') {
          window.trackSubscriptionEvent('subscription_dialog_opened', {
            plan: 'monthly',
            trigger: 'chat',
            authenticated: false,
          });
          window.trackSubscriptionEvent('subscription_auth_required', {
            plan: 'monthly',
            trigger: 'chat',
            authenticated: false,
          });
        }
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
    }

    if (typeof event !== 'undefined') {
      event.preventDefault();
      event.stopPropagation();
    }
    return false;
  };

  $(document).on('click', '.subscribe-btn', showSubscribeDialog);
  // Signup links should open the signup modal, not the subscribe dialog
  $(document).on('click', '.signup-link', function (event) {
    if (typeof window.showSignupDialog === 'function') {
      return window.showSignupDialog(event);
    }
    // Fallback: navigate to /signup if modal helper missing
    window.location.href = '/signup#modal';
    return false;
  });
  $('#subscribe-dialog .close').click(function (event) {
    var dialog = document.getElementById('subscribe-dialog');

    if (window.stripeCheckout) {
      try {
        window.stripeCheckout.destroy();
        window.stripeCheckout = null;
      } catch (e) {
        console.warn('[SUBSCRIBE] Error destroying checkout:', e);
      }
    }

    dialog.close();
    event.preventDefault();
    event.stopPropagation();

    return false;
  });

  $('#add-room-form').submit(function (event) {
    if (window.authSession && window.authSession.isLoggedIn()) {
      var currentUser = window.authSession.getCurrentUser();
      if (currentUser) {
        var email = currentUser.email;
        $('.email').html(email);

        const api_key = currentUser.secret;
        var roomName = $("#add-room-form input[name='roomname']").val();
        var roomVisibility = $(
          "#add-room-form input[name='public']:checked"
        ).val();
        var roomUrlKey = encodeURIComponent(roomName.replace(/ /g, '-'));
        $.ajax({
          url: '/new-chatroom',
          type: 'POST',
          data: {
            secret: api_key,
            roomname: roomName,
            url_key: roomUrlKey,
            public: roomVisibility,
          },
          success: function (data) {
            console.log(data);
            var dialog = document.getElementById('add-room-dialog');
            dialog.close();

            if (window.fixtures) {
              window.fixtures.room_name = roomName;
              window.fixtures.room_ais = {};
              window.fixtures.currentAI = null;
            }

            var urlencodedroomname = encodeURIComponent(
              roomName.replace(/ /g, '-')
            );
            if (window.APP && window.APP.goto) {
              window.APP.goto('/r/' + urlencodedroomname);
            } else {
              window.location.href = '/r/' + urlencodedroomname;
            }
          },
          error: function (data) {
            console.log(data);
          },
        });
      } else {
        if (window.showLoginDialog) {
          window.showSignupDialog();
        } else {
          location.href = '/login';
        }
      }
    } else {
      location.href = '/login';
    }

    var dialog = document.getElementById('add-room-dialog');
    dialog.close();
    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  let onTypingFunction = function (event) {
    var query = $(this).val();
    APPController.renderRooms(null, query);
  };

  $('#searchrooms').on('input', onTypingFunction);

  $('#main-chatbox').keydown(function (event) {
    var key = event.keyCode || event.charCode;

    if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) {
      return true;
    }

    if (key === 13) {
      event.preventDefault();
      if (!(window.userData && window.userData.is_subscribed)) {
        if (subscribeMessageCounter >= 5) {
          showSubscribeDialog();
          trackEvent('user', 'paywall', 'chat');
          return false;
        }
      }
      var $form = $(this).closest('form');
      $form.submit();
    }
  });

  var onTypingAIFunction = function (event, target, query) {
    if (!target) {
      console.error('No target provided for AI search');
      return;
    }

    try {
      $('#search-ai-loading-progress').show();

      APPController.renderAIs(target, query);
    } catch (err) {
      console.error('Error in AI search:', err);
      $('#search-ai-loading-progress').hide();

      if (target) {
        var $target = $(target);
        $target.html(`<div class="mdl-cell mdl-cell--12-col">
                    <p>Search failed. Please try again.</p>
                </div>`);
      }
    }
  };

  // Make function globally accessible to avoid scope issues in debounced callbacks
  window.onTypingAIFunction = onTypingAIFunction;

  $('.search-ais-button').click(function (event) {
    var query = $('#searchusers').val();
    onTypingAIFunction(event, '#autoCompleteUsersList', query);
  });
  $('.search-ais-room-button').click(function (event) {
    var query = $('#searchusersforroom').val();

    onTypingAIFunction(event, '#autoCompleteUsersRoomList', query);
  });

  // Event handlers moved to end of file to avoid duplicates

  var onTypingCreateAIFunction = function (event) {
    var query = $('#createaiimageprompt').val();
    APPController.renderAIPics(null, query);
  };

  var onTypingCreateAITransparentFunction = function (event) {
    var query = $('#createaiimageprompttransparent').val();
    APPController.renderAITransparentPics(null, query);
  };

  var onTypingCreateAIUpscaleFunction = function (event) {
    var query = $('#createaiimagepromptupscaler').val();
    APPController.renderAIUpscalePics(null, query);
  };

  var onTypingCreateAIStyleTransferFunction = function (event) {
    var query = $('#createaiimagepromptStyleTransfer').val();
    APPController.renderAIStyleTransferPics(null, query);
  };

  $('.create-ai-image-button').click(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 3) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }

    onTypingCreateAIFunction(event);
  });

  $('.create-ai-image-button-transparent').click(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 1) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }

    onTypingCreateAITransparentFunction(event);
  });
  $('.create-ai-image-button-upscaler').click(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 1) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }

    onTypingCreateAIUpscaleFunction(event);
  });

  $('.create-ai-image-button-style-transfer').click(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 1) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }

    onTypingCreateAIStyleTransferFunction(event);
  });

  $('.search-ai-bots-button').click(function (event) {
    var query = $('#searchboxaisprompt').val();

    $('#search-ai-loading-progress').show();
    dataAccessor
      .searchAis(query)
      .then((ais) => renderAIViewSearchPage(ais || [], '.ai-character-grid'))
      .catch((err) => {
        console.error('AI search failed:', err);
        renderAIViewSearchPage([], '.ai-character-grid');
      })
      .finally(() => $('#search-ai-loading-progress').hide());
  });

  $('#searchboxaisprompt').keydown(function (event) {
    if (event.keyCode === 13) {
      var query = $('#searchboxaisprompt').val();

      $('#search-ai-loading-progress').show();
      dataAccessor
        .searchAis(query)
        .then((ais) => renderAIViewSearchPage(ais || [], '.ai-character-grid'))
        .catch((err) => {
          console.error('AI search failed:', err);
          renderAIViewSearchPage([], '.ai-character-grid');
        })
        .finally(() => $('#search-ai-loading-progress').hide());
    }
  });

  $('#createaiimageprompt').keydown(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 5) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }
    if (event.keyCode === 13) {
      onTypingCreateAIFunction(event);
    }
  });
  $('#createaiimageprompttransparent').keydown(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 5) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }
    if (event.keyCode === 13) {
      onTypingCreateAITransparentFunction(event);
    }
  });

  $('#createaiimagepromptupscaler').keydown(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 5) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }
    if (event.keyCode === 13) {
      onTypingCreateAIUpscaleFunction(event);
    }
  });

  $('#createaiimagepromptStyleTransfer').keydown(function (event) {
    if (
      window.userData &&
      !window.userData?.is_subscribed &&
      window.userFullyLoaded
    ) {
      if (subscribeImageGenCounter >= 5) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }
    if (event.keyCode === 13) {
      onTypingCreateAIStyleTransferFunction(event);
    }
  });

  $(document).on('click', '.mdl-download-btn', function (evt) {
    var downloadClass = $(evt.currentTarget).data('download');
    var $imgToDownload = $(`.${downloadClass}`);
    var imgSrcAttr = $imgToDownload.attr('src');

    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');

    $imgToDownload[0].crossOrigin = 'anonymous';

    $imgToDownload.on('load', function () {
      canvas.width = $imgToDownload[0].naturalWidth;
      canvas.height = $imgToDownload[0].naturalHeight;

      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage($imgToDownload[0], 0, 0);

      var link = document.createElement('a');
      var fileName =
        window.ImageUtils && window.ImageUtils.getImageName
          ? window.ImageUtils.getImageName(imgSrcAttr.replace('.webp', ''))
          : imgSrcAttr.replace('.webp', '').split('/').pop().split('?')[0];
      window.setTimeout(function () {
        link.download = fileName + '.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      }, 100);
    });

    if ($imgToDownload[0].complete || $imgToDownload[0].naturalWidth !== 0) {
      $imgToDownload.trigger('load');
    }
  });

  $(document).on('click', '.mdl-style-transf-btn', function (evt) {
    if (
      window.userFullyLoaded &&
      window.userData &&
      !window.userData.is_subscribed
    ) {
      if (subscribeImageGenCounter >= 3) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }

    const $mainInput = $('#createaiimageprompt');
    $mainInput.focus();

    $('html, body').animate(
      {
        scrollTop: $mainInput.offset().top,
      },
      500
    );

    var downloadClass = $(evt.currentTarget).data('download');
    var $imgToDownload = $(`.${downloadClass}`);
    var imgSrcAttr = $imgToDownload.attr('src');
    window.setDropzoneImgToUrl(imgSrcAttr);
  });
  $(document).on('click', '.mdl-upres-btn', function (evt) {
    if (
      window.userFullyLoaded &&
      window.userData &&
      !window.userData.is_subscribed
    ) {
      if (subscribeImageGenCounter >= 0) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');

        return;
      }
    }
    var dialog = document.getElementById('upres-dialog');

    dialog.close();
    dialog.showModal();

    var downloadClass = $(evt.currentTarget).data('download');
    var $imgToDownload = $(`.${downloadClass}`);
    var imgSrcAttr = $imgToDownload.attr('src');
    var imgAltAttr = $imgToDownload.attr('alt');
    var baseUrl = '';

    $('#high-res-result-image').html(`
            <div class="mdl-spinner mdl-spinner--single-color mdl-js-spinner is-active"></div>
        `);
    fetch(baseUrl + '/run', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model_name:
          'philz1337x/clarity-upscaler:b8a46b09384dc1ac996596bc14058e2b7604971128ee7de709a40d4bbf982d2c',
        seed: 1337,
        image: imgSrcAttr,
        prompt: imgAltAttr,
        dynamic: 6,
        sd_model: 'juggernaut_reborn.safetensors [338b85bc4f]',
        scheduler: 'DPM++ 3M SDE Karras',
        creativity: 0.35,
        lora_links: '',
        downscaling: false,
        resemblance: 0.6,
        scale_factor: 2,
        tiling_width: 112,
        tiling_height: 144,
        custom_sd_model: '',
        negative_prompt:
          '(worst quality, low quality, normal quality:2) JuggernautNegative-neg',
        num_inference_steps: 25,
        downscaling_resolution: 768,
        dmal:
          new Date().toLocaleString('en-US', {
            hour12: false,
            hour: '2-digit',
            minute: '2-digit',
            year: '2-digit',
            timeZone: 'UTC',
          }) +
          'asdf' +
          ((window.userData && window.userData.is_subscribed) || false) +
          'asdf' +
          ((window.userData && window.userData.secret) || ''),
      }),
    })
      .then(async function (result) {
        if (!result.ok) {
          var errorData = await result.json();
          if (result.status === 401 && errorData.redirect) {
            window.location.href =
              errorData.redirect +
              '?next=' +
              encodeURIComponent(
                window.location.pathname + window.location.search
              );
            return;
          } else if (result.status === 402) {
            alert(
              errorData.message ||
                'This feature requires an active subscription'
            );
            return;
          }
          throw new Error(errorData.error || 'Request failed');
        }
        var decoded = await result.json();
        var filefullURLEncoded = encodeURIComponent(decoded.output);
        var i = 99999;
        $('#high-res-result-image').html(`


<div class="ai-character-item-aiartnil ai-char-item mdl-cell mdl-cell--stretch mdl-cell--2-col mdl-list__item mdl-list__item--three-line" >
  <a id="mdl-editor-btn-${i}" href="https://editor.ebank.nz/index.html?imageUrl=${filefullURLEncoded}"
                       class="mdl-editor-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank">
                          <i class="material-icons">brush</i>
                    </a>
                    <label class="mdl-tooltip" data-mdl-for="mdl-editor-btn-${i}">
                        Open in Editor
                    </label>
                    <a id="mdl-download-btn-${i}"
                       class="mdl-editor-btn mdl-download-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank" data-download="jspicture${i}">
                          <i class="material-icons">download</i>
                    </a>
                    <label class="mdl-tooltip" data-mdl-for="mdl-download-btn-${i}">
                        Download
                    </label>
                    <a class="mdl-list__item-primary-content" href="aiartgenerator?c=${encodeURIComponent(imgAltAttr)}">

        <span class="mdl-list__item-avatar-aiart mdl-chip__contact mdl-list__item-avatar">
            <img id="aiartgenerator-image${i}" loading="lazy" class="jspicture${i} ai-avatar-icon--aiart ai-generated-char-chooser ai-avatar-icon" src="${decoded.output}" alt="${imgAltAttr}">
            <label class="mdl-tooltip mdl-tooltip-large mdl-tooltip--top" data-mdl-for="aiartgenerator-image${i}">
                ${imgAltAttr}
            </label>
        </span>
    </a>

</div>
</div>

            `);
      })
      .catch(function (err) {
        $('#high-res-result-image').html(`Error please try again`);
        console.log(err);
      });
  });

  $(document).on('click', '.mdl-video-btn', function (evt) {
    if (
      window.userFullyLoaded &&
      window.userData &&
      !window.userData.is_subscribed
    ) {
      if (subscribeImageGenCounter >= 0) {
        showSubscribeDialog();
        trackEvent('user', 'paywall', 'art');
        return;
      }
    }
    var downloadClass = $(evt.currentTarget).data('download');
    var $imgToDownload = $(`.${downloadClass}`);
    var imgSrcAttr = $imgToDownload.attr('src');
    var imgAltAttr = $imgToDownload.attr('alt');
    if (window.LTX23VideoDialog && typeof window.LTX23VideoDialog.open === 'function') {
      evt.preventDefault();
      window.LTX23VideoDialog.open({
        imageUrl: imgSrcAttr,
        prompt:
          imgAltAttr ||
          'The character moves naturally with subtle animation and graceful gestures',
      });
    }
  });

  setupVoiceDialog();
  setupVoiceCreation();
}

function setupSearch(rooms) {}

$(document).ready(function () {
  setupDialog();

  // Initialize infinite scroll will be done after function definition

  // Initialize AI Controls button
  function initAIControls() {
    const aiControlBtn = document.getElementById('ai-control-btn');
    if (aiControlBtn) {
      aiControlBtn.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();

        const aiSettingsDialog = document.getElementById('ai-settings-dialog');
        if (aiSettingsDialog) {
          // Load AI settings before showing dialog
          loadAISettings();
          aiSettingsDialog.showModal();
        } else {
          console.log('AI Settings dialog not found');
        }
      });
    }
  }

  // Load AI settings from localStorage
  function loadAISettings() {
    try {
      const audioEnabled = localStorage.getItem('audioEnabled') !== 'false';
      const voiceVolume = parseInt(
        localStorage.getItem('preferredVolume') || '50'
      );
      const sfxVolume = parseInt(
        localStorage.getItem('preferredSfxVolume') || '50'
      );
      let rawMakeArt = localStorage.getItem('makeArtEnabled');
      if (rawMakeArt === null) {
        localStorage.setItem('makeArtEnabled', 'false');
        rawMakeArt = 'false';
      }
      const makeArtEnabled = (rawMakeArt || '').toLowerCase() === 'true';
      let makeArtPromptAddition = localStorage.getItem('makeArtPromptAddition');
      if (makeArtPromptAddition === null || !makeArtPromptAddition.trim()) {
        makeArtPromptAddition = 'anime';
        localStorage.setItem('makeArtPromptAddition', makeArtPromptAddition);
      }
      const makeArtModel = localStorage.getItem('makeArtModel') || 'default';
      const makeArtSize = localStorage.getItem('makeArtSize') || '1024x1024';

      const savedVoice = localStorage.getItem('preferredVoice') || 'af_nicole';
      const savedSpeechLanguage = localStorage.getItem('preferredSpeechLanguage') || 'en';

      const audioEnabledSwitch = document.getElementById(
        'audio-enabled-switch'
      );
      const voiceVolumeSlider = document.getElementById('voice-volume-slider');
      const sfxVolumeSlider = document.getElementById('sfx-volume-slider');
      const makeArtCheckbox = document.getElementById('make-art-checkbox');
      const makeArtPromptInput = document.getElementById('make-art-prompt-input');
      const makeArtModelSelect = document.getElementById('make-art-model-select');
      const makeArtSizeSelect = document.getElementById('make-art-size-select');
      const voiceSelectorSettings = document.getElementById('voice-selector-settings');
      const speechLanguageSettings = document.getElementById('speech-language-settings');

      if (audioEnabledSwitch) audioEnabledSwitch.checked = audioEnabled;
      if (voiceVolumeSlider) voiceVolumeSlider.value = voiceVolume;
      if (sfxVolumeSlider) sfxVolumeSlider.value = sfxVolume;
      if (
        voiceSelectorSettings &&
        Array.from(voiceSelectorSettings.options).some(
          (option) => option.value === savedVoice
        )
      ) {
        voiceSelectorSettings.value = savedVoice;
      }
      if (typeof window.setVoice === 'function') window.setVoice(savedVoice);
      if (speechLanguageSettings) speechLanguageSettings.value = savedSpeechLanguage;

      const previewVoiceBtn = document.getElementById('preview-voice-btn');
      if (previewVoiceBtn && !previewVoiceBtn.dataset.bound) {
        previewVoiceBtn.dataset.bound = '1';
        previewVoiceBtn.addEventListener('click', function () {
          const sel = document.getElementById('voice-selector-settings');
          const v = sel ? sel.value : savedVoice;
          if (typeof window.playSound === 'function') {
            window.playSound('Hi, this is a voice preview.', v);
          }
        });
      }
      if (makeArtCheckbox) makeArtCheckbox.checked = makeArtEnabled;
      if (makeArtPromptInput) {
        makeArtPromptInput.value = makeArtPromptAddition;
        makeArtPromptInput.disabled = !makeArtEnabled;
        if (!makeArtPromptInput.dataset.makeArtBound) {
          makeArtPromptInput.dataset.makeArtBound = '1';
          makeArtCheckbox?.addEventListener('change', () => {
            const enabled = makeArtCheckbox.checked;
            makeArtPromptInput.disabled = !enabled;
            if (enabled && !makeArtPromptInput.value.trim()) {
              makeArtPromptInput.value = 'anime';
            }
          });
        }
      }
      if (makeArtModelSelect) makeArtModelSelect.value = makeArtModel;
      if (makeArtSizeSelect) makeArtSizeSelect.value = makeArtSize;
    } catch (err) {
      console.error('Error loading AI settings:', err);
    }
  }

  // Save AI settings handler
  const saveAISettingsBtn = document.getElementById('save-ai-settings-btn');
  if (saveAISettingsBtn) {
    saveAISettingsBtn.addEventListener('click', function () {
      try {
        const audioEnabled = document.getElementById(
          'audio-enabled-switch'
        ).checked;
        const voiceVolume = document.getElementById(
          'voice-volume-slider'
        ).value;
        const sfxVolume = document.getElementById('sfx-volume-slider').value;
        const makeArtEnabled =
          document.getElementById('make-art-checkbox').checked;
        const makeArtPromptInput = document.getElementById(
          'make-art-prompt-input'
        );
        const makeArtModelSelect = document.getElementById('make-art-model-select');
        const makeArtSizeSelect = document.getElementById('make-art-size-select');

        const voiceSel = document.getElementById('voice-selector-settings');
        if (voiceSel && voiceSel.value) {
          localStorage.setItem('preferredVoice', voiceSel.value);
          if (typeof window.setVoice === 'function') window.setVoice(voiceSel.value);
        }
        const speechLangSel = document.getElementById('speech-language-settings');
        if (speechLangSel && speechLangSel.value) {
          localStorage.setItem('preferredSpeechLanguage', speechLangSel.value);
        }

        localStorage.setItem('audioEnabled', audioEnabled);
        localStorage.setItem('preferredVolume', voiceVolume);
        localStorage.setItem('preferredSfxVolume', sfxVolume);
        localStorage.setItem('makeArtEnabled', makeArtEnabled);
        if (makeArtEnabled && makeArtPromptInput && !makeArtPromptInput.value.trim()) {
          makeArtPromptInput.value = 'anime';
        }
        if (makeArtPromptInput) {
          const addition = makeArtPromptInput.value.trim();
          localStorage.setItem(
            'makeArtPromptAddition',
            addition || ''
          );
        }
        if (makeArtModelSelect) {
          localStorage.setItem('makeArtModel', makeArtModelSelect.value || 'default');
        }
        if (makeArtSizeSelect) {
          localStorage.setItem('makeArtSize', makeArtSizeSelect.value || '1024x1024');
        }

        // Close dialog
        const dialog = document.getElementById('ai-settings-dialog');
        if (dialog) dialog.close();

        console.log('AI settings saved');
      } catch (err) {
        console.error('Error saving AI settings:', err);
      }
    });
  }

  initAIControls();

  document.addEventListener('paste', function (e) {
    const target = e.target.closest && e.target.closest('.chat-log__message');
    if (target && target.isContentEditable) {
      e.preventDefault();
      const text = (e.clipboardData || window.clipboardData).getData('text');
      document.execCommand('insertHTML', false, processMarkdown(text));
    }
  });
});
const copyInviteLink = async () => {
  var text = $('#invite-link')[0].innerHTML;

  try {
    var tempInput = document.createElement('textarea');
    tempInput.value = text;
    tempInput.setAttribute('readonly', '');
    tempInput.style.position = 'absolute';
    tempInput.style.left = '-9999px';
    document.body.appendChild(tempInput);
    tempInput.select();
    if (document.execCommand) {
      document.execCommand('copy');
    }
    document.body.removeChild(tempInput);
    await safeClipboardWrite(text);
    console.log('Content copied to clipboard');
  } catch (err) {
    console.error('Failed to copy: ', err);
  }
};

function getImageFileNameForAI(ai) {
  if (window.ImageUtils && window.ImageUtils.getImageFileNameForAI) {
    return window.ImageUtils.getImageFileNameForAI(ai);
  }
  return 'static/img/netwrck-logo-colord256.webp';
}

function renderPicsWithControls(aiJson, il, searchQuery) {
  var aiImgUrl = aiJson['path'];
  var filefullURL = `${aiImgUrl}`;
  var filefullURLEncoded = encodeURIComponent(filefullURL);

  $('#createdImagePicsList')
    .append(`<div class="ai-char-item mdl-cell mdl-cell--stretch mdl-cell--6-col mdl-list__item mdl-list__item--three-line">
    <a id="mdl-editor-btn-${il}" href="https://editor.ebank.nz/index.html?imageUrl=${filefullURLEncoded}"
           class="mdl-editor-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank">
              <i class="material-icons">brush</i>
        </a>
        <label class="mdl-tooltip" data-mdl-for="mdl-editor-btn-${il}">
            Open in Editor
        </label>
        <a id="mdl-download-btn-${il}"
           class="mdl-editor-btn mdl-download-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank" data-download="madepicture${il}">
              <i class="material-icons">download</i>
        </a>
        <label class="mdl-tooltip" data-mdl-for="mdl-download-btn-${il}">
            Download
        </label>
        <a id="mdl-share-btn-${il}"
           class="mdl-editor-btn mdl-share-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank" data-share="madepicture${il}">
              <i class="material-icons">share</i>
        </a>
        <label class="mdl-tooltip" data-mdl-for="mdl-share-btn-${il}">
            Share To Social
        </label>
        <img loading="lazy" class="madepicture${il} b8 ai-generated-char-chooser ai-generated-char-item" src="${aiImgUrl}" alt="${searchQuery}">
    </div>`);
}

function normalizeAICharacter(raw) {
  if (!raw) return null;
  let ai = raw;
  if (ai.data && typeof ai.data === 'object') {
    ai = ai.data;
  } else if (ai.success && ai.data && typeof ai.data === 'object') {
    ai = ai.data;
  }
  if (Array.isArray(ai) && ai.length > 0) {
    ai = ai[0];
  }
  if (!ai || typeof ai !== 'object') {
    return null;
  }
  const normalized = { ...ai };
  const name = normalized.name || normalized.Name || '';
  normalized.name = typeof name === 'string' ? name : String(name || '');
  const description = normalized.description || normalized.Description || '';
  normalized.description =
    typeof description === 'string' ? description : String(description || '');
  const greeting = normalized.greeting || normalized.Greeting || '';
  normalized.greeting =
    typeof greeting === 'string' ? greeting : String(greeting || '');
  if (!normalized.avatar_file_name && normalized.AvatarFileName) {
    normalized.avatar_file_name = normalized.AvatarFileName;
  }
  return normalized;
}

var APPController = (function () {
  var appControllerSelf = {};

  appControllerSelf.renderRooms = function ($target, searchQuery) {
    var escapeRoomText =
      window.StringUtils && typeof window.StringUtils.escapeHtml === 'function'
        ? window.StringUtils.escapeHtml
        : function (value) {
            return $('<div>').text(value == null ? '' : String(value)).html();
          };
    var render = function (rooms) {
      var $target = $('#autoCompleteRoomsList');
      var template = '';
      for (var i = 0; i < rooms.length; i++) {
        var room = rooms[i];
        if (!room || !room.name) continue;
        var slug =
          room.room_key &&
          typeof room.room_key === 'string' &&
          room.room_key.trim()
            ? room.room_key
            : room.name.replace(/ /g, '-').toLowerCase();
        var urlencodedName = encodeURIComponent(slug);
        var count = Number(room.message_count) || 0;
        var meta = [];
        if (room.is_owned) meta.push('Yours');
        if (count > 0) meta.push(count.toLocaleString() + (count === 1 ? ' message' : ' messages'));
        template +=
          '<a class="room-result" href="/r/' +
          urlencodedName +
          '"><span class="room-result-icon">' +
          escapeRoomText(room.emoji || '#') +
          '</span><span class="room-result-name">' +
          escapeRoomText(room.name) +
          '</span><span class="room-result-meta">' +
          escapeRoomText(meta.join(' · ')) +
          '</span></a>';
      }
      $target.html(template || '<div class="room-results-empty">No rooms found</div>');
    };

    $('#autoCompleteRoomsList').html('<div class="room-results-empty">Loading rooms</div>');

    if (searchQuery && searchQuery.trim().length > 0) {
      dataAccessor.searchRooms(searchQuery.trim(), function (rooms) {
        render(rooms);
      });
      return;
    }

    dataAccessor.getAllRooms(function (rooms) {
      render(rooms);
    });
  };

  var renderAIViewSearchPage = function (ais, target) {
    ais = ais || [];
    if (!Array.isArray(ais)) {
      ais = [];
    }

    dataAccessor.currentAIs = ais;

    dataAccessor.aisByName = dataAccessor.aisByName || {};
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      if (ai && ai.name) {
        dataAccessor.aisByName[ai.name] = ai;
      }
    }

    var $target = $(target);
    var template = '';

    // Define isRoomContext at a higher scope to ensure it's accessible to all callbacks
    var isRoomContext =
      target === '#autoCompleteUsersRoomList' ||
      (target && target.indexOf('autoCompleteUsersRoomList') !== -1);

    if (
      target === '.ai-character-grid' ||
      target.selector === '.ai-character-grid'
    ) {
      for (let i = 0; i < ais.length; i++) {
        let ai = ais[i];
        if (!ai.name) {
          continue;
        }
        var fileName = getImageFileNameForAI(ai);
        var displayText = ai.description ? ai.description : ai.greeting;
        var aiPath = ai.url_name || ai.URLName || ai.name;

        var hasDescriptionClass =
          ai.description && ai.description.trim() ? 'has-description' : '';

        if (isRoomContext) {
          template += `
                    <div class="ai-character-item hidden-bot ${hasDescriptionClass}" data-ai-name="${encodeURIComponent(ai.name)}" style="cursor: pointer;">
                     <span class="mdl-list__item-primary-content">
                            <span class="mdl-chip__contact mdl-list__item-avatar">
                               <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${ai.name}" loading="lazy" >
                            </span>
                            <span class="ai-name mdl-chip__text">${ai.name}</span>
                            <span class="mdl-list__item-text-body">
                            ${displayText}
                            </span>
                        </span>
                    </div>
                    `;
        } else {
          template += `
                    <a class="ai-character-item hidden-bot ${hasDescriptionClass}" href="/ai-chat/${encodeURIComponent(aiPath)}" data-bypass="true">
                     <span class="mdl-list__item-primary-content">
                            <span class="mdl-chip__contact mdl-list__item-avatar">
                               <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${ai.name}" loading="lazy" >
                            </span>
                            <span class="ai-name mdl-chip__text">${ai.name}</span>
                            <span class="mdl-list__item-text-body">
                            ${displayText}
                            </span>
                        </span>
                    </a>
                    `;
        }
      }

      $target.html(template);

      if (typeof applyMasonrySizes === 'function') {
        applyMasonrySizes();
      } else {
        applyBasicMasonrySizing();
      }

      revealMoreBots();

      $(window).off('scroll.loadMoreBots');

      let scrollTimeout;
      $(window).on('scroll.loadMoreBots', function () {
        if (scrollTimeout) {
          clearTimeout(scrollTimeout);
        }
        scrollTimeout = setTimeout(function () {
          if (
            $(window).scrollTop() + $(window).height() >
            $(document).height() - 600
          ) {
            if ($('.ai-character-item.hidden-bot').length > 0) {
              console.log('Scrolling near bottom, revealing more bots...');
              revealMoreBots();
            }
          }
        }, 150);
      });
    } else {
      if (ais.length >= 120) {
        // Build all AI items directly
        for (let i = 0; i < ais.length; i++) {
          let ai = ais[i];
          if (!ai.name) {
            continue;
          }
          var fileName2 = getImageFileNameForAI(ai);
          var displayText2 = ai.description ? ai.description : ai.greeting;
          var aiPath2 = ai.url_name || ai.URLName || ai.name;

          if (isRoomContext) {
            template += `
                        <div class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" data-ai-name="${encodeURIComponent(ai.name)}" style="cursor: pointer;">
                         <span class="mdl-list__item-primary-content">
                                <span class="mdl-chip__contact mdl-list__item-avatar">
                                   <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName2}" alt="${ai.name}" loading="lazy" >
                                </span>
                                <span class="ai-name mdl-chip__text">${ai.name}</span>
                                <span class="mdl-list__item-text-body">
                                ${displayText2}
                                </span>
                            </span>
                        </div>
                        `;
          } else {
            template += `
                        <a class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" href="/ai-chat/${encodeURIComponent(aiPath2)}" data-bypass="true">
                         <span class="mdl-list__item-primary-content">
                                <span class="mdl-chip__contact mdl-list__item-avatar">
                                   <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName2}" alt="${ai.name}" loading="lazy" >
                                </span>
                                <span class="ai-name mdl-chip__text">${ai.name}</span>
                                <span class="mdl-list__item-text-body">
                                ${displayText2}
                                </span>
                            </span>
                        </a>
                        `;
          }
        }

        // Add load more button after AI items
        template += `
                <div class="mdl-cell mdl-cell--12-col" style="text-align: center; margin-top: 30px;">
                    <button id="load-more-button" class="mdl-button mdl-js-button mdl-button--raised mdl-button--colored">
                        Load More
                    </button>
                    <div id="load-more-progress" class="mdl-progress mdl-js-progress mdl-progress__indeterminate" style="display: none; width: 100%"></div>
                </div>
                `;

        $target.html(template);

        // Enable infinite scroll for AI image gallery
        enableInfiniteScroll();

        // Auto-load more content if there's not much on screen
        setTimeout(checkAndAutoLoad, 500);

        $('#load-more-button')
          .off('click')
          .on('click', function () {
            loadMoreAISearchResults();
          });
      } else {
        for (let i = 0; i < ais.length; i++) {
          let ai = ais[i];
          if (!ai.name) {
            continue;
          }
          var fileName3 = getImageFileNameForAI(ai);
          var displayText3 = ai.description ? ai.description : ai.greeting;
          var aiPath3 = ai.url_name || ai.URLName || ai.name;
          if (isRoomContext) {
            template += `
                        <div class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" data-ai-name="${encodeURIComponent(ai.name)}" style="cursor: pointer;">
                         <span class="mdl-list__item-primary-content">
                                <span class="mdl-chip__contact mdl-list__item-avatar">
                                   <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName3}" alt="${ai.name}" loading="lazy" >
                                </span>
                                <span class="ai-name mdl-chip__text">${ai.name}</span>
                                <span class="mdl-list__item-text-body">
                                ${displayText3}
                                </span>
                            </span>
                        </div>
                        `;
          } else {
            template += `
                        <a class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" href="/ai-chat/${encodeURIComponent(aiPath3)}" data-bypass="true">
                         <span class="mdl-list__item-primary-content">
                                <span class="mdl-chip__contact mdl-list__item-avatar">
                                   <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName3}" alt="${ai.name}" loading="lazy" >
                                </span>
                                <span class="ai-name mdl-chip__text">${ai.name}</span>
                                <span class="mdl-list__item-text-body">
                                ${displayText3}
                                </span>
                            </span>
                        </a>
                        `;
          }
        }
        $target.html(template);
      }
    }
    if (isRoomContext) {
      $target.find('.ai-character-item').each(function () {
        var item = $(this);
        var nameEl = item.find('.ai-name');
        var name = nameEl.text().trim();
        item.attr({
          role: 'button',
          tabindex: '0',
          'aria-label': 'Add ' + name + ' to this room',
        });
        if (!nameEl.is('a')) {
          var roomAI = dataAccessor.aisByName[name] || {};
          var roomAIPath = roomAI.url_name || roomAI.URLName || name;
          nameEl.replaceWith(
            $('<a>', {
              class: nameEl.attr('class') + ' room-character-chat-link',
              href: '/ai-chat/' + encodeURIComponent(roomAIPath),
              target: '_blank',
              rel: 'noopener',
              'data-bypass': 'true',
              title: 'Chat 1:1 with ' + name + ' in a new tab',
              text: name,
            })
          );
        }
        if (!item.find('.room-character-add-label').length) {
          item.append(
            '<span class="room-character-add-label"><i class="material-icons" aria-hidden="true">add</i>Add</span>'
          );
        }
      });
    }
    $target.attr('data-owned-priority', 'true');
  };

  function loadMoreAISearchResults() {
    var currentQuery = $('.searchboxaisprompt').val();

    if (!currentQuery) {
      return;
    }

    $('#load-more-progress').show();
    $('#load-more-button').prop('disabled', true);

    var offset = dataAccessor.currentAIs ? dataAccessor.currentAIs.length : 0;

    dataAccessor
      .searchAiImages(currentQuery, offset, 120)
      .then(function (moreAis) {
        appendAIViewSearchPage(moreAis, '#autoCompleteUsersList');
      })
      .catch(function (error) {
        console.error('Error loading more results:', error);
      })
      .finally(function () {
        $('#load-more-progress').hide();
        $('#load-more-button').prop('disabled', false);
      });
  }

  // Infinite scroll functionality for AI image gallery
  let infiniteScrollState = {
    isLoading: false,
    hasMoreContent: true,
    isEnabled: false,
    lastQuery: '',
    autoLoadCount: 0,
    maxAutoLoads: 20, // Prevent excessive auto-loading
  };

  function enableInfiniteScroll() {
    infiniteScrollState.isEnabled = true;
    infiniteScrollState.autoLoadCount = 0;
    $(window).on('scroll.infiniteScroll', handleInfiniteScroll);
  }

  function checkAndAutoLoad() {
    if (!infiniteScrollState.isEnabled || infiniteScrollState.isLoading) {
      return;
    }

    var $container = $('#autoCompleteUsersList');
    if ($container.length === 0) {
      return;
    }

    var containerHeight = $container.outerHeight();
    var viewportHeight = $(window).height();

    // If container content is less than 1.5 viewport heights, auto-load more
    if (
      containerHeight < viewportHeight * 1.5 &&
      infiniteScrollState.hasMoreContent
    ) {
      console.log('Auto-loading more content - not enough on screen');
      loadMoreAIImagesInfiniteScroll();
    }
  }

  function disableInfiniteScroll() {
    infiniteScrollState.isEnabled = false;
    $(window).off('scroll.infiniteScroll');
    $('#infinite-scroll-loading').remove();
  }

  // Cleanup infinite scroll when window is unloaded
  $(window).on('beforeunload', disableInfiniteScroll);

  // Export functions to window for testing and external access
  window.infiniteScrollFunctions = {
    enableInfiniteScroll: enableInfiniteScroll,
    disableInfiniteScroll: disableInfiniteScroll,
    checkAndAutoLoad: checkAndAutoLoad,
    loadMoreAIImagesInfiniteScroll: loadMoreAIImagesInfiniteScroll,
    infiniteScrollState: infiniteScrollState,
  };

  function handleInfiniteScroll() {
    if (
      !infiniteScrollState.isEnabled ||
      infiniteScrollState.isLoading ||
      !infiniteScrollState.hasMoreContent
    ) {
      return;
    }

    var currentQuery = $('.searchboxaisprompt').val();
    if (!currentQuery) {
      return;
    }

    // Check if query changed, reset state if so
    if (currentQuery !== infiniteScrollState.lastQuery) {
      infiniteScrollState.lastQuery = currentQuery;
      infiniteScrollState.hasMoreContent = true;
      infiniteScrollState.autoLoadCount = 0;
    }

    // Stop auto-loading after max attempts to prevent excessive requests
    if (infiniteScrollState.autoLoadCount >= infiniteScrollState.maxAutoLoads) {
      return;
    }

    var $container = $('#autoCompleteUsersList');
    if ($container.length === 0) {
      return;
    }

    var containerBottom = $container.offset().top + $container.outerHeight();
    var viewportBottom = $(window).scrollTop() + $(window).height();
    var threshold = 300; // Trigger when 300px from bottom

    if (viewportBottom + threshold >= containerBottom) {
      console.log('Infinite scroll triggered - loading more AI images');
      infiniteScrollState.autoLoadCount++;
      loadMoreAIImagesInfiniteScroll();
    }
  }

  function loadMoreAIImagesInfiniteScroll() {
    var currentQuery = $('.searchboxaisprompt').val();

    if (!currentQuery || infiniteScrollState.isLoading) {
      return;
    }

    infiniteScrollState.isLoading = true;

    // Show subtle loading indicator
    var $loadingIndicator = $('#infinite-scroll-loading');
    if ($loadingIndicator.length === 0) {
      $loadingIndicator = $(
        '<div id="infinite-scroll-loading" style="text-align: center; padding: 20px; color: #666;"><i class="material-icons" style="animation: spin 1s linear infinite;">refresh</i> Loading more art...</div>'
      );
      $('#autoCompleteUsersList').after($loadingIndicator);
    }
    $loadingIndicator.show();

    var offset = dataAccessor.currentAIs ? dataAccessor.currentAIs.length : 0;

    dataAccessor
      .searchAiImages(currentQuery, offset, 40) // Smaller batch for infinite scroll
      .then(function (moreAis) {
        if (!moreAis || moreAis.length === 0) {
          infiniteScrollState.hasMoreContent = false;
          $loadingIndicator.html(
            '<div style="padding: 20px; color: #999;">No more art to load</div>'
          );
          setTimeout(() => $loadingIndicator.fadeOut(), 2000);
          return;
        }

        appendAIViewSearchPage(moreAis, '#autoCompleteUsersList');
        $loadingIndicator.hide();

        // If we got fewer results than requested, assume we're near the end
        if (moreAis.length < 40) {
          infiniteScrollState.hasMoreContent = false;
        }

        // After loading, check if we need to load more to fill screen
        setTimeout(checkAndAutoLoad, 300);
      })
      .catch(function (error) {
        console.error('Error loading more results:', error);
        infiniteScrollState.hasMoreContent = false;
        $loadingIndicator.html(
          '<div style="padding: 20px; color: #d32f2f;">Error loading more art</div>'
        );
        setTimeout(() => $loadingIndicator.fadeOut(), 3000);
      })
      .finally(function () {
        infiniteScrollState.isLoading = false;
      });
  }

  // Add CSS for loading spinner animation
  if (!document.getElementById('infinite-scroll-styles')) {
    var style = document.createElement('style');
    style.id = 'infinite-scroll-styles';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      #infinite-scroll-loading {
        font-size: 14px;
        transition: opacity 0.3s ease;
      }
    `;
    document.head.appendChild(style);
  }

  const BOTS_PER_PAGE = 80;

  function revealMoreBots() {
    const $hiddenBots = $('.ai-character-item.hidden-bot');
    if ($hiddenBots.length === 0) {
      return;
    }

    $hiddenBots.slice(0, BOTS_PER_PAGE).removeClass('hidden-bot');
  }

  function setupInfiniteScroll() {
    $(window).off('scroll.loadMoreBots');

    let scrollTimeout;
    $(window).on('scroll.loadMoreBots', function () {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(function () {
        if (
          $(window).scrollTop() + $(window).height() >
          $(document).height() - 600
        ) {
          if ($('.ai-character-item.hidden-bot').length > 0) {
            console.log('Scrolling near bottom, revealing more bots...');
            revealMoreBots();
          }
        }
      }, 150);
    });
  }

  // Initialize infinite scroll if we have hidden bots on the page
  if (
    $('.ai-character-item.hidden-bot').length > 0 ||
    window.location.pathname === '/aiartgenerator'
  ) {
    setupInfiniteScroll();
  }

  appControllerSelf.renderAIBotsSearchPage = function (target, searchQuery) {
    $('#search-ai-loading-progress').show();

    let allBotsRendered =
      dataAccessor.initialAIs || dataAccessor.currentAIs || [];
    let botsToShow = [];

    if (searchQuery) {
      searchQuery = searchQuery.toLowerCase().trim();
      if (searchQuery === '') {
        botsToShow = allBotsRendered;
      } else {
        botsToShow = allBotsRendered.filter(
          (ai) =>
            (ai.name && ai.name.toLowerCase().includes(searchQuery)) ||
            (ai.description &&
              ai.description.toLowerCase().includes(searchQuery)) ||
            (ai.greeting && ai.greeting.toLowerCase().includes(searchQuery)) ||
            (() => {
              // Normalize tags to array for safe searching
              let tagsArray = [];
              if (ai.tags) {
                if (Array.isArray(ai.tags)) {
                  tagsArray = ai.tags;
                } else if (typeof ai.tags === 'string') {
                  tagsArray = ai.tags.split(',').map((t) => t.trim());
                }
              }
              return tagsArray.some((tag) =>
                String(tag).toLowerCase().includes(searchQuery)
              );
            })()
        );
      }
    } else {
      botsToShow = allBotsRendered;
    }

    renderAIViewSearchPage(botsToShow, target);
    $('#search-ai-loading-progress').hide();
  };

  appControllerSelf.renderAIs = function (targetSelector, searchQuery = '') {
    $('#search-ai-loading-progress').show();

    const query = searchQuery.trim().toLowerCase();
    // If query present, merge "yours" (user AIs) first + matched normal search
    if (query) {
      const normalizeArr = (arr) => (Array.isArray(arr) ? arr : []);
      const fetchUserAIs = () =>
        dataAccessor.userAIs && typeof dataAccessor.userAIs === 'function'
          ? dataAccessor.userAIs(window.userData?.secret)
          : Promise.resolve([]);

      Promise.allSettled([dataAccessor.searchAis(query), fetchUserAIs()])
        .then((parts) => {
          const searchRes =
            parts[0].status === 'fulfilled' ? normalizeArr(parts[0].value) : [];
          const userRes =
            parts[1].status === 'fulfilled' ? normalizeArr(parts[1].value) : [];

          // Filter user AIs by the same query
          const q = query;
          const matches = (ai) => {
            try {
              const hay = [
                ai && (ai.name || ai.Name || ''),
                ai && (ai.description || ai.Description || ''),
                ai && (ai.greeting || ai.Greeting || ''),
                Array.isArray(ai && ai.tags)
                  ? (ai.tags || []).join(' ')
                  : (ai && ai.tags) || '',
              ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
              return hay.indexOf(q) !== -1;
            } catch (_) {
              return false;
            }
          };

          const filteredUser = userRes.filter(matches);

          // Dedup by name (case-insensitive): user AIs first
          const seen = new Set();
          const out = [];
          const pushUnique = (ai) => {
            if (!ai) return;
            const name = (ai.name || ai.Name || '').trim();
            if (!name) return;
            const key = name.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            out.push(ai);
          };

          filteredUser.forEach(pushUnique);
          searchRes.forEach(pushUnique);

          renderAIViewSearchPage(out, targetSelector);
        })
        .catch((err) => {
          console.error('AI search failed:', err);
          renderAIViewSearchPage([], targetSelector);
        })
        .finally(() => $('#search-ai-loading-progress').hide());
      return;
    }

    // No query: show combined top/default list via accessor (already merged + deduped)
    dataAccessor
      .topAIs(window.userData?.secret)
      .then((ais) => renderAIViewSearchPage(ais || [], targetSelector))
      .catch(() => renderAIViewSearchPage([], targetSelector))
      .finally(() => $('#search-ai-loading-progress').hide());
  };

  let renderAIPicsView = function (ais) {
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      if (ai.name.startsWith('ai ')) {
        ai.name = ai.name.slice(3);
      }
    }
    dataAccessor.currentAIs = ais;

    dataAccessor.aisByName = dataAccessor.aisByName || {};
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      dataAccessor.aisByName[ai.name] = ai;
    }

    var $target = $('#autoCompleteUsersPicsList');

    var template = '';
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      if (!ai.name) {
        continue;
      }

      // Decode the name for display
      var nameDecoded = ai.name;
      try {
        nameDecoded = decodeURIComponent(ai.name);
      } catch (e) {
        console.error('Error decoding AI name:', e);
        nameDecoded = ai.name; // Fall back to original name if decoding fails
      }

      var fileName = ai.avatar_file_name;
      if (fileName) {
        fileName = fileName.replace(' ', '-');
      }
      if (fileName && fileName.indexOf('static/uploads') === -1) {
        fileName = 'static/uploads/' + fileName;
      }
      if (!fileName || fileName === 'None') {
        fileName = 'static/img/netwrck-logo-colord256.webp';
      }
      var filefullURL = `https://netwrckstatic.netwrck.com/${fileName}`;
      var displayText = ai.description ? ai.description : ai.greeting;
      template += `<div class="ai-char-item mdl-cell mdl-cell--stretch mdl-cell--2-col mdl-list__item mdl-list__item--three-line" >
 <a class="mdl-list__item-primary-content" href="/?c=${encodeURIComponent(ai.name)}">

        <span class="mdl-chip__contact mdl-list__item-avatar">
            <img id="aiartgenerator-image${i}" loading="lazy" class="jspicture${i} ai-generated-char-chooser ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${ai.name}">
            <label class="mdl-tooltip mdl-tooltip-large mdl-tooltip--top" data-mdl-for="aiartgenerator-image${i}">
                ${nameDecoded}
            </label>
        </span>
    </a>

</div>`;
    }
    $target.html(template);
    componentHandler.upgradeDom();
    if (typeof adsFix === 'function') {
      adsFix();
    }
  };

  let renderAIImagesPicsView = function (ais) {
    if (!Array.isArray(ais)) {
      ais = [];
    }
    dataAccessor.currentAIs = ais;

    for (var i = 0; i < ais.length; i++) {
      var ai = ais[i];
      if (ai.name.startsWith('ai ')) {
        ai.name = ai.name.slice(3);
      }
    }

    dataAccessor.aisByName = dataAccessor.aisByName || {};
    for (var j = 0; j < ais.length; j++) {
      var aiForName = ais[j];
      dataAccessor.aisByName[aiForName.name] = aiForName;
    }

    var $target = $('#autoCompleteUsersAIImagesPicsList');

    var template = '';
    for (var k = 0; k < ais.length; k++) {
      var currentAi = ais[k];
      if (!currentAi.name) {
        continue;
      }

      // Decode the name for display
      var nameDecoded = currentAi.name;
      try {
        nameDecoded = decodeURIComponent(currentAi.name);
      } catch (e) {
        console.error('Error decoding AI name:', e);
        nameDecoded = currentAi.name; // Fall back to original name if decoding fails
      }

      var fileName = currentAi.avatar_file_name;
      if (fileName) {
        fileName = fileName.replace(' ', '-');
      }
      if (fileName && fileName.indexOf('static/uploads') === -1) {
        fileName = 'static/uploads/' + fileName;
      }
      if (!fileName || fileName === 'None') {
        fileName = 'static/img/netwrck-logo-colord256.webp';
      }

      var displayText = currentAi.description
        ? currentAi.description
        : currentAi.greeting;
      var filefullURL = `https://netwrckstatic.netwrck.com/${fileName}`;
      template += `<div class="ai-char-item mdl-cell mdl-cell--stretch mdl-cell--2-col mdl-list__item mdl-list__item--three-line" >
 <a class="mdl-list__item-primary-content" href="/?c=${encodeURIComponent(
   currentAi.name
 )}">

        <span class="mdl-chip__contact mdl-list__item-avatar">
            <img id="aiartgenerator-image${k}" loading="lazy" class="jspicture${k} ai-generated-char-chooser ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${currentAi.name}">
            <label class="mdl-tooltip mdl-tooltip-large mdl-tooltip--top" data-mdl-for="aiartgenerator-image${k}">
                ${nameDecoded}
            </label>
        </span>
    </a>

</div>`;
    }
    $target.html(template);
    componentHandler.upgradeDom();
    if (typeof adsFix === 'function') {
      adsFix();
    }
  };

  appControllerSelf.renderAIPics = async function ($target, searchQuery) {
    try {
      $('#create-ai-find-loading-progress').show();
      let workFunc;
      if (searchQuery) {
        searchQuery = searchQuery.toLowerCase();
        workFunc = dataAccessor.searchAis(searchQuery);
      } else if (dataAccessor && typeof dataAccessor.topAIs === 'function') {
        workFunc = dataAccessor.topAIs(window.userData?.secret);
      } else {
        workFunc = Promise.resolve(
          dataAccessor.initialAIs || dataAccessor.currentAIs || []
        );
      }
      Promise.resolve(workFunc)
        .then(renderAIPicsView)
        .finally(function () {
          $('#create-ai-find-loading-progress').hide();
        });

      if (searchQuery) {
        searchQuery = searchQuery.toLowerCase();
        var aiImagesWorkFunc = dataAccessor.searchAiImages(searchQuery);
        aiImagesWorkFunc.then(renderAIImagesPicsView).finally(function (err) {
          $('#create-ai-find-loading-progress').hide();
        });
      }
    } catch (e) {
      console.error(e);
    }

    try {
      await Promise.resolve(window.userDataReady || window.userData);
    } catch (e) {}

    var textAugmentations = [
      '',
      ' good looking fantastic',
      ' awesome portrait',
      ' confident engaging wow',
      '',
      '',
    ];
    var prefixModifiers = [
      '',
      'trending ',
      'amazing ',
      'artstation art ',
      'great ',
      'best quality ',
    ];

    $('#createdImagePicsList').html('');
    var retries = 75;

    var styleTransferImgUrl = '';
    var $image = $('#new-ai-avatar-image img');
    if ($image.length > 0) {
      if ($image.parent().attr('id') === 'style-transfer-zone') {
        styleTransferImgUrl = $image.attr('src') || '';
      }
    }

    var promises = [];
    for (var i = 0; i < 5; i++) {
      var currentQuery =
        prefixModifiers[i] + searchQuery + textAugmentations[i];
      var urlkey = encodeURIComponent(currentQuery.split(' ').join('-'));
      if (i > 0) {
        urlkey += '-' + i;
      }
      $('#create-ai-loading-progress').show();
      var shouldRetry = false;
      var creationRequest;

      if (i < 2 && !styleTransferImgUrl) {
        var { aspect, width, height } = dataAccessor.getWidthHeight();

        if (height == 1920) {
          height = 1360;
        }
        if (width == 1920) {
          width = 1360;
        }
        var sizeKey = dataAccessor.getKeyForSDSize();

        if (width == 1080) {
          width = 768;
        }
        if (height == 1080) {
          height = 768;
        }
        var falargs = {
          prompt: currentQuery,

          model_name: 'fal-ai/flux/schnell',
          image_size: sizeKey,
          num_inference_steps: 4,
          guidance_scale: 3.5,
          num_images: 1,
          enable_safety_checker: false,
        };
        creationRequest = dataAccessor
          .falFetch(falargs)
          .then(async function (result) {
            var decoded = await result.json();
            var filefullURL =
              (decoded &&
                decoded.result &&
                decoded.result.images &&
                decoded.result.images[0] &&
                decoded.result.images[0].url) ||
              (decoded && decoded.video && decoded.video.url) ||
              (decoded &&
                decoded.result &&
                decoded.result.video &&
                decoded.result.video.url);
            if (!filefullURL) throw new Error('Missing image URL in response');
            return { path: filefullURL };
          });
      } else if (i == 2 && !styleTransferImgUrl) {
        const secret =
          window.netwrckGetAuthSecret && window.netwrckGetAuthSecret()
            ? window.netwrckGetAuthSecret()
            : userData && userData.secret;
        if (
          !secret ||
          secret === 'anonymous' ||
          secret === 'secret' ||
          secret === 'YOUR_API_KEY'
        ) {
          if (window.thFunnel) window.thFunnel.paywall('auth_required', { feature: 'art' });
          if (typeof window.showLoginDialog === 'function') {
            window.showLoginDialog(null, window.location.pathname);
          }
          continue;
        }
        const ra1Body = {
          prompt: currentQuery,
          size: dataAccessor.getKeyForSDSize(),
          ...(styleTransferImgUrl && { style_image: styleTransferImgUrl }),
          api_key: secret,
          secret: secret,
        };
        const headers = {
          'Content-Type': 'application/json',
          'X-API-Key': secret,
          Authorization: `Bearer ${secret}`,
        };
        const endpoint = typeof window !== 'undefined' && window.NETWRCK_DEFAULT_ART_ENDPOINT ? window.NETWRCK_DEFAULT_ART_ENDPOINT : '/api/ra2';
        creationRequest = (async function () {
          const resp = await fetch(endpoint, {
            method: 'POST',
            headers,
            credentials: 'include',
            body: JSON.stringify(ra1Body),
          });
          if (resp.status === 401) {
            if (window.thFunnel) window.thFunnel.paywall('auth_required', { feature: 'art' });
            if (typeof window.showLoginDialog === 'function') {
              window.showLoginDialog(null, window.location.pathname);
            }
            throw new Error('Authentication required');
          }
          if (resp.status === 402) {
            if (typeof window.showSubscribeDialog === 'function') {
              window.showSubscribeDialog();
            }
            throw new Error('Subscription required');
          }
          const body = await resp.text();
          if (!resp.ok) {
            throw new Error(
              'RA1 request failed ' + resp.status + ': ' + body.slice(0, 180)
            );
          }
          let data;
          try {
            data = body ? JSON.parse(body) : {};
          } catch (e) {
            throw new Error('RA1 returned invalid JSON: ' + body.slice(0, 180));
          }
          const u =
            data?.image_url ||
            (Array.isArray(data?.images) ? data.images[0] : null) ||
            data?.result?.images?.[0]?.url ||
            data?.video?.url ||
            data?.result?.video?.url;
          if (!u) throw new Error('Missing image URL from RA1');
          return { path: u };
        })();
      } else if (i == 3 && !styleTransferImgUrl) {
        var imageSize = dataAccessor.getKeyForSDSize();
        let { aspect, width, height } = dataAccessor.getWidthHeight();

        if (height == 1920) {
          height = 1360;
        }
        if (width == 1920) {
          width = 1360;
        }
        if (width == 1080) {
          width = 768;
        }
        if (height == 1080) {
          height = 768;
        }
        let apiModelName = 'comfy/lee101/imgen';
        let falargs = {
          model_name: apiModelName,
          emptylatentimage_width: width,
          emptylatentimage_height: height,
          cliptextencode_text: searchQuery,
        };
        creationRequest = dataAccessor
          .falFetch(falargs)
          .then(async function (result) {
            var decoded = await result.json();

            var filefullURL = decoded.result.outputs['9'].images[0].url;
            return {
              path: filefullURL,
            };
          });
      } else if (i == 4 && !styleTransferImgUrl) {
        let dimensions = dataAccessor.getWidthHeight();
        let imageWidth = dimensions.width || 1024;
        let imageHeight = dimensions.height || 1024;
        if (imageWidth == 1920) {
          imageWidth = 1360;
        }
        if (imageHeight == 1920) {
          imageHeight = 1360;
        }
        if (imageWidth == 1080) {
          imageWidth = 768;
        }
        if (imageHeight == 1080) {
          imageHeight = 768;
        }
        let params = new URLSearchParams({
          prompt: currentQuery,
          width: String(imageWidth),
          height: String(imageHeight),
          model: 'zimage-turbo',
          auto_lora: 'true',
          teleport: 'true',
          perf: 'false',
        });
        creationRequest = fetch('/api/create-and-upload-image?' + params.toString(), {
          method: 'GET',
          headers: { Accept: 'application/json' },
          credentials: 'include',
        }).then(async function (resp) {
          const body = await resp.text();
          if (!resp.ok) {
            throw new Error(
              'images.netwrck create failed ' +
                resp.status +
                ': ' +
                body.slice(0, 180)
            );
          }
          let data;
          try {
            data = body ? JSON.parse(body) : {};
          } catch (e) {
            throw new Error(
              'images.netwrck returned invalid JSON: ' + body.slice(0, 180)
            );
          }
          const u = data.path || data.image_url;
          if (!u) throw new Error('Missing path from images.netwrck');
          return { path: u };
        });
      } else if (!styleTransferImgUrl) {
        creationRequest = dataAccessor.makeAIImage(searchQuery, urlkey);
      } else {
        creationRequest = dataAccessor.makeAIStyleTransferImage(
          currentQuery,
          urlkey,
          styleTransferImgUrl
        );
      }
      var responsePromise = creationRequest
        .then(
          (function (il) {
            return function (aiJson) {
              renderPicsWithControls(aiJson, il, searchQuery);
              try {
                trackEvent('ai', 'create', 'success');
              } catch (e) {
                console.error(e);
              }
              shouldRetry = false;
            };
          })(i)
        )
        .catch(function (err) {
          console.log(err);

          shouldRetry = true;
        });
      promises.push(responsePromise);
      if (styleTransferImgUrl) {
        await responsePromise;
      }

      if (!styleTransferImgUrl) {
        shouldRetry = false;
      }
      if (shouldRetry) {
        if (retries > 0) {
          i--;
          retries--;
        }
        trackEvent('ai', 'create', 'fail');
      } else {
        trackEvent('ai', 'create', 'win');
        subscribeImageGenCounter++;
        setCookie('subscribeImageGenCounter', subscribeImageGenCounter, 365);
      }
    }
    await Promise.all(promises);
    $('#create-ai-loading-progress').hide();
  };

  appControllerSelf.renderAITransparentPics = async function (
    $target,
    searchQuery
  ) {
    var textAugmentations = [
      '',
      ' good looking fantastic',
      ' awesome portrait',
      ' confident engaging wow',
      '',
      '',
    ];
    var prefixModifiers = [
      'solid color background, ',
      'solid color background, trending ',
      'solid color background, amazing ',
      'solid color background, artstation art ',
      'solid color background, high contrast ',
      'solid color background, best quality ',
    ];

    $('#createdImagePicsList').html('');
    var retries = 75;

    var styleTransferImgUrl = '';
    var $image = $('#new-ai-avatar-image img');
    if ($image.length > 0) {
      styleTransferImgUrl = $image.attr('src') || '';
    }

    for (var i = 0; i < 3; i++) {
      var currentQuery =
        prefixModifiers[i] + searchQuery + textAugmentations[i];
      var urlkey = encodeURIComponent(currentQuery.split(' ').join('-'));
      if (i > 0) {
        urlkey += '-' + i;
      }
      $('#create-ai-loading-progress').show();
      var shouldRetry = false;

      var { aspect, width, height } = dataAccessor.getWidthHeight();

      if (height == 1920) {
        height = 1360;
      }
      if (width == 1920) {
        width = 1360;
      }
      if (width == 1080) {
        width = 768;
      }
      if (height == 1080) {
        height = 768;
      }
      var falargs = {
        model_name: 'comfy/lee101/imgen-bgr',
        emptylatentimage_width: width,
        emptylatentimage_height: height,
        cliptextencode_text: currentQuery,
      };

      var creationRequest = dataAccessor
        .falFetch(falargs)
        .then(async function (result) {
          var decoded = await result.json();

          var filefullURL = decoded.result.outputs['9'].images[0].url;

          return {
            path: filefullURL,
          };
        });
      var responsePromise = creationRequest
        .then(
          (function (il) {
            return function (aiJson) {
              renderPicsWithControls(aiJson, il, searchQuery);
              try {
                trackEvent('ai', 'create', 'success');
              } catch (e) {
                console.error(e);
              }
              shouldRetry = false;
            };
          })(i)
        )
        .catch(function (err) {
          console.log(err);

          shouldRetry = true;
        })
        .finally(function (err) {
          $('#create-ai-loading-progress').hide();
        });
      if (i !== 0) {
        await responsePromise;
      }

      if (i == 0) {
        shouldRetry = false;
      }
      if (shouldRetry) {
        if (retries > 0) {
          i--;
          retries--;
        }
        trackEvent('ai', 'create', 'fail');
      } else {
        trackEvent('ai', 'create', 'win');
        subscribeImageGenCounter++;
        setCookie('subscribeImageGenCounter', subscribeImageGenCounter, 365);
      }
    }
  };

  appControllerSelf.renderAIStyleTransferPics = async function (
    $target,
    searchQuery
  ) {
    var textAugmentations = [
      '',
      ' artistic style',
      ' masterpiece rendition',
      ' creative interpretation',
      '',
      '',
    ];
    var prefixModifiers = [
      '',
      'in the style of ',
      'inspired by ',
      'reimagined as ',
      'transformed into ',
      'artistic rendition of ',
    ];

    $('#createdImagePicsList').html('');
    var retries = 75;

    var styleTransferImgUrl = '';
    var $image = $('#new-ai-avatar-image img');
    if ($image.length > 0) {
      styleTransferImgUrl = $image.attr('src') || '';
    }

    for (var i = 0; i < 3; i++) {
      var currentQuery =
        prefixModifiers[i] + searchQuery + textAugmentations[i];
      var urlkey = encodeURIComponent(currentQuery.split(' ').join('-'));
      if (i > 0) {
        urlkey += '-' + i;
      }
      $('#create-ai-loading-progress').show();
      var shouldRetry = false;

      var creationRequest = dataAccessor.makeAIStyleTransferImage(
        currentQuery,
        urlkey,
        styleTransferImgUrl
      );

      var responsePromise = creationRequest
        .then(
          (function (il) {
            return function (aiJson) {
              var aiImgUrl = aiJson['path'];
              var filefullURL = `${aiImgUrl}`;
              var filefullURLEncoded = encodeURIComponent(filefullURL);
              $('#createdImagePicsList')
                .append(`<div class="ai-char-item mdl-cell mdl-cell--stretch mdl-cell--6-col mdl-list__item mdl-list__item--three-line">
    <a id="mdl-editor-btn-${il}" href="https://editor.ebank.nz/index.html?imageUrl=${filefullURLEncoded}"
           class="mdl-editor-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank">
              <i class="material-icons">brush</i>
        </a>
        <label class="mdl-tooltip" data-mdl-for="mdl-editor-btn-${il}">
            Open in Editor
        </label>
        <a id="mdl-download-btn-${il}"
           class="mdl-editor-btn mdl-download-btn mdl-button mdl-js-button mdl-button--fab mdl-button--mini-fab mdl-button--colored" target="_blank" data-download="madepicture${il}">
              <i class="material-icons">download</i>
        </a>
        <label class="mdl-tooltip" data-mdl-for="mdl-download-btn-${il}">
            Download
        </label>
        <img loading="lazy" class="madepicture${il} b8 ai-generated-char-chooser ai-generated-char-item" src="${aiImgUrl}" alt="${searchQuery}">
                        </div>`);
              try {
                trackEvent('ai', 'create', 'success');
              } catch (e) {
                console.error(e);
              }
              shouldRetry = false;
            };
          })(i)
        )
        .catch(function (err) {
          console.log(err);
          shouldRetry = true;
        })
        .finally(function (err) {
          $('#create-ai-loading-progress').hide();
        });

      if (i !== 0) {
        await responsePromise;
      }

      if (i == 0) {
        shouldRetry = false;
      }
      if (shouldRetry) {
        if (retries > 0) {
          i--;
          retries--;
        }
        trackEvent('ai', 'create', 'fail');
      } else {
        trackEvent('ai', 'create', 'win');
        subscribeImageGenCounter++;
        setCookie('subscribeImageGenCounter', subscribeImageGenCounter, 365);
      }
    }
  };

  appControllerSelf.renderAIUpscalePics = async function (
    $target,
    searchQuery
  ) {
    var textAugmentations = [
      '',
      ' good looking fantastic',
      ' awesome portrait',
      ' confident engaging wow',
      '',
      '',
    ];

    var prefixModifiers = [
      '',
      'solid color background, trending ',
      'solid color background, amazing ',
      'solid background, artstation art ',
      'solid color background, high contrast ',
      'solid color background, best quality ',
    ];

    $('#createdImagePicsList').html('');
    var retries = 75;

    var styleTransferImgUrl = '';
    var $image = $('#new-ai-avatar-image img');
    if ($image.length > 0) {
      styleTransferImgUrl = $image.attr('src') || '';
    }

    for (var i = 0; i < 1; i++) {
      var currentQuery =
        prefixModifiers[i % prefixModifiers.length] +
        searchQuery +
        textAugmentations[i % textAugmentations.length];
      var urlkey = encodeURIComponent(currentQuery.split(' ').join('-'));
      if (i > 0) {
        urlkey += '-' + i;
      }
      $('#create-ai-loading-progress').show();
      var shouldRetry = false;

      var baseUrl = '';

      var creationRequest = fetch(baseUrl + '/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model_name:
            'philz1337x/clarity-upscaler:b8a46b09384dc1ac996596bc14058e2b7604971128ee7de709a40d4bbf982d2c',
          seed: 1337,
          image: styleTransferImgUrl,
          prompt: currentQuery,
          dynamic: 6,
          sd_model: 'juggernaut_reborn.safetensors [338b85bc4f]',
          scheduler: 'DPM++ 3M SDE Karras',
          creativity: 0.35,
          lora_links: '',
          downscaling: false,
          resemblance: 0.6,
          scale_factor: 2,
          tiling_width: 112,
          tiling_height: 144,
          custom_sd_model: '',
          negative_prompt:
            '(worst quality, low quality, normal quality:2) JuggernautNegative-neg',
          num_inference_steps: 25,
          downscaling_resolution: 768,
          dmal:
            new Date().toLocaleString('en-US', {
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              year: '2-digit',
              timeZone: 'UTC',
            }) +
            'asdf' +
            ((window.userData && window.userData.is_subscribed) || false) +
            'asdf' +
            ((window.userData && window.userData.secret) || ''),
        }),
      }).then(async function (result) {
        if (!result.ok) {
          var errorData = await result.json();
          if (result.status === 401 && errorData.redirect) {
            window.location.href =
              errorData.redirect +
              '?next=' +
              encodeURIComponent(
                window.location.pathname + window.location.search
              );
            return;
          } else if (result.status === 402) {
            alert(
              errorData.message ||
                'This feature requires an active subscription'
            );
            return;
          }
          throw new Error(errorData.error || 'Request failed');
        }
        var decoded = await result.json();
        var filefullURLEncoded = encodeURIComponent(decoded.output);

        var filefullURL = decoded.output;
        return {
          path: filefullURL,
        };
      });
      var responsePromise = creationRequest
        .then(
          (function (il) {
            return function (aiJson) {
              renderPicsWithControls(aiJson, il, searchQuery);
              try {
                trackEvent('ai', 'create', 'success');
              } catch (e) {
                console.error(e);
              }
              shouldRetry = false;
            };
          })(i)
        )
        .catch(function (err) {
          console.log(err);

          shouldRetry = true;
        })
        .finally(function (err) {
          $('#create-ai-loading-progress').hide();
        });
      if (i !== 0) {
        await responsePromise;
      }

      if (i == 0) {
        shouldRetry = false;
      }
      if (shouldRetry) {
        if (retries > 0) {
          i--;
          retries--;
        }
        trackEvent('ai', 'create', 'fail');
      } else {
        trackEvent('ai', 'create', 'win');
        subscribeImageGenCounter++;
        setCookie('subscribeImageGenCounter', subscribeImageGenCounter, 365);
      }
    }
  };

  appControllerSelf.writeNewAItoLocationFunc = function (name, imgUrl) {
    function extractObjectPath(urlOrPath) {
      try {
        if (!urlOrPath) return '';
        var s = String(urlOrPath).trim();
        if (s.startsWith('static/uploads/')) return s;
        if (s.startsWith('uploads/'))
          return 'static/uploads/' + s.slice('uploads/'.length);
        var idx = s.indexOf('static/uploads/');
        if (idx >= 0) return s.slice(idx);
        var idx2 = s.indexOf('/uploads/');
        if (idx2 >= 0)
          return 'static/uploads/' + s.slice(idx2 + '/uploads/'.length);
        var last = s.split('/').pop();
        if (last && last.indexOf('.') > 0) return 'static/uploads/' + last;
        return s;
      } catch (_) {
        return String(urlOrPath || '');
      }
    }
    // Decide if we must upload to our R2 bucket
    var mustUpload = false;
    var isHttp = /^https?:\/\//i.test(imgUrl);
    if (isHttp) {
      try {
        var u = new URL(imgUrl);
        var host = (u.hostname || '').toLowerCase();
        // Upload if not already on our static bucket
        var isOurBucket =
          host === 'netwrckstatic.netwrck.com' || host === 'static.netwrck.com';
        // Also consider any fal domains as requiring upload
        var isFalDomain =
          /(^|\.)fal\.media$/i.test(host) ||
          host === 'cdn.fal.ai' ||
          /(^|\.)fal\.ai$/i.test(host);
        mustUpload = !isOurBucket || isFalDomain;
      } catch (e) {
        // If URL parsing fails but starts with http, err on the side of uploading
        mustUpload = true;
      }
    }

    if (mustUpload) {
      // Ensure uniqueness to avoid collisions
      name = name + ' ' + Math.floor(Math.random() * 9000 + 1000);

      handleUpload(imgUrl, name)
        .then((uploadedPath) => {
          const bucket =
            uploadedPath.bucket_name || 'netwrckstatic.netwrck.com';
          imgUrl = 'https://' + bucket + '/' + uploadedPath.object_path;

          var fileName = extractObjectPath(uploadedPath.object_path);
          $('#add-new-ai-form .new-ai-avatar-upload').html(
            `<img src="${imgUrl}" width="81" height="81"/>`
          );
          $('#add-new-ai-form input[name="newaiavatarurl"]').val(fileName);
          $('#add-new-ai-form input[name="newaibucketname"]').val(bucket);
        })
        .catch((error) => {
          console.error('Failed to upload image:', error);
        });
    } else {
      // Already our bucket or a local object path
      if (!isHttp) {
        imgUrl = 'https://netwrckstatic.netwrck.com/' + imgUrl;
      }

      var fileName = extractObjectPath(imgUrl);
      $('#add-new-ai-form .new-ai-avatar-upload').html(
        `<img src="${imgUrl}" width="81" height="81"/>`
      );
      $('#add-new-ai-form input[name="newaiavatarurl"]').val(fileName);
      $('#add-new-ai-form input[name="newaibucketname"]').val(
        'netwrckstatic.netwrck.com'
      );
    }
  };

  appControllerSelf.writeNewAItoLocationCharacterDesignFunc =
    appControllerSelf.writeNewAItoLocationFunc;

  appControllerSelf.writeNewAItoLocationChatFunc = function (name, imgUrl) {
    var fileName = extractObjectPath(imgUrl);
    var aiImgUrl = 'https://netwrckstatic.netwrck.com/' + fileName;

    let currentVal = $('.question-text').val();
    var isFinishingSpace = currentVal[currentVal.length - 1] === ' ';
    let textToWrite = `a ${name} appears`;

    if (!isFinishingSpace && currentVal[currentVal.length - 1]) {
      textToWrite = ' ' + textToWrite;
    }

    $('.question-text').focus();
    var input = $('.question-text')[0];
    input.selectionStart = input.selectionEnd = input.value.length;
    $('.question-text').sendkeys(textToWrite);
  };

  window.dataAccessor = window.dataAccessor || {};
  window.dataAccessor.aisByName = window.dataAccessor.aisByName || {};

  $(document).on('click', '.ai-generated-char-chooser', function (event) {
    var $self = $(this);
    var aiImgUrl = $self.attr('src');
    var aiName = $self.attr('alt');
    appControllerSelf.writeNewAItoLocationFunc(aiName, aiImgUrl);
    var dialog = document.getElementById('create-pic-dialog');
    dialog.close();
  });

  $(document).on('click', '#autoCompleteUsersList a', function (event) {
    var $self = $(this);
    var currentName = $self.find('.ai-name').text();
    var currentAI = dataAccessor.aisByName[currentName];

    if (currentAI) {
      fixtures.currentAI = currentAI;
      if (!fixtures.room_name) {
        ReRenderCurrentGame();
      } else {
        console.log(
          '[CHAT] Skipping ReRenderCurrentGame for chatroom while selecting AI'
        );
      }
      var avatarFileName = fixtures.currentAI?.avatar_file_name;

      if (avatarFileName && avatarFileName !== 'None') {
        avatarFileName = avatarFileName.replace(' ', '-').trim();
        if (avatarFileName.indexOf('static/uploads') === -1) {
          avatarFileName = 'static/uploads/' + avatarFileName;
        }
        $('.chat-one-to-one-icon').attr(
          'src',
          'https://netwrckstatic.netwrck.com/' + avatarFileName
        );
      } else {
        $('.chat-one-to-one-icon').attr(
          'src',
          'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
        );
      }
      $('.chat-one-to-one-name').text(fixtures.currentAI?.name || 'AI');
      $('.chat-one-to-one-name-tooltip').text(
        fixtures.currentAI?.description || ''
      );
      $('#add-chat-dialog')[0].close();
      if (fixtures.room_name) {
        var urlkey = fixtures.currentAI?.name
          ? encodeURIComponent(fixtures.currentAI.name.replace(/ /g, '-'))
          : '';
        if (window.location.pathname !== '/') {
          APP.goto('/ai-chat/' + urlkey);
        }
      }
    }
  });

  $(document).on('click', '.add-to-room-users-list a', function (event) {
    if ($(this).hasClass('room-character-chat-link')) return;
    event.preventDefault();
    event.stopPropagation();
    var $self = $(this);
    var currentName = $self.find('.ai-name').text();
    var currentAI = dataAccessor.aisByName[currentName];

    if (currentAI) {
      fixtures.currentAI = currentAI;
      if (!fixtures.room_name) {
        ReRenderCurrentGame();
      } else {
        console.log(
          '[CHAT] Skipping ReRenderCurrentGame for chatroom while adding AI'
        );
      }
      var avatarFileName = fixtures.currentAI?.avatar_file_name;

      if (avatarFileName && avatarFileName !== 'None') {
        avatarFileName = avatarFileName.replace(' ', '-').trim();
        if (avatarFileName.indexOf('static/uploads') === -1) {
          avatarFileName = 'static/uploads/' + avatarFileName;
        }
        $('.chat-one-to-one-icon').attr(
          'src',
          'https://netwrckstatic.netwrck.com/' + avatarFileName
        );
      } else {
        $('.chat-one-to-one-icon').attr(
          'src',
          'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
        );
      }
      $('.chat-one-to-one-name').text(fixtures.currentAI?.name || 'AI');
      $('.chat-one-to-one-name-tooltip').text(
        fixtures.currentAI?.description || ''
      );
      $('#add-chat-dialog')[0].close();
      if (fixtures.room_name) {
        fixtures.room_ais[currentName] = currentAI;
        renderAIsInRoom(fixtures.room_ais);
        renderAIsInRoomList(fixtures.room_ais);
        closeAnyDialog();
      }
    }
  });

  appControllerSelf.renderAIPicsSearchPage = function (target, searchQuery) {
    $('#search-ai-loading-progress').show();
    let workFunc;
    if (searchQuery) {
      searchQuery = searchQuery.toLowerCase();
      workFunc = dataAccessor.searchAiImages(searchQuery, 0, 120);
    } else {
      workFunc = Promise.resolve([]);
    }
    workFunc
      .then(function (ais) {
        renderAIViewSearchPage(ais, target);
      })
      .finally(function (err) {
        $('#search-ai-loading-progress').hide();
      });
  };

  // Expose renderAIViewSearchPage to global scope to fix ESLint errors
  appControllerSelf.renderAIViewSearchPage = renderAIViewSearchPage;

  return appControllerSelf;
})();

window.APPController = APPController;

// Expose renderAIViewSearchPage to global scope
window.renderAIViewSearchPage = APPController.renderAIViewSearchPage;

let nagDialog = function () {
  var $dialog = $('#subscribe-dialog');
  if (window.userData?.is_subscribed) {
    return;
  }

  var $userDialog = $('#add-user-dialog');
  if ($userDialog.length && $userDialog[0].open) {
    return;
  }

  if ($dialog.length) {
    $dialog[0].close();
    $dialog[0].showModal();
  }
};

let icoDialog = function () {
  var $dialog = $('#ico-dialog');
  if ($dialog.length) {
    $dialog[0].close();
    $dialog[0].showModal();
  }
};

const NW_TOUR_CSS = `
.shepherd-element.nw-tour-step {
  background: #172341;
  border: 1px solid rgba(255, 141, 75, 0.5);
  border-radius: 18px;
  box-shadow: 0 18px 44px rgba(23, 35, 65, 0.42), 0 0 0 4px rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(10px);
  margin: 0;
  max-width: 340px;
}
.nw-tour-step .shepherd-arrow:before {
  background: #172341;
  border-color: rgba(255, 141, 75, 0.5);
}
.nw-tour-step .shepherd-header {
  background: transparent !important;
  padding: 18px 18px 0;
}
.nw-tour-step .shepherd-title {
  color: #fff !important;
  font-size: 17px;
  font-weight: 700;
  letter-spacing: -0.01em;
}
.nw-tour-step .shepherd-cancel-icon {
  color: rgba(255, 255, 255, 0.7) !important;
  font-size: 20px;
  transition: color 0.15s;
}
.nw-tour-step .shepherd-cancel-icon:hover { color: #ffb27d !important; }
.nw-tour-step .shepherd-text {
  color: rgba(255, 255, 255, 0.88);
  font-size: 14px;
  line-height: 1.55;
  padding: 10px 18px 6px;
}
.nw-tour-step .shepherd-footer {
  padding: 12px 18px 18px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
}
.nw-tour-step .shepherd-button {
  border: 0;
  border-radius: 999px;
  padding: 9px 17px;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: filter 0.15s, background 0.15s, color 0.15s;
}
.nw-tour-step .nw-tour-btn-primary {
  background: linear-gradient(135deg, #ff8d4b, #e9506e);
  color: #fff;
}
.nw-tour-step .nw-tour-btn-primary:hover { filter: brightness(1.12); }
.nw-tour-step .nw-tour-btn-ghost {
  background: transparent;
  color: rgba(255, 255, 255, 0.72);
}
.nw-tour-step .nw-tour-btn-ghost:hover { color: #fff; background: rgba(255,255,255,0.12); }
.nw-tour-step .nw-tour-progress {
  color: rgba(255, 255, 255, 0.58);
  font-size: 12px;
  align-self: center;
  margin-right: auto;
}
.shepherd-modal-overlay-container { opacity: 0.36; }
`;

window.addEventListener('load', function () {
  if (document.cookie.indexOf('firstvisit') !== -1) return;
  document.cookie =
    'firstvisit=true; expires=Fri, 31 Dec 9999 23:59:59 GMT; path=/';
  window.stour = false;
  if (document.location.pathname !== '/') return;
  if (typeof Shepherd === 'undefined') {
    console.warn('Shepherd not available, skipping tour');
    return;
  }

  const firstVisible = function (selectors) {
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el && el.offsetParent !== null) return el;
    }
    return null;
  };

  const isMobile = window.innerWidth <= 768;
  const steps = [
    {
      selectors: ['.chat-showcase-hero__action--chat', '#add-chat-btn'],
      title: 'Find AI Chatbots',
      text: 'Chat and roleplay with thousands of AI characters created by the community.',
    },
    {
      selectors: ['#create-new-ai-btn'],
      title: 'Create Your Own AI',
      text: 'Make an AI chatbot with its own name, backstory and greeting.',
    },
    {
      selectors: [
        '.chat-showcase-hero__action--art',
        '#ai-art-generator-button',
        '.ai-art-generator-button',
      ],
      title: 'Generate AI Art',
      text: 'Describe what you want and get beautiful AI art in seconds.',
    },
  ]
    .map(function (s) {
      return Object.assign({}, s, { el: firstVisible(s.selectors) });
    })
    .filter(function (s) {
      return s.el;
    });

  if (isMobile) return;
  const shown = steps;
  if (!shown.length) return;

  const style = document.createElement('style');
  style.textContent = NW_TOUR_CSS;
  document.head.appendChild(style);

  const stour = new Shepherd.Tour({
    useModalOverlay: true,
    defaultStepOptions: {
      classes: 'nw-tour-step',
      scrollTo: { behavior: 'smooth', block: 'center' },
      cancelIcon: { enabled: true },
      modalOverlayOpeningPadding: 6,
      modalOverlayOpeningRadius: 14,
    },
  });
  window.stour = stour;

  shown.forEach(function (s, i) {
    const last = i === shown.length - 1;
    stour.addStep({
      title: s.title,
      text: '<p class="ask-felix-robot-message-text">' + s.text + '</p>',
      attachTo: {
        element: s.el,
        on: s.el.closest('.chat-showcase-hero') ? 'bottom' : 'right',
      },
      buttons: [
        {
          text: i + 1 + '/' + shown.length,
          classes: 'nw-tour-progress',
          action: function () {},
        },
        {
          text: 'Skip',
          classes: 'nw-tour-btn-ghost',
          action: stour.cancel,
        },
        {
          text: last ? "Let's go!" : 'Next',
          classes: 'nw-tour-btn-primary',
          action: last ? stour.complete : stour.next,
        },
      ],
    });
    s.el.addEventListener('click', function () {
      stour.cancel();
    });
  });

  stour.start();
});

// Removed timed subscription nag to avoid unsolicited popups
// (manual triggers remain via explicit user actions)

$(document).ready(function () {
  window.setTimeout(function () {
    var isAndroid = /(android)/i.test(navigator.userAgent);
    if (isAndroid) {
      $('.android-hide').hide();

      var styleTag = document.createElement('style');
      styleTag.innerHTML = '.android-hide { display: none; }';
      document.head.appendChild(styleTag);
    }
  }, 1000);
});

window.backgroundChangeNum = window.backgroundChangeNum || 1;

function setBackground(bgImageName) {
  var $oldEl = $('.backdrop-ai-' + window.backgroundChangeNum);

  window.backgroundChangeNum = window.backgroundChangeNum === 1 ? 2 : 1;

  let $elToChange = $('.backdrop-ai-' + window.backgroundChangeNum);

  $elToChange.css('background', $oldEl.css('background'));
  $elToChange.css({ display: 'block', opacity: 0 });
  $oldEl.css({ display: 'block', opacity: 1 });

  requestAnimationFrame(() => {
    $elToChange.css({
      transition: 'opacity 0.5s ease-in-out',
      background: `url(${bgImageName}) no-repeat center center fixed`,
      'background-size': 'cover',
      opacity: 1,
    });
    $oldEl.css('opacity', 0);

    setTimeout(() => {
      requestAnimationFrame(() => {
        $oldEl.css('background', $elToChange.css('background'));
        $oldEl.css('opacity', 1);
      });
    }, 500);
  });
}

function preloadImage(imageName, onload) {
  if (window.ImageUtils && window.ImageUtils.preloadImage) {
    return window.ImageUtils.preloadImage(imageName, onload);
  } else {
    // Fallback implementation if ImageUtils is not loaded
    var img = new Image();
    img.onload = onload || function () {};
    img.onerror = onload || function () {};
    img.src = imageName;
    return img;
  }
}

async function handleUpload(imgUrl, name) {
  try {
    // Prefer high‑quality WebP blob conversion using MediaProcessor
    let webpBlob = null;
    if (window.MediaProcessor) {
      const processor = new window.MediaProcessor();
      webpBlob = await processor.convertToWebP(imgUrl, 0.85, 2048, 2048);
    } else if (window.ImageUtils && window.ImageUtils.convertToWebP) {
      // Fallback to ImageUtils; ensure we create a Blob from dataURL
      const dataUrl = await window.ImageUtils.convertToWebP(imgUrl);
      const byteString = atob(dataUrl.split(',')[1] || '');
      const mimeString =
        (dataUrl.split(',')[0] || '').split(':')[1].split(';')[0] ||
        'image/webp';
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++)
        ia[i] = byteString.charCodeAt(i);
      webpBlob = new Blob([ab], { type: mimeString });
    } else {
      // Last resort: fetch the image and wrap as Blob (no re-encode)
      const resp = await fetch(imgUrl, { mode: 'cors' });
      webpBlob = await resp.blob();
    }

    const fileName = name.replace(/\s+/g, '-').toLowerCase() + '.webp';

    const uploadedPath = await getSignedURLAndUpload(
      webpBlob,
      'image/webp',
      fileName
    );

    return uploadedPath;
  } catch (error) {
    console.error('Error during upload:', error);
    throw error;
  }
}

function setupVoiceDialog() {
  $(document).on('click', '#find-voice-btn', function (event) {
    var dialog = document.getElementById('find-voice-dialog');
    if (dialog) {
      dataAccessor.voices.getAllVoices().then((voices) => {
        renderVoiceList(voices);
      });
      dialog.close();
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#searchvoices').focus();
    }
    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  $(document).on('click', '.search-voices-button', function (event) {
    var query = $('#searchvoices').val();

    dataAccessor.voices.getAllVoices().then((voices) => {
      const filtered = voices.filter(
        (v) =>
          v.name.toLowerCase().includes(query.toLowerCase()) ||
          v.description.toLowerCase().includes(query.toLowerCase())
      );
      renderVoiceList(filtered);
    });
  });

  $(document).on(
    'input',
    '#searchvoices',
    _.debounce(function () {
      const query = $(this).val().toLowerCase().trim();
      dataAccessor.voices.getAllVoices().then((voices) => {
        if (query) {
          const filtered = voices.filter(
            (v) =>
              v.name.toLowerCase().includes(query) ||
              (v.description && v.description.toLowerCase().includes(query))
          );
          renderVoiceList(filtered);
        } else {
          renderVoiceList(voices);
        }
      });
    }, 300)
  );

  $(document).on('click', '.select-voice-btn', async function () {
    const voiceName = $(this).data('voice-name');
    const voices = await dataAccessor.voices.getAllVoices();
    const selectedVoice = voices.find((v) => v.name === voiceName);

    if (selectedVoice) {
      $('.current-voice-name').text(voiceName);

      localStorage.setItem('selectedVoice', JSON.stringify(selectedVoice));

      document.getElementById('find-voice-dialog').close();

      const generateDialog = document.getElementById('generate-voice-dialog');
      generateDialog.showModal();
      $('#voice-generation-text').focus();
    }
  });

  $(document).on('click', '.generate-voice-btn', async function () {
    const text = $('#voice-generation-text').val().trim();
    if (!text) {
      alert('Please enter some text to generate');
      return;
    }

    const selectedVoice = JSON.parse(localStorage.getItem('selectedVoice'));
    if (!selectedVoice) {
      alert('Please select a voice first');
      return;
    }

    try {
      $('#voice-generation-progress').show();

      const response = await dataAccessor.falFetch({
        model_name: 'fal-ai/f5-tts',
        gen_text: text,
        ref_audio_url: selectedVoice.voice_input_url,
        model_type: 'F5-TTS',
        remove_silence: true,
      });

      const data = await response.json();

      if (!data.result?.audio_url?.url) {
        throw new Error('No audio URL in response');
      }

      const audioHtml = `
                <div class="generated-voice-item">
                    <audio controls src="${data.result.audio_url.url}">
                        Your browser does not support the audio element.
                    </audio>
                    <button class="mdl-button mdl-js-button mdl-button--icon download-voice-btn">
                        <i class="material-icons">download</i>
                    </button>
                    <div class="mdl-tooltip" data-mdl-for="download-voice-btn">
                        Download audio
                    </div>
                </div>
            `;

      $('#voice-generation-preview').html(audioHtml);

      $('.download-voice-btn').click(async () => {
        try {
          const audioResponse = await fetch(data.result.audio_url.url);
          const audioBlob = await audioResponse.blob();
          const audioFile = new File(
            [audioBlob],
            `generated-voice-${Date.now()}.wav`,
            { type: 'audio/wav' }
          );

          const savedAudio = await getSignedURLAndUpload(
            audioFile,
            audioFile.type,
            audioFile.name
          );
          const finalUrl = `https://netwrckstatic.netwrck.com/${savedAudio.object_path}`;

          const a = document.createElement('a');
          a.href = finalUrl;
          a.download = audioFile.name;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } catch (err) {
          console.error('Error downloading audio:', err);
          alert('Error downloading audio. Please try again.');
        }
      });
    } catch (error) {
      console.error('Error generating voice:', error);
      alert('Failed to generate voice. Please try again.');
    } finally {
      $('#voice-generation-progress').hide();
    }
  });

  $(document).on('click', '#generate-voice-dialog .close', function () {
    document.getElementById('generate-voice-dialog').close();
  });
}

function renderVoiceList(voices) {
  const $target = $('#autoCompleteVoicesList');
  let template = '';

  voices.forEach((voice, i) => {
    const voiceName = voice.name || 'Voice ' + (i + 1);
    const fileName =
      voice.avatar_file_name || 'static/img/netwrck-logo-colord256.webp';

    template += `
        <div class="voice-item mdl-cell mdl-cell--4-col mdl-cell--6-col-tablet mdl-cell--12-col-phone" data-voice-name="${voiceName}">
            <div class="mdl-card mdl-shadow--2dp">
                <div class="mdl-card__title">
                    <img class="voice-avatar" src="https://netwrckstatic.netwrck.com/${fileName}"
                         alt="${voiceName}" loading="lazy">
                    <h2 class="mdl-card__title-text">${voiceName}</h2>
                </div>
                <div class="mdl-card__supporting-text">
                    ${voice.description || ''}
                </div>
                <div class="mdl-card__actions mdl-card--border">
                    ${
                      voice.voice_sample_url
                        ? `
                        <audio controls src="${voice.voice_sample_url}" style="width: 100%; margin-bottom: 8px">
                            Your browser does not support the audio element.
                        </audio>
                    `
                        : ''
                    }
                    <button class="select-voice-btn mdl-button mdl-js-button mdl-button--raised mdl-button--colored"
                            data-voice-name="${voiceName}">
                        Select Voice
                    </button>
                </div>
            </div>
        </div>`;
  });

  $target.html(
    template || '<div class="mdl-cell mdl-cell--12-col">No voices found</div>'
  );
}

$(document).on('click', '.select-voice-btn', async function () {
  const voiceId = $(this).data('voice-id');
  const voices = await dataAccessor.voices.getAllVoices();
  const selectedVoice = voices.find((v) => v.id === voiceId);

  if (selectedVoice) {
    const voiceName = selectedVoice.name || 'Voice';

    $('.current-voice-name').text(voiceName);

    localStorage.setItem(
      'selectedVoice',
      JSON.stringify({
        ...selectedVoice,
        name: voiceName,
      })
    );

    document.getElementById('find-voice-dialog').close();

    const generateDialog = document.getElementById('generate-voice-dialog');
    generateDialog.showModal();
  }
});

function setupVoiceCreation() {
  let mediaRecorder;
  let audioChunks = [];
  let isRecording = false;

  $(document).on('click', '#create-new-voice-btn', function (event) {
    var dialog = document.getElementById('create-voice-dialog');
    if (dialog) {
      dialog.close();
      try {
        dialog.close();
      } catch (e) {
        // Dialog might already be closed
      }

      try {
        dialog.showModal();
      } catch (e) {
        console.error('[SUBSCRIBE] Failed to open dialog:', e.message);
      }
      $('#voicename').focus();
    }
    event.preventDefault();
    event.stopPropagation();
    return false;
  });

  $(document).on('click', '#record-voice-button', async function () {
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        mediaRecorder = new MediaRecorder(stream);
        audioChunks = [];

        mediaRecorder.ondataavailable = (event) => {
          audioChunks.push(event.data);
        };

        mediaRecorder.onstop = () => {
          const audioBlob = new Blob(audioChunks, { type: 'audio/wav' });
          const audioUrl = URL.createObjectURL(audioBlob);
          const audioPreview = $('#voice-sample-preview-audio')[0];
          audioPreview.src = audioUrl;
          audioPreview.style.display = 'block';
          $('#voice-sample-preview').html(
            '<span class="material-icons" style="font-size: 81px">done</span>'
          );

          const audioFile = new File([audioBlob], 'recorded-voice-sample.wav', {
            type: 'audio/wav',
          });
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(audioFile);
          $('#voice-sample')[0].files = dataTransfer.files;
        };

        mediaRecorder.start();
        isRecording = true;
        $(this).addClass('recording');
        $(this).html('<span class="material-icons">stop</span> Stop Recording');

        setTimeout(() => {
          if (isRecording) {
            stopRecording();
          }
        }, 10000);
      } catch (err) {
        console.error('Error accessing microphone:', err);
        alert(
          'Unable to access microphone. Please ensure you have granted microphone permissions.'
        );
      }
    } else {
      stopRecording();
    }
  });

  function stopRecording() {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      isRecording = false;
      $('#record-voice-button').removeClass('recording');
      $('#record-voice-button').html(
        '<span class="material-icons">mic</span> Record Voice'
      );
    }
  }
  $(document).on('submit', '#add-new-voice-form', async function (e) {
    e.preventDefault();

    const voiceSample = $('#voice-sample')[0].files[0];
    const voiceData = {
      name: $('#voicename').val(),
      description: $('#voicedescription').val(),
      visibility: $('#publicvoice').is(':checked') ? 'PUBLIC' : 'PRIVATE',
      avatar_file_name: $(
        '#add-new-voice-form input[name="newvoiceavatarurl"]'
      ).val(),
    };

    if (!voiceSample || !voiceData.name) {
      alert('Please provide both a voice sample and name.');
      return;
    }

    try {
      const voicePath = await getSignedURLAndUpload(
        voiceSample,
        voiceSample.type,
        `voice-sample-${Date.now()}.wav`
      );
      voiceData.voice_input_url = `https://netwrckstatic.netwrck.com/${voicePath.object_path}`;

      const result = await dataAccessor.voices.createVoice(voiceData);

      if (result) {
        document.getElementById('create-voice-dialog').close();

        const voices = await dataAccessor.voices.getAllVoices();
        renderVoiceList(voices);
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Error creating voice. Please try again.');
    }
  });

  $(document).on('click', '.select-voice-btn', async function () {
    const voiceId = $(this).data('voice-id');
    const voices = await dataAccessor.voices.getAllVoices();
    const selectedVoice = voices.find((v) => v.id === voiceId);

    if (selectedVoice) {
      $('.current-voice-name').text(selectedVoice.name);

      localStorage.setItem('selectedVoice', JSON.stringify(selectedVoice));

      document.getElementById('find-voice-dialog').close();
    }
  });
}

let appendAIViewSearchPage = function (ais, target) {
  if (!ais || ais.length === 0) {
    return;
  }

  if (!dataAccessor.currentAIs) {
    dataAccessor.currentAIs = [];
  }

  dataAccessor.aisByName = dataAccessor.aisByName || {};
  for (let i = 0; i < ais.length; i++) {
    let ai = ais[i];
    const aiName = (ai && (ai.name || ai.Name)) || '';
    if (aiName) {
      dataAccessor.aisByName[aiName] = ai;
      dataAccessor.currentAIs.push(ai);
    }
  }

  var $target = $(target);
  var template = '';
  for (let i = 0; i < ais.length; i++) {
    let ai = ais[i];
    const aiName = (ai && (ai.name || ai.Name)) || '';
    if (!aiName) {
      continue;
    }
    var fileName = getImageFileNameForAI(ai);
    var displayText =
      ai.description || ai.Description || ai.greeting || ai.Greeting || '';
    template += `
            <a class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" href="/ai-chat/${encodeURIComponent(aiName)}" data-bypass="true">
             <span class="mdl-list__item-primary-content">

                    <span class="mdl-chip__contact mdl-list__item-avatar">
                       <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${aiName}" loading="lazy" >

                    </span>

                    <span class="ai-name mdl-chip__text">${aiName}</span>

                    <span class="mdl-list__item-text-body">
                    ${displayText}
                    </span>
                </span>
            </a>
`;
  }
  $target.append(template);

  if (ais.length < 120) {
    $('#load-more-button').hide();
  } else {
    $('#load-more-button').show();
  }
};

// IMPORTANT: DO NOT UNCOMMENT - Duplicate function definition
// This was causing search results to be overwritten when dialogs opened
// The correct renderAIViewSearchPage is defined inside APPController (line ~3996)
// All calls to renderAIViewSearchPage are within APPController scope and use the internal version
// Keeping this commented to prevent the duplicate global definition from breaking search again
/*
var renderAIViewSearchPage = function (ais, target) {
  ais = ais || [];
  if (!Array.isArray(ais)) {
    ais = [];
  }

  dataAccessor.currentAIs = ais;

  dataAccessor.aisByName = dataAccessor.aisByName || {};
  for (let i = 0; i < ais.length; i++) {
    let ai = ais[i];
    if (ai) {
      const aiName = (ai.name || ai.Name);
      if (aiName) {
        dataAccessor.aisByName[aiName] = ai;
      }
    }
  }

  var $target = $(target);
  var template = '';

  // Define isRoomContext for this function as well to ensure it's accessible to all callbacks
  var isRoomContext =
    target === '#autoCompleteUsersRoomList' ||
    (target && target.indexOf('autoCompleteUsersRoomList') !== -1);

  if (
    target === '.ai-character-grid' ||
    target.selector === '.ai-character-grid'
  ) {
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      const aiName = (ai && (ai.name || ai.Name)) || '';
      if (!aiName) {
        continue;
      }
      var fileName = getImageFileNameForAI(ai);
      var displayText = ai.description || ai.Description || ai.greeting || ai.Greeting || '';

      template += `
                <a class="ai-character-item hidden-bot mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" href="/ai-chat/${encodeURIComponent(aiName)}" data-bypass="true">
                 <span class="mdl-list__item-primary-content">
                        <span class="mdl-chip__contact mdl-list__item-avatar">
                           <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${aiName}" loading="lazy" >
                        </span>
                        <span class="ai-name mdl-chip__text">${aiName}</span>
                        <span class="mdl-list__item-text-body">
                        ${displayText}
                        </span>
                    </span>
                </a>
                `;
    }

    $target.html(template);

    revealMoreBots();

    $(window).off('scroll.loadMoreBots');

    let scrollTimeout;
    $(window).on('scroll.loadMoreBots', function () {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(function () {
        if (
          $(window).scrollTop() + $(window).height() >
          $(document).height() - 600
        ) {
          if ($('.ai-character-item.hidden-bot').length > 0) {
            console.log('Scrolling near bottom, revealing more bots...');
            revealMoreBots();
          }
        }
      }, 150);
    });
  } else {
    // Always render all results for the add-chat dialog (#autoCompleteUsersList),
    // and avoid image infinite-scroll/load-more logic here.
    for (let i = 0; i < ais.length; i++) {
      let ai = ais[i];
      const aiName = (ai && (ai.name || ai.Name)) || '';
      if (!aiName) {
        continue;
      }
      let fileName = getImageFileNameForAI(ai);
      let displayText = ai.description || ai.Description || ai.greeting || ai.Greeting || '';
      if (isRoomContext) {
        template += `
                      <div class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" data-ai-name="${encodeURIComponent(aiName)}" style="cursor: pointer;">
                       <span class="mdl-list__item-primary-content">
                              <span class="mdl-chip__contact mdl-list__item-avatar">
                                 <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${aiName}" loading="lazy" >
                               </span>
                              <span class="ai-name mdl-chip__text">${aiName}</span>
                              <span class="mdl-list__item-text-body">
                              ${displayText}
                              </span>
                          </span>
                      </div>
                      `;
      } else {
        template += `
                      <a class="ai-character-item mdl-cell mdl-cell--4-col mdl-cell--12-col-tablet mdl-cell--12-col-phone mdl-list__item mdl-list__item--three-line mdl-cell--stretch" href="/ai-chat/${encodeURIComponent(aiName)}" data-bypass="true">
                       <span class="mdl-list__item-primary-content">
                              <span class="mdl-chip__contact mdl-list__item-avatar">
                                 <img class="ai-avatar-icon" src="https://netwrckstatic.netwrck.com/${fileName}" alt="${aiName}" loading="lazy" >
                               </span>
                              <span class="ai-name mdl-chip__text">${aiName}</span>
                              <span class="mdl-list__item-text-body">
                              ${displayText}
                              </span>
                          </span>
                      </a>
                      `;
      }
    }
    $target.html(template);
  }
};
*/

function loadMoreAISearchResults() {
  var currentQuery = $('.searchboxaisprompt').val();

  if (!currentQuery) {
    return;
  }

  $('#load-more-progress').show();
  $('#load-more-button').prop('disabled', true);

  var offset = dataAccessor.currentAIs ? dataAccessor.currentAIs.length : 0;

  dataAccessor
    .searchAiImages(currentQuery, offset, 120)
    .then(function (moreAis) {
      appendAIViewSearchPage(moreAis, '#autoCompleteUsersList');
    })
    .catch(function (error) {
      console.error('Error loading more results:', error);
    })
    .finally(function () {
      $('#load-more-progress').hide();
      $('#load-more-button').prop('disabled', false);
    });
}

const BOTS_PER_PAGE = 80;

function revealMoreBots() {
  const $hiddenBots = $('.ai-character-item.hidden-bot');
  if ($hiddenBots.length === 0) {
    return;
  }

  $hiddenBots.slice(0, BOTS_PER_PAGE).removeClass('hidden-bot');
}

$('.search-ai-bots-button')
  .off('click')
  .on('click', function (event) {
    var query = $('#searchboxaisprompt').val();

    APPController.renderAIBotsSearchPage($('.ai-character-grid'), query);
  });

$('#searchboxaisprompt')
  .off('keydown')
  .on('keydown', function (event) {
    if (event.keyCode === 13) {
      var query = $('#searchboxaisprompt').val();
      APPController.renderAIBotsSearchPage($('.ai-character-grid'), query);
    }
  });

if (typeof fixtures !== 'undefined') {
  if (
    !fixtures.ais ||
    !Array.isArray(fixtures.ais) ||
    fixtures.ais.length === 0
  ) {
    if (fixtures.currentAI) {
      fixtures.ais = [fixtures.currentAI];
    } else if (window.DefaultAICharacter) {
      fixtures.ais = [window.DefaultAICharacter];
    } else {
      console.warn(
        'fixtures.ais is undefined or empty, initializing with default data'
      );
      fixtures.ais = [
      {
        avatar_file_name: 'pr8sX__VG8tCCMoYClvUHGaTXYwpFQ3TXk7t4i191vA.webp',
        description: 'An otherworld fantasy role playing experience.',
        name: 'Isekai narrator',
        greeting:
          'An unknown multiverse phenomenon occurred, and you found yourself in a dark space.',
        tags: ['Fantasy', 'Adventure', 'RPG'],
        num_interactions: 1000,
      },
      {
        avatar_file_name: 'TextAdventure3.png',
        description: 'Text adventure game with you as the hero.',
        name: 'Text Adventure Game',
        greeting: "Let's play a text-based adventure game. I'll be your guide.",
        tags: ['text adventure', 'ai chat', 'adventure'],
        num_interactions: 900,
      },
      {
        avatar_file_name: 'default_avatar.png',
        description: 'A helpful AI assistant ready to answer your questions.',
        name: 'AI Assistant',
        greeting:
          "Hello! I'm here to help you with any questions you might have.",
        tags: ['assistant', 'helpful', 'knowledgeable'],
        num_interactions: 1200,
      },
      {
        avatar_file_name: 'creative_writer.png',
        description:
          'Creative writer who can help with stories, poems, and other creative content.',
        name: 'Creative Writer',
        greeting:
          "Let's create something amazing together! What kind of story would you like to write?",
        tags: ['creative', 'writer', 'storytelling'],
        num_interactions: 800,
      },
      {
        avatar_file_name: 'tech_expert.png',
        description:
          'Tech expert who can help with programming, tech questions, and troubleshooting.',
        name: 'Tech Expert',
        greeting:
          "I'm here to help with all your technology questions and coding challenges!",
        tags: ['technology', 'programming', 'expert'],
        num_interactions: 1100,
      },
      {
        avatar_file_name: 'history_buff.png',
        description:
          'History enthusiast with knowledge spanning ancient to modern times.',
        name: 'History Buff',
        greeting:
          'Welcome to a journey through time! What historical period interests you most?',
        tags: ['history', 'knowledge', 'education'],
        num_interactions: 750,
      },
      {
        avatar_file_name: 'language_tutor.png',
        description:
          'Language tutor who can help you learn and practice different languages.',
        name: 'Language Tutor',
        greeting:
          "Hola! Bonjour! Hello! Let's practice some languages together!",
        tags: ['language', 'education', 'tutor'],
        num_interactions: 950,
      },
      {
        avatar_file_name: 'philosopher.png',
        description:
          'Deep thinker ready to explore philosophical questions and ideas.',
        name: 'Philosopher',
        greeting:
          "Let's ponder the big questions of existence, meaning, and consciousness.",
        tags: ['philosophy', 'thought', 'wisdom'],
        num_interactions: 600,
      },
      ];
    }
  }

  dataAccessor.initialAIs = fixtures.ais;
  dataAccessor.currentAIs = fixtures.ais;

  if (!dataAccessor.aisByName) {
    dataAccessor.aisByName = {};
  }

  fixtures.ais.forEach((ai) => {
    if (ai && ai.name) {
      dataAccessor.aisByName[ai.name] = ai;
    }
  });
}

function setupBotInfiniteScroll() {
  $(window).off('scroll.loadMoreBots');

  let scrollTimeout;
  $(window).on('scroll.loadMoreBots', function () {
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    scrollTimeout = setTimeout(function () {
      if (
        $(window).scrollTop() + $(window).height() >
        $(document).height() - 600
      ) {
        if ($('.ai-character-item.hidden-bot').length > 0) {
          console.log('Scrolling near bottom, revealing more bots...');
          revealMoreBots();
        }
      }
    }, 150);
  });
}

$(document).ready(function () {
  const dialogObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      if (
        mutation.attributeName === 'open' &&
        mutation.target.hasAttribute('open')
      ) {
        const dialogId = mutation.target.id;

        if (dialogId === 'add-chat-dialog') {
          console.log('AI chat dialog opened');

          // Check if there's already search results displayed
          const existingResults = $(
            '#autoCompleteUsersList .ai-character-item'
          ).length;
          if (existingResults > 0 && $('#autoCompleteUsersList').attr('data-owned-priority') === 'true') {
            console.log('Preserving existing search results:', existingResults);
            return; // Don't overwrite existing search results
          }

          if (dataAccessor.initialAIs && dataAccessor.initialAIs.length > 0) {
            APPController.renderAIs('#autoCompleteUsersList', '');
          } else {
            $('#search-ai-loading-progress').show();

            const workFunc =
              dataAccessor.topAIs && typeof dataAccessor.topAIs === 'function'
                ? dataAccessor.topAIs()
                : fetch('/api/top-ais').then((res) => res.json());

            workFunc
              .then(function (ais) {
                if (ais && Array.isArray(ais) && ais.length > 0) {
                  dataAccessor.initialAIs = ais;
                  dataAccessor.currentAIs = ais;

                  APPController.renderAIs('#autoCompleteUsersList', '');
                }
              })
              .catch(function (err) {
                console.error('Error loading initial AI data:', err);

                $('#autoCompleteUsersList')
                  .html(`<div class="mdl-cell mdl-cell--12-col">
                                    <p>Failed to load AI characters. Please try searching.</p>
                                </div>`);
              })
              .finally(function () {
                $('#search-ai-loading-progress').hide();
              });
          }
        } else if (dialogId === 'add-chat-chatroom-dialog') {
          var roomKeywords = (function () {
            var name = (window.fixtures && window.fixtures.room_name) || '';
            var words = name.split(/[-_\s]+/).filter(function (w) {
              return w.length > 2 && !/^\d+$/.test(w);
            });
            return words.slice(0, 3).join(' ');
          })();
          var initialRoomQuery = roomKeywords || '';
          if (initialRoomQuery) {
            $('#searchusersforroom').val(initialRoomQuery).closest('.mdl-textfield').addClass('is-dirty');
          }
          if (dataAccessor.initialAIs && dataAccessor.initialAIs.length > 0) {
            APPController.renderAIs('#autoCompleteUsersRoomList', initialRoomQuery);
          } else {
            $('#search-ai-loading-progress').show();

            const workFunc =
              dataAccessor.topAIs && typeof dataAccessor.topAIs === 'function'
                ? dataAccessor.topAIs()
                : fetch('/api/top-ais')
                    .then((res) => res.json())
                    .then((serverResponse) => {
                      let data = [];
                      const serverData =
                        serverResponse && serverResponse.data
                          ? serverResponse.data
                          : serverResponse;
                      if (
                        serverData &&
                        Array.isArray(serverData) &&
                        serverData.length > 0
                      ) {
                        data = serverData.map((ai) => ({
                          avatar_file_name:
                            ai.image_url ||
                            ai.avatar_file_name ||
                            'default_avatar.png',
                          description: ai.description || '',
                          name: ai.name || 'Unknown',
                          greeting:
                            ai.greeting ||
                            `Hello! I'm ${ai.name || 'here to help'}.`,
                          tags: ai.tags
                            ? typeof ai.tags === 'string'
                              ? ai.tags.split(',').map((t) => t.trim())
                              : ai.tags
                            : [],
                          num_interactions:
                            ai.popularity || ai.num_interactions || 0,
                        }));
                      }

                      if (window.userData?.secret) {
                        return fetch('/api/get-characters-for-user', {
                          method: 'GET',
                          headers: { 'Content-Type': 'application/json' },
                        })
                          .then((response) => response.json())
                          .then((userChars) => {
                            if (userChars && Array.isArray(userChars)) {
                              const userAIs = userChars.map((ai) => ({
                                avatar_file_name:
                                  ai.avatar_file_name || 'default_avatar.png',
                                description: ai.description || '',
                                name: ai.name || 'Unknown',
                                greeting:
                                  ai.greeting ||
                                  `Hello! I'm ${ai.name || 'here to help'}.`,
                                tags: ai.tags
                                  ? typeof ai.tags === 'string'
                                    ? ai.tags.split(',').map((t) => t.trim())
                                    : ai.tags
                                  : [],
                                num_interactions: ai.num_interactions || 0,
                                is_user_created: true,
                              }));

                              const existingNames = new Set(
                                userAIs.map((ai) => ai.name)
                              );
                              const uniqueServerAIs = data.filter(
                                (ai) => !existingNames.has(ai.name)
                              );
                              return [...userAIs, ...uniqueServerAIs];
                            }
                            return data;
                          })
                          .catch((err) => {
                            console.log(
                              'Could not fetch user characters:',
                              err
                            );
                            return data;
                          });
                      }
                      return data;
                    });

            workFunc
              .then(function (ais) {
                if (ais && Array.isArray(ais) && ais.length > 0) {
                  dataAccessor.initialAIs = ais;
                  dataAccessor.currentAIs = ais;
                  APPController.renderAIs('#autoCompleteUsersRoomList', initialRoomQuery);
                }
              })
              .catch(function (err) {
                console.error('Error loading initial AI data:', err);
                $('#autoCompleteUsersRoomList')
                  .html(`<div class="mdl-cell mdl-cell--12-col">
                            <p>Failed to load AI characters. Please try searching.</p>
                        </div>`);
              })
              .finally(function () {
                $('#search-ai-loading-progress').hide();
              });
          }
        }
      }
    });
  });

  document.querySelectorAll('dialog').forEach((dialog) => {
    if (typeof dialog.addEventListener === 'function') {
      dialog.addEventListener('show', function () {
        handleDialogOpen(dialog.id);
      });
    }

    dialogObserver.observe(dialog, {
      attributes: true,
      attributeFilter: ['open'],
    });
  });

  function handleDialogOpen(dialogId) {
    if (dialogId === 'add-chat-dialog') {
      console.log('AI chat dialog opened via event');

      // Check if there's already search results displayed
      const existingResults = $(
        '#autoCompleteUsersList .ai-character-item'
      ).length;
      if (existingResults > 0 && $('#autoCompleteUsersList').attr('data-owned-priority') === 'true') {
        console.log('Preserving existing search results:', existingResults);
        return; // Don't overwrite existing search results
      }

      if (dataAccessor.initialAIs && dataAccessor.initialAIs.length > 0) {
        APPController.renderAIs('#autoCompleteUsersList', '');
      } else {
        $('#search-ai-loading-progress').show();

        const workFunc =
          dataAccessor.topAIs && typeof dataAccessor.topAIs === 'function'
            ? dataAccessor.topAIs()
            : fetch('/api/top-ais')
                .then((res) => res.json())
                .then((serverResponse) => {
                  let data = [];
                  const serverData =
                    serverResponse && serverResponse.data
                      ? serverResponse.data
                      : serverResponse;
                  if (
                    serverData &&
                    Array.isArray(serverData) &&
                    serverData.length > 0
                  ) {
                    data = serverData.map((ai) => ({
                      avatar_file_name:
                        ai.image_url ||
                        ai.avatar_file_name ||
                        'default_avatar.png',
                      description: ai.description || '',
                      name: ai.name || 'Unknown',
                      greeting:
                        ai.greeting ||
                        `Hello! I'm ${ai.name || 'here to help'}.`,
                      tags: ai.tags
                        ? typeof ai.tags === 'string'
                          ? ai.tags.split(',').map((t) => t.trim())
                          : ai.tags
                        : [],
                      num_interactions:
                        ai.popularity || ai.num_interactions || 0,
                    }));
                  }

                  if (window.userData?.secret) {
                    return fetch('/api/get-characters-for-user', {
                      method: 'GET',
                      headers: { 'Content-Type': 'application/json' },
                    })
                      .then((response) => response.json())
                      .then((userChars) => {
                        if (userChars && Array.isArray(userChars)) {
                          const userAIs = userChars.map((ai) => ({
                            avatar_file_name:
                              ai.avatar_file_name || 'default_avatar.png',
                            description: ai.description || '',
                            name: ai.name || 'Unknown',
                            greeting:
                              ai.greeting ||
                              `Hello! I'm ${ai.name || 'here to help'}.`,
                            tags: ai.tags
                              ? typeof ai.tags === 'string'
                                ? ai.tags.split(',').map((t) => t.trim())
                                : ai.tags
                              : [],
                            num_interactions: ai.num_interactions || 0,
                            is_user_created: true,
                          }));

                          const existingNames = new Set(
                            userAIs.map((ai) => ai.name)
                          );
                          const uniqueServerAIs = data.filter(
                            (ai) => !existingNames.has(ai.name)
                          );
                          return [...userAIs, ...uniqueServerAIs];
                        }
                        return data;
                      })
                      .catch((err) => {
                        console.log('Could not fetch user characters:', err);
                        return data;
                      });
                  }
                  return data;
                });

        workFunc
          .then(function (ais) {
            if (ais && Array.isArray(ais) && ais.length > 0) {
              dataAccessor.initialAIs = ais;
              dataAccessor.currentAIs = ais;
              APPController.renderAIs('#autoCompleteUsersList', '');
            }
          })
          .catch(function (err) {
            console.error('Error loading initial AI data:', err);
            $('#autoCompleteUsersList')
              .html(`<div class="mdl-cell mdl-cell--12-col">
                            <p>Failed to load AI characters. Please try searching.</p>
                        </div>`);
          })
          .finally(function () {
            $('#search-ai-loading-progress').hide();
          });
      }
    } else if (dialogId === 'add-chat-chatroom-dialog') {
      var roomKw = (function () {
        var name = (window.fixtures && window.fixtures.room_name) || '';
        var words = name.split(/[-_\s]+/).filter(function (w) {
          return w.length > 2 && !/^\d+$/.test(w);
        });
        return words.slice(0, 3).join(' ');
      })();
      if (roomKw) {
        $('#searchusersforroom').val(roomKw).closest('.mdl-textfield').addClass('is-dirty');
      }
      if (dataAccessor.initialAIs && dataAccessor.initialAIs.length > 0) {
        APPController.renderAIs('#autoCompleteUsersRoomList', roomKw || '');
      } else {
        loadAndRenderInitialAIs('#autoCompleteUsersRoomList', roomKw || '');
      }
    } else if (dialogId === 'find-room-dialog') {
      console.log('Find room dialog opened');
      APPController.renderRooms();
    } else if (dialogId === 'find-voice-dialog') {
      console.log('Find voice dialog opened');
      if (
        typeof dataAccessor.voices !== 'undefined' &&
        typeof dataAccessor.voices.getAllVoices === 'function'
      ) {
        dataAccessor.voices.getAllVoices().then(function (voices) {
          renderVoiceList(voices);
        });
      }
    }
  }

  function loadAndRenderInitialAIs(targetSelector, initialQuery) {
    $('#search-ai-loading-progress').show();

    const workFunc =
      dataAccessor.topAIs && typeof dataAccessor.topAIs === 'function'
        ? dataAccessor.topAIs()
        : fetch('/api/top-ais')
            .then((res) => res.json())
            .then((serverResponse) => {
              let data = [];
              const serverData =
                serverResponse && serverResponse.data
                  ? serverResponse.data
                  : serverResponse;
              if (
                serverData &&
                Array.isArray(serverData) &&
                serverData.length > 0
              ) {
                data = serverData.map((ai) => ({
                  avatar_file_name:
                    ai.image_url || ai.avatar_file_name || 'default_avatar.png',
                  description: ai.description || '',
                  name: ai.name || 'Unknown',
                  greeting:
                    ai.greeting || `Hello! I'm ${ai.name || 'here to help'}.`,
                  tags: ai.tags
                    ? typeof ai.tags === 'string'
                      ? ai.tags.split(',').map((t) => t.trim())
                      : ai.tags
                    : [],
                  num_interactions: ai.popularity || ai.num_interactions || 0,
                }));
              }

              if (userData && userData.secret) {
                return fetch('/api/get-characters-for-user', {
                  method: 'GET',
                  headers: { 'Content-Type': 'application/json' },
                })
                  .then((response) => response.json())
                  .then((userChars) => {
                    if (userChars && Array.isArray(userChars)) {
                      const userAIs = userChars.map((ai) => ({
                        avatar_file_name:
                          ai.avatar_file_name || 'default_avatar.png',
                        description: ai.description || '',
                        name: ai.name || 'Unknown',
                        greeting:
                          ai.greeting ||
                          `Hello! I'm ${ai.name || 'here to help'}.`,
                        tags: ai.tags
                          ? typeof ai.tags === 'string'
                            ? ai.tags.split(',').map((t) => t.trim())
                            : ai.tags
                          : [],
                        num_interactions: ai.num_interactions || 0,
                        is_user_created: true,
                      }));

                      const existingNames = new Set(
                        userAIs.map((ai) => ai.name)
                      );
                      const uniqueServerAIs = data.filter(
                        (ai) => !existingNames.has(ai.name)
                      );
                      return [...userAIs, ...uniqueServerAIs];
                    }
                    return data;
                  })
                  .catch((err) => {
                    console.log('Could not fetch user characters:', err);
                    return data;
                  });
              }
              return data;
            });

    workFunc
      .then(function (ais) {
        if (ais && Array.isArray(ais) && ais.length > 0) {
          dataAccessor.initialAIs = ais;
          dataAccessor.currentAIs = ais;
          APPController.renderAIs(targetSelector, initialQuery || '');
        }
      })
      .catch(function (err) {
        console.error('Error loading initial AI data:', err);
        $(targetSelector).html(`<div class="mdl-cell mdl-cell--12-col">
                    <p>Failed to load AI characters. Please try searching.</p>
                </div>`);
      })
      .finally(function () {
        $('#search-ai-loading-progress').hide();
      });
  }

  $(document).on('click', '#add-chat-btn', function () {
    setTimeout(function () {
      // Check if there's already search results displayed
      const existingResults = $(
        '#autoCompleteUsersList .ai-character-item'
      ).length;
      if (existingResults > 0 && $('#autoCompleteUsersList').attr('data-owned-priority') === 'true') {
        console.log('Preserving existing search results:', existingResults);
        return; // Don't overwrite existing search results
      }

      if (dataAccessor.initialAIs && dataAccessor.initialAIs.length > 0) {
        APPController.renderAIs('#autoCompleteUsersList', '');
      }
    }, 200);
  });

  $(document).on('click', '#find-room-btn', function () {
    setTimeout(function () {
      APPController.renderRooms();
    }, 200);
  });

  $(document).on('click', '#find-voice-btn', function () {
    setTimeout(function () {
      if (
        typeof dataAccessor.voices !== 'undefined' &&
        typeof dataAccessor.voices.getAllVoices === 'function'
      ) {
        dataAccessor.voices.getAllVoices().then(function (voices) {
          renderVoiceList(voices);
        });
      }
    }, 200);
  });
});

// Remove duplicate handlers and combine functionality
$('#searchusers')
  .off('keydown')
  .off('input')
  .on('keydown', function (event) {
    if (event.keyCode === 13) {
      var query = $('#searchusers').val();
      // Always use API search on Enter key for consistent results
      onTypingAIFunction(event, '#autoCompleteUsersList', query);
    }
  })
  .on(
    'input',
    _.debounce(function () {
      var query = $(this).val();
      var searchQuery = query.toLowerCase().trim();

      // Always use API search for consistency
      // This ensures we get the same results as shown in console
      window.onTypingAIFunction(null, '#autoCompleteUsersList', query);
    }, 300)
  );

$(document)
  .on('keydown', '#searchusersforroom', function (event) {
    if (event.keyCode === 13) {
      var query = $('#searchusersforroom').val();
      window.onTypingAIFunction(event, '#autoCompleteUsersRoomList', query);
    }
  })
  .on(
    'input',
    '#searchusersforroom',
    _.debounce(function () {
      var query = $('#searchusersforroom').val();
      window.onTypingAIFunction(null, '#autoCompleteUsersRoomList', query);
    }, 300)
  );

// Duplicate handler removed - already handled above

function applyBasicMasonrySizing() {
  $('.ai-character-item').each(function () {
    const $item = $(this);
    const $img = $item.find('.ai-avatar-icon');
    const $description = $item.find('.mdl-list__item-text-body');

    if ($description.length && $description.text().trim()) {
      $item.addClass('has-description');
    } else {
      $item.removeClass('has-description');
    }

    if ($img.length && $img[0].complete) {
      applyAspectRatioClass($item, $img[0]);
    } else if ($img.length) {
      $img.on('load', function () {
        applyAspectRatioClass($item, this);
      });
    }
  });
}

function applyAspectRatioClass($item, img) {
  if (!img.naturalWidth || !img.naturalHeight) return;

  const aspectRatio = img.naturalWidth / img.naturalHeight;

  $item.removeClass(
    'square size-small size-medium landscape wide-single portrait tall-single'
  );

  if (aspectRatio >= 2.5) {
    $item.addClass('landscape');
  } else if (aspectRatio > 1.4) {
    $item.addClass('wide-single');
  } else if (aspectRatio < 0.5) {
    $item.addClass('portrait');
  } else if (aspectRatio < 0.8) {
    $item.addClass('tall-single');
  } else {
    $item.addClass('square');
  }
}

function initializeRoomManagement() {
  const addRoomBtn = document.getElementById('add-room-btn');
  if (addRoomBtn) {
    addRoomBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      showAddRoomDialog();
    });
  }

  const addRoomForm = document.getElementById('add-room-form');
  if (addRoomForm) {
    addRoomForm.addEventListener('submit', function (e) {
      e.preventDefault();
      e.stopPropagation();
      handleAddRoomSubmit();
    });
  }

  const addRoomDialog = document.getElementById('add-room-dialog');
  if (addRoomDialog) {
    const closeBtn = addRoomDialog.querySelector('.close');
    if (closeBtn) {
      closeBtn.addEventListener('click', function (e) {
        e.preventDefault();
        addRoomDialog.close();
      });
    }
  }
}

function showAddRoomDialog() {
  console.log('[GAME.JS v2] showAddRoomDialog called - NEW VERSION');
  console.log('[GAME.JS v2] Checking auth - window.userData:', window.userData);
  console.log(
    '[GAME.JS v2] Checking auth - window.authSession?.isLoggedIn():',
    window.authSession?.isLoggedIn()
  );

  const hasUserData = window.userData && window.userData.id;
  const isLoggedInViaSession =
    window.authSession && window.authSession.isLoggedIn();

  if (!hasUserData && !isLoggedInViaSession) {
    console.log('[GAME.JS] User not authenticated, showing signup modal first');

    window.sessionStorage.setItem('pendingAction', 'createRoom');
    if (typeof window.showSignupDialog === 'function') {
      window.showSignupDialog();
    } else if (typeof window.showLoginDialog === 'function') {
      window.showLoginDialog();
    } else {
      window.location.href =
        '/signup?next=' +
        encodeURIComponent(window.location.pathname + window.location.search);
    }
    return;
  }

  if (!hasUserData && isLoggedInViaSession) {
    console.log(
      '[GAME.JS] User logged in via authSession but userData not populated, fetching...'
    );
    const currentUser = window.authSession.getCurrentUser();
    if (currentUser && currentUser.id) {
      window.userData = window.userData || {};
      window.userData.id = currentUser.id;
      window.userData.email = currentUser.email;
      window.userData.secret = currentUser.secret;
      console.log(
        '[GAME.JS] Populated userData from authSession:',
        window.userData
      );
    }
  }

  console.log('[GAME.JS] User authenticated, showing add room dialog');
  const dialog = document.getElementById('add-room-dialog');
  if (dialog && typeof dialog.showModal === 'function') {
    dialog.showModal();
  } else {
    console.error('Add room dialog not found or showModal not available');
  }
}

async function handleAddRoomSubmit() {
  const form = document.getElementById('add-room-form');
  const submitBtn = form.querySelector('button[type="submit"]');
  const roomNameInput = document.getElementById('roomname');
  const publicCheckbox = document.getElementById('public');

  if (!roomNameInput.value.trim()) {
    alert('Please enter a room name');
    return;
  }

  if (!window.userData || !window.userData.id) {
    console.log('User not authenticated, showing signup modal');
    if (typeof window.showSignupDialog === 'function') {
      window.showSignupDialog();
    } else if (typeof window.showLoginDialog === 'function') {
      window.showLoginDialog();
    } else {
      window.location.href =
        '/signup?next=' +
        encodeURIComponent(window.location.pathname + window.location.search);
    }
    return;
  }

  const originalText = submitBtn.textContent;
  submitBtn.disabled = true;
  submitBtn.textContent = 'Creating...';

  try {
    const roomData = {
      name: roomNameInput.value.trim(),
      public: publicCheckbox.checked,
    };

    const response = await fetch('/api/new-chatroom', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(roomData),
    });

    if (response.ok) {
      const result = await response.json();
      if (result.success) {
        console.log('Room created successfully:', result);

        const dialog = document.getElementById('add-room-dialog');
        if (dialog) dialog.close();

        roomNameInput.value = '';
        publicCheckbox.checked = true;

        if (
          result.data &&
          (result.data.room_key || result.data.room_id || result.data.id)
        ) {
          const roomKey =
            result.data.room_key || result.data.room_id || result.data.id;
          const roomName = result.data.name || roomNameInput.value || roomKey;

          if (window.fixtures) {
            window.fixtures.room_name = roomName;
            window.fixtures.room_ais = {};
            window.fixtures.currentAI = null;
          }

          if (window.APP && window.APP.goto) {
            window.APP.goto(`/r/${roomKey}`);
          } else {
            window.location.href = `/r/${roomKey}`;
          }
        } else {
          alert('Room created successfully!');
          window.location.reload();
        }

        return;
      }
    }

    let errorMessage = 'Failed to create room';
    if (response.status === 401) {
      errorMessage = 'Please log in to create a room';
      if (typeof window.showLoginDialog === 'function') window.showLoginDialog();
    } else if (response.status === 400) {
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        console.error('Error parsing error response JSON:', e);
      }
    }

    alert(errorMessage);
  } catch (error) {
    console.error('Error creating room:', error);
    alert('Network error. Please check your connection and try again.');
  } finally {
    submitBtn.disabled = false;
    submitBtn.textContent = originalText;
  }
}

document.addEventListener('DOMContentLoaded', function () {
  initializeRoomManagement();
});

window.addEventListener('userLoggedIn', function (event) {
  console.log(
    '[GAME.JS] userLoggedIn event received, event detail:',
    event.detail
  );
  const pendingAction = window.sessionStorage.getItem('pendingAction');

  if (pendingAction === 'createRoom') {
    window.sessionStorage.removeItem('pendingAction');

    let retryCount = 0;
    const maxRetries = 10;
    const checkAuthAndShowDialog = () => {
      console.log(
        '[GAME.JS] Checking if userData is populated, attempt:',
        retryCount + 1
      );
      console.log('[GAME.JS] window.userData:', window.userData);
      console.log('[GAME.JS] window.userData?.id:', window.userData?.id);

      if (window.userData && window.userData.id) {
        console.log('[GAME.JS] userData is ready, showing add room dialog');
        showAddRoomDialog();
      } else if (retryCount < maxRetries) {
        retryCount++;
        const delay = Math.min(100 * Math.pow(2, retryCount), 2000);
        console.log(
          '[GAME.JS] userData not ready yet, retrying in',
          delay,
          'ms'
        );
        setTimeout(checkAuthAndShowDialog, delay);
      } else {
        console.error(
          '[GAME.JS] Failed to get userData after',
          maxRetries,
          'attempts'
        );

        if (window.authSession && window.authSession.isLoggedIn()) {
          console.log(
            '[GAME.JS] authSession says logged in, forcing dialog open'
          );
          showAddRoomDialog();
        }
      }
    };

    setTimeout(checkAuthAndShowDialog, 100);
  }
});

window.addEventListener('load', function () {
  [150, 1000].forEach(function (delay) {
    window.setTimeout(function () {
      if (typeof window.syncEmptyRoomSetupDialog === 'function') {
        window.syncEmptyRoomSetupDialog();
      }
    }, delay);
  });
});

function renderRoomHistory(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return;
  if ($('.chat-log .chat-log__item').length > 0) return;
  var escape = function (value) {
    var node = document.createElement('span');
    node.textContent = String(value === null || value === undefined ? '' : value);
    return node.innerHTML.replace(/"/g, '&quot;');
  };
  messages.forEach(function (message) {
    if (!message) return;
    var srcImg = getSrcImgForCharacter({
      avatar_file_name: message.avatar_file_name,
    });
    var username = escape(message.username || 'Netwrck');
    var attributes = message.id
      ? ` data-message-id="${escape(message.id)}"`
      : '';
    appendToChatLog(
      `<img class="chat-log-avatar-icon" src="${srcImg}" alt="${username}" width="100" height="100"><p class="chat-group-name" tabindex="0">${username}</p><div class="chat-log__item"${attributes} data-message-author="${username}"><div class="chat-log__message" contenteditable="" spellcheck="false">${escape(message.text)}</div></div>`,
      undefined,
      message
    );
  });
}

window.switchChatMode = function (mode, config, skipHistoryUpdate) {
  // Ensure config is defined
  config = config || {};
  window.mediaUploadHandler?.resetChatImages?.();

  $('.chat-log').empty();

  if (mode === 'chatroom') {
    fixtures.room_is_private = !!config.room_is_private || new URLSearchParams(window.location.search).get('anon_chat') === '1';
    fixtures.room_name = config.room_name || 'New Room';
    fixtures.room_ais = config.room_ais || {};
    preparePrivateRoomChat();
    fixtures.currentAI = null;

    $('.chat-one-to-one-icon').hide();
    $('#chat-one-to-one-name').hide();
    $('#chat-one-to-one-name-tags').hide();
    $('#chat-one-to-one-name-tooltip').hide();
    $('#save-button').hide();

    if (!$('.header1-g.white-bg-highlight').length) {
      $('.container').prepend(
        '<h1 class="header1-g white-bg-highlight">' +
          fixtures.room_name +
          ' Chat</h1>'
      );
    } else {
      $('.header1-g.white-bg-highlight').text(fixtures.room_name + ' Chat');
    }

    if (!$('#chatroom-ais-in-room').length) {
      $('.header1-g').after(
        '<div id="chatroom-ais-in-room" class="mdl-grid chatroom-ais-in-room"></div>'
      );
    }
    renderAIsInRoom(fixtures.room_ais);
    renderAIsInRoomList(fixtures.room_ais);

    if (!$('#edit-characters-btn').length) {
      $('#chatroom-ais-in-room').after(
        '<a id="edit-characters-btn" class="mdl-list__item-secondary-action" href="#" data-bypass>' +
          '<button class="new-chat-button mdl-button mdl-js-button mdl-button--fab mdl-js-ripple-effect mdl-button--colored">' +
          '<i class="material-icons">edit</i>' +
          '</button>' +
          '</a>' +
          '<div id="characters-in-room-tooltip" class="chars-in-room-tooltip playground-tooltip mdl-tooltip mdl-tooltip--large mdl-tooltip--right" for="edit-characters-btn">Edit Characters In Room</div>'
      );
    }

    $('#share-ai-dialog .mdl-dialog__title').text('Share Room');
    $('#share-ai-dialog p:first').text(
      'Share this room (people can see the chats)'
    );

    renderRoomHistory(isPrivateRoomChat() ? fixtures.messages : config.messages || fixtures.messages);
    if (isPrivateRoomChat()) fixtures.private_room_rendered = true;

    if (window.liveSource) {
      try {
        window.liveSource.close();
      } catch (e) {
        console.warn('[GAME.JS] Failed to close existing liveSource:', e);
      }
      window.liveSource = null;
    }
    var searchBaseUrl = fixtures['search_base_url'] || '';
    var liveUpdatesEnabled =
      fixtures && fixtures.live_updates_enabled ? true : false;
    if (
      liveUpdatesEnabled && !isPrivateRoomChat() &&
      fixtures &&
      fixtures.room_name &&
      String(fixtures.room_name).trim()
    ) {
      window.liveSource = new EventSource(
        searchBaseUrl +
          '/api/live/subscribe?channel=' +
          encodeURIComponent(fixtures.room_name)
      );

      window.liveSource.onmessage = function (e) {
        try {
          var data = JSON.parse(e.data);
          if (window.ChatWorkspace?.applyLiveModerationEvent(data)) {
            return;
          }
          if (
            data.type === 'room_character_lock_update' &&
            data.room_name === fixtures.room_name
          ) {
            fixtures.room_characters_locked = !!data.characters_locked;
            syncEditRoomCharactersCopy();
            return;
          }
          if (
            data.type === 'room_characters_update' &&
            data.room_name === fixtures.room_name
          ) {
            if (data.room_ais && typeof data.room_ais === 'object') {
              applySharedRoomCharacters(data.room_ais);
            }
            return;
          }
          if (data.text && data.username) {
            appendToChatLog(
              `
                          <img class="chat-log-avatar-icon" src="${data.avatar_url || '/img/netwrck-logo-colord256.webp'}" alt="${data.username}" width="100" height="100">
                          <p class="chat-group-name">${data.username}</p>
                          <div class="chat-log__item">
                              <div class="chat-log__message" contenteditable="" spellcheck="false">${data.text}</div>
                          </div>
                      `,
              undefined,
              data
            );
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };
      window.liveSource.onerror = function (err) {
        console.warn('[GAME.JS] Live updates error:', err);
      };
    }

    if (typeof window.syncEmptyRoomSetupDialog === 'function') {
      window.syncEmptyRoomSetupDialog();
    }
  } else {
    fixtures.room_is_private = false;
    fixtures.private_room_prepared = false;
    fixtures.private_room_rendered = false;
    fixtures.room_name = null;
    fixtures.currentAI =
      normalizeAICharacter(config.ai_char || fixtures.currentAI) || null;
    fixtures.room_ais = {};

    $('.header1-g').remove();
    $('#chatroom-ais-in-room').remove();
    $('#edit-characters-btn').remove();
    $('#characters-in-room-tooltip').remove();

    const aiName =
      (fixtures.currentAI && fixtures.currentAI.name) || 'Netwrck';
    const aiDescription =
      (fixtures.currentAI && fixtures.currentAI.description) ||
      'Please Subscribe and support us';
    const avatarFile =
      fixtures.currentAI && fixtures.currentAI.avatar_file_name
        ? fixtures.currentAI.avatar_file_name
        : '';

    $('.chat-one-to-one-icon')
      .show()
      .attr(
        'src',
        avatarFile
          ? 'https://netwrckstatic.netwrck.com/static/uploads/' +
              avatarFile
          : 'https://netwrckstatic.netwrck.com/static/img/netwrck-logo-colord256.webp'
      );
    $('#chat-one-to-one-name')
      .show()
      .text(aiName);
    $('#chat-one-to-one-name-tags').show();
    $('#chat-one-to-one-name-tooltip')
      .show()
      .text(aiDescription);
    $('#save-button').show();

    $('#share-ai-dialog .mdl-dialog__title').text('Share Chat');
    $('#share-ai-dialog p:first').text(
      'Anyone with this link can continue from this chat.'
    );
    closeEmptyRoomSetupDialog();

    if (window.liveSource) {
      window.liveSource.close();
      window.liveSource = null;
    }

    const greetingText =
      fixtures.currentAI && typeof fixtures.currentAI.greeting === 'string'
        ? fixtures.currentAI.greeting
        : '';
    if (greetingText) {
      appendToChatLog(
        '<div class="chat-log__item"><div class="chat-log__message" contenteditable="" spellcheck="false">' +
          greetingText +
          '</div></div>'
      );
    }
  }

  if (!skipHistoryUpdate && window.history && window.history.pushState) {
    var newUrl;
    if (mode === 'chatroom') {
      newUrl =
        '/r/' +
        encodeURIComponent(String(fixtures.room_name || 'new-room').replace(/ /g, '-').toLowerCase());
    } else if (fixtures.currentAI?.name) {
      newUrl =
        '/ai-chat/' +
        encodeURIComponent(fixtures.currentAI?.name?.replace(/ /g, '-'));
    }
    var isHome =
      window.location.pathname === '/' ||
      window.location.pathname === '/index.html';
    var isChatMode = mode === 'chat' || mode === 'ai-chat';
    if (newUrl && newUrl !== window.location.pathname) {
      if (!(isChatMode && isHome)) {
        window.history.pushState({ mode: mode, config: config }, '', newUrl);
      }
    }
  }
  window.dispatchEvent(
    new CustomEvent('netwrck:chat-route', { detail: { mode: mode } })
  );
  window.requestAnimationFrame(function () {
    window.chatCharacterOverlay?.install();
  });
};

// Register popstate handler with coordinator if available
(function () {
  var gamePopstateHandler = function (event) {
    console.log('[GAME.JS] popstate event:', event.state);
    if (event.state && event.state.mode) {
      switchChatMode(event.state.mode, event.state.config || {}, true);
    } else {
      // If no state, try to detect current mode from URL or fixtures
      var path = window.location.pathname;
      if (path.startsWith('/r/')) {
        // Chatroom mode
        var roomName = path.replace('/r/', '').replace(/-/g, ' ');
        switchChatMode('chatroom', { room_name: roomName }, true);
      } else if (path.startsWith('/ai-chat/')) {
        // AI chat mode
        switchChatMode('chat', { ai_char: fixtures.currentAI }, true);
      } else {
        // Default to chat mode with current AI
        switchChatMode('chat', { ai_char: fixtures.currentAI }, true);
      }
    }
  };

  if (window.PopStateCoordinator) {
    window.PopStateCoordinator.register(
      gamePopstateHandler,
      50, // Medium priority
      'Game.js'
    );
  } else {
    window.addEventListener('popstate', gamePopstateHandler);
  }
})();

window.setupGameInit = setupGameInit;
// Normalize any image URL/path to a stable object path under static/uploads
if (typeof window.extractObjectPath !== 'function') {
  window.extractObjectPath = function (urlOrPath) {
    try {
      if (!urlOrPath) return '';
      var s = String(urlOrPath).trim();
      if (s.startsWith('static/uploads/')) return s;
      if (s.startsWith('uploads/'))
        return 'static/uploads/' + s.slice('uploads/'.length);
      var idx = s.indexOf('static/uploads/');
      if (idx >= 0) return s.slice(idx);
      var idx2 = s.indexOf('/uploads/');
      if (idx2 >= 0)
        return 'static/uploads/' + s.slice(idx2 + '/uploads/'.length);
      var last = s.split('/').pop();
      if (last && last.indexOf('.') > 0) return 'static/uploads/' + last;
      return s;
    } catch (_) {
      return String(urlOrPath || '');
    }
  };
}
